import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import VoiceMessagesPage from './VoiceMessagesPage';
import {
  buildPermissionMatrix,
  DEFAULT_FEATURES,
  DEFAULT_PRODUCTS,
  DEFAULT_SERVICES,
  type GroupRoleBinding,
  type GroupPermissionBinding,
  type IamPermission,
  type IamRole,
  loadGroupRoleBindings,
  type UserGroupBinding,
  loadGroupPermissionBindings,
  loadIamRoles,
  loadUserGroupBindings,
  parseCsvTokens,
  roleSlug,
  saveGroupRoleBindings,
  saveGroupPermissionBindings,
  saveIamRoles,
  saveUserGroupBindings,
} from './iamModel';
import { CONSOLE_PURSUIT_PATH, ADMIN_CONSOLE_EXPERIENCE_PATH, ADMIN_CONSOLE_OBSERVABILITY_PATH } from './consolePaths';
import { ADMIN_CONSOLE_COPY, storedUiLanguage } from './i18n/consoleChromeCopy';
import ObservabilityAdminSection from './ObservabilityAdminSection';
import ExperienceAdminSection from './ExperienceAdminSection';

const inferGatewayBaseUrl = (): string => {
  const configured = (process.env.REACT_APP_GATEWAY_URL || '').trim();
  if (configured) {
    // Prefer IPv4 loopback locally — Cloudflare WARP / IPv6 localhost can stall fetches.
    if (/^https?:\/\/localhost(?::|\/|$)/i.test(configured)) {
      return configured.replace(/localhost/i, '127.0.0.1').replace(/\/$/, '');
    }
    return configured.replace(/\/$/, '');
  }

  if (typeof window === 'undefined') {
    return 'http://127.0.0.1:8000';
  }

  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '::1';
  if (isLocal) {
    return 'http://127.0.0.1:8000';
  }

  const apexHost = host.replace(/^www\./, '').replace(/^dashboard\./, '').replace(/^metrics\./, '');
  return `https://${apexHost}`;
};

const GATEWAY_BASE_URL = inferGatewayBaseUrl();
const METRICS_SERVICE_URL = (
  process.env.REACT_APP_METRICS_URL
  || (typeof window !== 'undefined' && ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname)
    ? ''
    : `${GATEWAY_BASE_URL}/api/v1/metrics`)
).replace(/\/$/, '');
const ADMIN_GROUPS_URL = `${GATEWAY_BASE_URL}/api/v1/admin/groups`;
const ADMIN_USERS_URL = `${GATEWAY_BASE_URL}/api/v1/admin/users`;
const ADMIN_TIER1_MAILBOX_PASSWORD_URL = `${GATEWAY_BASE_URL}/api/v1/admin/email-operations/tier1-mailboxes`;
const ADMIN_EMAIL_INBOX_URL = (process.env.REACT_APP_ADMIN_EMAIL_INBOX_URL || 'https://mail.google.com/mail/u/0/?authuser=andrew.goss@gosslife.biz').trim();
const ADMIN_EMAIL_DIRECTORY = (process.env.REACT_APP_ADMIN_EMAIL_DIRECTORY || '').trim();
const MANAGED_EMAIL_DOMAIN = (process.env.REACT_APP_MANAGED_EMAIL_DOMAIN || 'nextcompli.com').trim().toLowerCase();
const TIER1_MAILBOX_ALLOWLIST = new Set([
  'ceo@nextcompli.com',
  'no-reply@nextcompli.com',
  'support@nextcompli.com',
  'operations@nextcompli.com',
  'prod-superuser@nextcompli.com',
]);

const buildInboxUrlForAddress = (address: string): string => {
  const base = ADMIN_EMAIL_INBOX_URL.trim();
  if (!base) return '';
  const raw = String(address || '').trim().toLowerCase();
  if (!raw) return base;
  const hasDomain = raw.includes('@');
  const normalizedEmail = hasDomain ? raw : `${raw}@nextcompli.com`;
  const [localPart = '', domainPart = ''] = normalizedEmail.split('@');
  const usernameCandidate = hasDomain ? localPart.trim() : raw;
  if (base.includes('{email}') || base.includes('{username}') || base.includes('{localpart}') || base.includes('{domain}')) {
    return base
      .replaceAll('{email}', encodeURIComponent(normalizedEmail))
      .replaceAll('{username}', encodeURIComponent(usernameCandidate || normalizedEmail))
      .replaceAll('{localpart}', encodeURIComponent(localPart))
      .replaceAll('{domain}', encodeURIComponent(domainPart));
  }
  try {
    const url = new URL(base);
    // WorkMail/IdP integrations vary; include common mailbox hint aliases.
    url.searchParams.set('login_hint', usernameCandidate || normalizedEmail);
    url.searchParams.set('loginHint', usernameCandidate || normalizedEmail);
    url.searchParams.set('email', normalizedEmail);
    url.searchParams.set('user', usernameCandidate || normalizedEmail);
    url.searchParams.set('username', usernameCandidate || normalizedEmail);
    return url.toString();
  } catch {
    return base;
  }
};

type EmailDirectoryEntry = {
  address: string;
  purpose: string;
  source: 'aws' | 'config' | 'legacy';
};

const LOCAL_GROUPS_KEY = 'nextcompli.accessManagement.groups';
const LOCAL_USERS_KEY = 'nextcompli.accessManagement.users';

type AdminGroup = {
  group_id: number;
  group_name: string;
  parent_group_id?: number | null;
  group_type?: 'organization' | 'team';
  group_path?: string;
  group_depth?: number;
  features?: string[];
};

type Membership = {
  company_id: number;
  company_name: string;
  roles: string[];
};

type AdminUser = {
  user_id: string;
  email: string;
  email_aliases?: string[];
  username?: string;
  display_name?: string;
  is_active: boolean;
  created_at?: string;
  password_reset_required?: boolean;
  last_login_at?: string | null;
  memberships: Membership[];
};

type ApiErrorPayload = {
  detail?: unknown;
  message?: unknown;
  error?: unknown;
};

const formatApiErrorValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (Array.isArray(value)) {
    const parts = value
      .map((entry) => formatApiErrorValue(entry))
      .filter((entry) => Boolean(entry));
    return parts.join('; ');
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const msg = formatApiErrorValue(record.msg);
    if (msg) {
      const location = Array.isArray(record.loc)
        ? record.loc.map((entry) => String(entry || '').trim()).filter((entry) => Boolean(entry)).join('.')
        : '';
      return location ? `${location}: ${msg}` : msg;
    }
    const prioritized = [record.detail, record.message, record.error]
      .map((entry) => formatApiErrorValue(entry))
      .find((entry) => Boolean(entry));
    if (prioritized) {
      return prioritized;
    }
    try {
      return JSON.stringify(record);
    } catch {
      return '';
    }
  }
  if (value == null) {
    return '';
  }
  return String(value).trim();
};

type CostOptimizationSummary = {
  timestamp?: string;
  cost?: {
    currency?: string;
    estimated_hourly_usd?: number;
    estimated_daily_usd?: number;
    actual_hourly_usd?: number;
    actual_source?: string;
    actual_month_to_date_usd?: number;
    actual_previous_month_usd?: number;
    actual_forecast_monthly_usd?: number;
    actual_projected_monthly_usd?: number;
    actual_daily_average_usd?: number;
    actual_top_services?: { service?: string; amount_usd?: number }[];
    actual_previous_top_services?: { service?: string; amount_usd?: number }[];
    actual_time_period?: { start?: string; end_exclusive?: string };
    actual_previous_time_period?: { start?: string; end_exclusive?: string };
    status?: string;
    assumptions?: string[];
  };
  resource_usage?: {
    active_services?: number;
    healthy_services?: number;
    degraded_services?: number;
    unhealthy_services?: number;
  };
  gemini?: {
    status?: string;
    source?: string;
    error?: string;
    checked_at?: string;
    project?: {
      project_id?: string;
      key_suffix?: string | null;
      ai_studio_usage_url?: string;
      ai_studio_spend_url?: string;
      ai_studio_rate_limits_url?: string;
    };
    estimated_today_usd?: number;
    estimated_month_to_date_usd?: number;
    requests_today?: number;
    requests_month_to_date?: number;
    tokens_today?: number;
    tokens_month_to_date?: number;
    today?: {
      day?: string;
      requests?: number;
      successes?: number;
      errors?: number;
      rate_limited?: number;
      prompt_tokens?: number;
      candidates_tokens?: number;
      thoughts_tokens?: number;
      total_tokens?: number;
      estimated_cost_usd?: number;
    };
    month_to_date?: {
      month?: string;
      requests?: number;
      successes?: number;
      errors?: number;
      rate_limited?: number;
      prompt_tokens?: number;
      candidates_tokens?: number;
      thoughts_tokens?: number;
      total_tokens?: number;
      estimated_cost_usd?: number;
    };
    by_model?: Record<string, {
      requests?: number;
      total_tokens?: number;
      estimated_cost_usd?: number;
    }>;
    by_feature?: Record<string, {
      requests?: number;
      total_tokens?: number;
      estimated_cost_usd?: number;
    }>;
    last_error?: string | null;
    last_rate_limited_at?: string | null;
    ops_checklist?: string[];
  };
  status?: string;
  recommendations?: string[];
  analysis?: {
    estimated_monthly_usd?: number;
    modeled_monthly_usd?: number;
    model_breakdown_hourly_usd?: {
      base_platform?: number;
      compute_vcpu?: number;
      compute_memory?: number;
      modeled_total?: number;
    };
    alert_reference_usd?: number | null;
    alert_gap_usd?: number | null;
    coverage_note?: string;
    billing_scope_warning?: string | null;
    potential_causes?: string[];
    billing_alignment?: {
      status?: string;
      source?: string;
      metric?: string;
      month_to_date_usd?: number;
      previous_month_total_usd?: number;
      forecast_month_total_usd?: number;
      projected_month_total_usd?: number;
      linked_account?: string | null;
      linked_account_filter_applied?: boolean;
      service_categories?: Record<string, number>;
      all_services?: Record<string, number>;
      time_period?: { start?: string; end_exclusive?: string };
      previous_time_period?: { start?: string; end_exclusive?: string };
    };
  };
};

type DeliveryAttempt = {
  channel?: string;
  provider?: string;
  delivery?: string;
  error?: string;
  error_code?: string;
};

type MailboxSignal = {
  email: string;
  has_mail: boolean;
  has_unread_imap?: boolean;
  imap_unseen_count?: number;
  imap_messages_count?: number;
  last_event_type?: string | null;
  last_event_at?: string | null;
  provider?: string | null;
};

type EmailRuntimeStatus = {
  provider?: string;
  normalized_provider?: string;
  status?: string;
  sender?: string;
  smtp?: {
    configured?: boolean;
    host?: string | null;
    hosts?: string[];
    port?: string;
  };
  ses?: {
    mode?: string;
    region?: string;
    webhook_configured?: boolean;
    native_runtime_available?: boolean;
    known_identities?: string[];
  };
  sendgrid?: {
    configured?: boolean;
  };
  webhook?: {
    configured?: boolean;
  };
  effective_delivery_order?: string[];
  warnings?: string[];
  immediate_checks?: string[];
  natural_next_steps?: string[];
};

type AdminSection = 'roles' | 'users' | 'groups' | 'crm' | 'emailOperations' | 'costOptimization' | 'voiceMessages' | 'keycloakAdmin' | 'observability' | 'experience' | 'splash';

type CrmLead = {
  lead_id: number;
  fullName: string;
  workEmail: string;
  phone?: string | null;
  company?: string | null;
  employees?: string | null;
  useCase?: string | null;
  source?: string | null;
  locale?: string | null;
  page_path?: string | null;
  submitted_at: string;
  created_at: string;
};

type CrmLeadRecencyFilter = 'all' | '7d' | '30d';

type PermissionTypeFilter = 'all' | 'service' | 'admin';

type PermissionGroupFilter =
  | 'all'
  | 'products'
  | 'services'
  | 'features'
  | 'identity'
  | 'themes';

type PermissionCatalogRow = {
  id: string;
  policyName: string;
  typeLabel: string;
  groupLabel: string;
  groupFilter: PermissionGroupFilter;
  description: string;
  usedAs: string;
  usedCount: number;
  typeFilter: PermissionTypeFilter;
};

type PermissionSortKey = 'policyName' | 'typeLabel' | 'usedCount' | 'description';
type SortDirection = 'asc' | 'desc';
type CreateUserPasswordMode = 'generate' | 'set';

const PERMISSION_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

const LEGACY_RBAC_PERMISSION_IDS = [
  '*',
  'dashboard.client.read',
  'users.company.manage',
  'features.company.manage',
  'audit.workspace.read',
  'audit.workspace.write',
  'alerts.manage',
  'dashboard.sales.read',
  'crm.pipeline.read',
  'crm.pipeline.write',
  'crm.key_players.approve',
  'demo.tenant.read',
  'proposal.pricing.read',
  'proposal.pricing.write',
  'crm.team.read',
  'inlet.workspace.read',
  'inlet.workspace.write',
  'razzmatazz.workspace.read',
  'razzmatazz.workspace.write',
  'razzmatazz.notify.read',
  'razzmatazz.notify.write',
  'account.profile.read',
  'account.profile.write',
  'account.security.manage',
  'reports.executive.read',
  'dashboard.design.read',
  'dashboard.design.manage',
] as const;

const DEFAULT_ADMIN_ROLE_NAME = 'Admin';
const DEFAULT_CLIENT_ROLE_NAME = 'Client';
const DEFAULT_CLIENT_ROLE_PERMISSION_IDS = [
  'dashboard.client.read',
  'audit.workspace.read',
  'reports.executive.read',
  'alerts.manage',
] as const;

const AccordionChevronIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    className="admin-console-accordion-chevron"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 140ms ease' }}
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const sectionFromPath = (pathname: string): AdminSection => {
  if (pathname.endsWith('/roles')) {
    return 'roles';
  }
  if (pathname.endsWith('/crm')) {
    return 'crm';
  }
  if (pathname.endsWith('/groups')) {
    return 'groups';
  }
  if (pathname.endsWith('/users')) {
    return 'users';
  }
  if (pathname.endsWith('/email-operations')) {
    return 'emailOperations';
  }
  if (pathname.endsWith('/cost-optimization')) {
    return 'costOptimization';
  }
  if (pathname.endsWith('/observability')) {
    return 'observability';
  }
  if (pathname.endsWith('/experience')) {
    return 'experience';
  }
  if (pathname.endsWith('/voice-messages')) {
    return 'voiceMessages';
  }
  if (pathname.endsWith('/keycloak')) {
    return 'keycloakAdmin';
  }
  return 'splash';
};

const sectionLabelMap: Record<AdminSection, string> = {
  splash: 'Overview',
  roles: 'Roles',
  crm: 'CRM',
  users: 'Users',
  groups: 'Accounts',
  voiceMessages: 'Voice Messages',
  emailOperations: 'Email Operations',
  costOptimization: 'Cost Optimization',
  observability: 'Observability',
  experience: 'User experience',
  keycloakAdmin: 'Identity Provider',
};

function adminSectionLabel(section: AdminSection, chrome: Record<string, string>): string {
  const labels: Record<AdminSection, string> = {
    splash: chrome.sectionSplash,
    roles: chrome.sectionRoles,
    crm: chrome.sectionCrm,
    users: chrome.sectionUsers,
    groups: chrome.sectionGroups,
    voiceMessages: chrome.sectionVoice,
    emailOperations: chrome.emailOperations,
    costOptimization: chrome.costOptimization,
    observability: chrome.observability,
    experience: chrome.experience,
    keycloakAdmin: chrome.identityProvider,
  };
  return labels[section] || sectionLabelMap[section];
}

const isPlatformAdmin = (user: ReturnType<typeof useAuth>['user']): boolean => {
  if (!user) {
    return false;
  }
  const allRoles = new Set(
    [...(user.platform_roles || []), ...(user.effective_roles || [])].map((role) => String(role || '').trim().toLowerCase()),
  );
  return Boolean(user.is_platform_admin) || allRoles.has('platform_admin');
};

const getUsernameLabel = (entry: AdminUser): string =>
  entry.email?.trim() || entry.username?.trim() || entry.display_name?.trim() || '';

const COMPANY_JOBS = [
  { id: 'company_admin', label: 'Subscription admin' },
  { id: 'contributor', label: 'Contributor' },
  { id: 'viewer', label: 'Viewer' },
] as const;

const ACCOUNT_TEMPLATE_ROLE_NAMES = new Set([
  'company_admin',
  'contributor',
  'viewer',
  'platform_admin',
  'admin',
]);

const PLAN_FEATURES: Record<string, string[]> = {
  none: [],
  basic: [
    'usage-based-pricing-plans',
    'ofac-compliance',
    'website-compliance',
    'cloud-compliance',
    'dod-contracts-compliance',
    'education-compliance',
    'business-creation-compliance',
    'wills-trusts-compliance',
    'dashboards',
    'research-reports',
  ],
  advanced: [
    'usage-based-pricing-plans',
    'ofac-compliance',
    'website-compliance',
    'cloud-compliance',
    'dod-contracts-compliance',
    'education-compliance',
    'business-creation-compliance',
    'wills-trusts-compliance',
    'dashboards',
    'research-reports',
    'continuous-monitoring',
    'real-time-batch-screening',
    'audit-trail-alert-workflow',
    'real-time-alerts',
  ],
  enterprise: [
    'usage-based-pricing-plans',
    'ofac-compliance',
    'website-compliance',
    'cloud-compliance',
    'dod-contracts-compliance',
    'education-compliance',
    'business-creation-compliance',
    'wills-trusts-compliance',
    'dashboards',
    'research-reports',
    'continuous-monitoring',
    'real-time-batch-screening',
    'audit-trail-alert-workflow',
    'real-time-alerts',
    'embedded-consultants',
    'executive-compliance-reporting',
    'scheduled-audits',
    'case-audit-workspace',
  ],
};

const planIdFromFeatures = (features: string[] | undefined): string => {
  const set = new Set(features || []);
  if (set.size === 0) {
    return 'none';
  }
  if (set.has('embedded-consultants') || set.has('case-audit-workspace')) {
    return 'enterprise';
  }
  if (set.has('continuous-monitoring') || set.has('real-time-batch-screening')) {
    return 'advanced';
  }
  if (set.has('dod-contracts-compliance') || set.has('website-compliance') || set.has('dashboards')) {
    return 'basic';
  }
  return 'custom';
};

const SYSTEM_ACCOUNT_NAMES = new Set(['keycloak', 'admin']);

const membershipRoleNames = (membership: Membership & { role?: string }): string[] => {
  if (Array.isArray(membership.roles) && membership.roles.length > 0) {
    return membership.roles.map((role) => String(role || '').trim()).filter(Boolean);
  }
  const single = String(membership.role || '').trim();
  return single ? [single] : [];
};

const companyJobFromRoles = (roles: string[] | undefined): string => {
  const names = new Set((roles || []).map((role) => role.trim().toLowerCase()));
  if (names.has('platform_admin')) {
    return 'platform_admin';
  }
  if (names.has('company_admin')) {
    return 'company_admin';
  }
  if (names.has('contributor')) {
    return 'contributor';
  }
  return 'viewer';
};

const accountMembershipsForDisplay = (memberships: Array<Membership & { role?: string }>) => {
  const byName = new Map<string, { name: string; roles: string[] }>();
  memberships.forEach((membership) => {
    const name = String(membership.company_name || '').trim();
    const key = name.toLowerCase();
    if (!name || SYSTEM_ACCOUNT_NAMES.has(key)) {
      return;
    }
    const current = byName.get(key) || { name, roles: [] as string[] };
    membershipRoleNames(membership).forEach((role) => {
      if (!current.roles.includes(role)) {
        current.roles.push(role);
      }
    });
    byName.set(key, current);
  });
  return [...byName.values()].sort((left, right) => left.name.localeCompare(right.name));
};

const companyJobLabel = (role: string): string => {
  if (role === 'platform_admin') {
    return 'Super user';
  }
  return COMPANY_JOBS.find((job) => job.id === role)?.label || role;
};

const accountsCellLabel = (memberships: Array<Membership & { role?: string }>): string => {
  const visibleAccounts = accountMembershipsForDisplay(memberships);
  const roles = new Set(visibleAccounts.flatMap((account) => account.roles.map((role) => role.toLowerCase())));
  memberships.forEach((membership) => {
    membershipRoleNames(membership).forEach((role) => roles.add(role.toLowerCase()));
  });
  if (roles.has('platform_admin')) {
    return 'Super user';
  }
  if (!visibleAccounts.length) {
    return 'None';
  }
  return visibleAccounts.map((account) => (
    `${account.name} (${companyJobLabel(companyJobFromRoles(account.roles))})`
  )).join(', ');
};

const normalizeGroupName = (value: string): string => String(value || '').trim().toLowerCase();

const stripScopePrefix = (scopePrefix: string, scopedName: string): string => {
  const normalizedPrefix = String(scopePrefix || '').trim();
  const normalizedName = String(scopedName || '').trim();
  if (!normalizedPrefix || !normalizedName) {
    return normalizedName;
  }
  const lowerPrefix = `${normalizedPrefix.toLowerCase()}:`;
  const lowerName = normalizedName.toLowerCase();
  if (!lowerName.startsWith(lowerPrefix)) {
    return normalizedName;
  }
  return normalizedName.slice(lowerPrefix.length).trim() || normalizedName;
};

const buildScopedName = (scopePrefix: string, baseName: string): string => {
  const normalizedPrefix = String(scopePrefix || '').trim();
  const normalizedBase = String(baseName || '').trim();
  if (!normalizedPrefix) {
    return normalizedBase;
  }
  const strippedBase = stripScopePrefix(normalizedPrefix, normalizedBase);
  return `${normalizedPrefix}:${strippedBase || normalizedBase}`;
};

const normalizeAdminGroup = (entry: unknown, fallbackIndex: number): AdminGroup | null => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  const record = entry as Record<string, unknown>;
  const parsedGroupId = Number(record.group_id);
  if (!Number.isFinite(parsedGroupId)) {
    return null;
  }
  const rawName = String(record.group_name || '').trim();
  const groupName = rawName || `Group ${parsedGroupId || fallbackIndex + 1}`;
  const parsedParentId = record.parent_group_id == null ? null : Number(record.parent_group_id);
  const parentGroupId = Number.isFinite(parsedParentId) ? parsedParentId : null;
  const parsedDepth = record.group_depth == null ? undefined : Number(record.group_depth);
  const groupDepth = Number.isFinite(parsedDepth) ? parsedDepth : undefined;
  const groupTypeRaw = String(record.group_type || '').trim().toLowerCase();
  const groupType = groupTypeRaw === 'team' || groupTypeRaw === 'organization' ? groupTypeRaw : undefined;
  const features = Array.isArray(record.features)
    ? record.features.map((feature) => String(feature || '').trim()).filter(Boolean)
    : undefined;
  return {
    group_id: parsedGroupId,
    group_name: groupName,
    parent_group_id: parentGroupId,
    group_type: groupType,
    group_path: String(record.group_path || '').trim() || undefined,
    group_depth: groupDepth,
    features,
  };
};

const formatLastAccessed = (value?: string | null): string => {
  if (!value) {
    return 'Not Accessed';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Unknown';
  }
  return parsed.toLocaleString();
};

const formatTimestamp = (value?: string | null): string => {
  if (!value) {
    return 'Unknown';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Unknown';
  }
  return parsed.toLocaleString();
};

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

const formatUsd = (value: unknown, fractionDigits = 2, fallback = 'Modeled estimate'): string => {
  if (!isFiniteNumber(value)) {
    return fallback;
  }
  return `$${value.toFixed(fractionDigits)}`;
};

const formatCount = (value: unknown, fallback = '0'): string => {
  if (!isFiniteNumber(value)) {
    return fallback;
  }
  return `${Math.round(value)}`;
};

const formatBillingWindow = (period?: { start?: string; end_exclusive?: string } | null): string => {
  if (!period?.start || !period?.end_exclusive) {
    return 'Current billing cycle (modeled)';
  }
  const start = new Date(period.start);
  const endExclusive = new Date(period.end_exclusive);
  if (Number.isNaN(start.getTime()) || Number.isNaN(endExclusive.getTime())) {
    return 'Current billing cycle (modeled)';
  }
  const end = new Date(endExclusive.getTime() - 1);
  return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
};

const loadLocalJson = <T,>(key: string, fallback: T): T => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return fallback;
    }
    const parsed = JSON.parse(raw) as T;
    return parsed;
  } catch {
    return fallback;
  }
};

const saveLocalJson = <T,>(key: string, value: T): void => {
  window.localStorage.setItem(key, JSON.stringify(value));
};

const toTitleCase = (value: string): string =>
  value
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(' ');

const formatResourceName = (resource: string): string =>
  toTitleCase(resource.replace(/[_-]+/g, ' '));

const formatActionName = (action: string): string =>
  toTitleCase(action.replace(/[_-]+/g, ' '));

const sanitizeInviteDeliveryError = (value: string): string => {
  const normalized = String(value || '').trim();
  if (!normalized) {
    return '';
  }
  const lower = normalized.toLowerCase();
  if (
    lower.includes('credentials are not available')
    || lower.includes('unable to locate credentials')
    || lower.includes('access key')
    || lower.includes('secret key')
    || lower.includes('token')
    || lower.includes('signature')
    || lower.includes('email delivery configuration error')
  ) {
    return 'Email provider credentials are not configured for automatic delivery in this environment.';
  }
  return normalized;
};

const dedupeTokens = (values: string[], separator = ' | '): string => {
  const seen = new Set<string>();
  const ordered: string[] = [];
  values.forEach((value) => {
    const normalized = String(value || '').trim();
    if (!normalized) {
      return;
    }
    const key = normalized.toLowerCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    ordered.push(normalized);
  });
  return ordered.join(separator);
};

const getPasswordStrengthLabel = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return 'Enter at least 12 characters.';
  }
  let score = 0;
  if (trimmed.length >= 12) score += 1;
  if (trimmed.length >= 16) score += 1;
  if (/[A-Z]/.test(trimmed) && /[a-z]/.test(trimmed)) score += 1;
  if (/\d/.test(trimmed)) score += 1;
  if (/[^A-Za-z0-9]/.test(trimmed)) score += 1;

  if (score <= 2) return 'Strength: low';
  if (score <= 4) return 'Strength: medium';
  return 'Strength: high';
};

const describePermission = (permission: IamPermission): string => {
  const subject = formatResourceName(permission.resource);
  if (permission.domain === 'users' || permission.domain === 'groups' || permission.domain === 'roles') {
    return `${formatActionName(permission.action)} ${subject} records in Access Management.`;
  }
  if (permission.domain === 'themes') {
    return `${formatActionName(permission.action)} website theming tokens and branding assets.`;
  }
  if (permission.domain === 'products') {
    return `${formatActionName(permission.action)} product-level entitlements for ${subject}.`;
  }
  if (permission.domain === 'services') {
    return `${formatActionName(permission.action)} service operations for ${subject}.`;
  }
  return `${formatActionName(permission.action)} feature controls for ${subject}.`;
};

const mapPermissionCatalogRow = (
  permission: IamPermission,
  usedCount: number,
): PermissionCatalogRow => {
  const isAdminDomain =
    permission.domain === 'users'
    || permission.domain === 'groups'
    || permission.domain === 'roles'
    || permission.domain === 'themes';

  let groupLabel = 'Services';
  let groupFilter: PermissionGroupFilter = 'services';
  if (permission.domain === 'products') {
    groupLabel = 'Products';
    groupFilter = 'products';
  } else if (permission.domain === 'features') {
    groupLabel = 'Features';
    groupFilter = 'features';
  } else if (permission.domain === 'users' || permission.domain === 'groups' || permission.domain === 'roles') {
    groupLabel = 'Identity';
    groupFilter = 'identity';
  } else if (permission.domain === 'themes') {
    groupLabel = 'Themes';
    groupFilter = 'themes';
  }

  const policyName = `${formatActionName(permission.action)} ${formatResourceName(permission.resource)}`;
  return {
    id: permission.id,
    policyName,
    typeLabel: isAdminDomain ? 'Administrative' : 'Service operation',
    groupLabel,
    groupFilter,
    description: describePermission(permission),
    usedAs: usedCount > 0 ? `Permissions policy (${usedCount})` : 'None',
    usedCount,
    typeFilter: isAdminDomain ? 'admin' : 'service',
  };
};

// ---------------------------------------------------------------------------
// KeycloakAdminPanel — embedded Identity Provider admin view
// ---------------------------------------------------------------------------

interface KeycloakUser {
  id: string;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  enabled: boolean;
  email_verified: boolean;
  created_timestamp?: number;
  required_actions?: string[];
}

interface SignInProvider {
  id: string;
  name: string;
  protocol: string;
  enabled: boolean;
  connected?: boolean;
  password?: boolean;
  detail: string;
}

interface KeycloakRealmInfo {
  configured: boolean;
  issuer?: string;
  admin_url?: string;
  client_id?: string;
  realm?: string;
  display_name?: string;
  enabled?: boolean;
  error?: string;
  providers?: SignInProvider[];
}

const FALLBACK_SIGN_IN_PROVIDERS: SignInProvider[] = [
  {
    id: 'google',
    name: 'Google',
    protocol: 'OpenID Connect',
    enabled: true,
    password: false,
    detail: 'A verified Google address signs in. NextCompli does not ask that person for a password.',
  },
  {
    id: 'microsoft',
    name: 'Microsoft',
    protocol: 'OpenID Connect',
    enabled: false,
    password: false,
    detail: 'A Microsoft work or personal account signs in the same way. The durable key is the Microsoft object id.',
  },
  {
    id: 'company',
    name: 'Company sign-in',
    protocol: 'Broker',
    enabled: true,
    connected: false,
    password: false,
    detail: 'Keycloak is the broker for a company directory. People still sign in with Google or Microsoft when those are turned on.',
  },
];

const plainBrokerError = (raw: string): string => {
  if (raw.includes('400') && raw.includes('openid-connect/token')) {
    return 'The realm rejected the admin sign-in. The client id or secret does not match this realm.';
  }
  return raw.replace(/For more information check:\s*https?:\/\/\S+/gi, '').trim();
};

const KcBadge: React.FC<{ variant: 'success' | 'warning' | 'error' | 'neutral'; children: React.ReactNode }> = ({ variant, children }) => {
  const colors: Record<string, { bg: string; color: string }> = {
    success: { bg: 'color-mix(in srgb, var(--color-success) 18%, var(--color-surface))', color: 'var(--color-success)' },
    warning: { bg: 'color-mix(in srgb, #f59e0b 18%, var(--color-surface))', color: 'color-mix(in srgb, #f59e0b 70%, var(--color-text-strong))' },
    error:   { bg: 'color-mix(in srgb, var(--color-danger) 18%, var(--color-surface))', color: 'var(--color-danger)' },
    neutral: { bg: 'var(--color-surface-muted)', color: 'var(--color-text-strong)' },
  };
  const { bg, color } = colors[variant];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600, background: bg, color }}>
      {children}
    </span>
  );
};

const KeycloakAdminPanel: React.FC = () => {
  const { accessToken } = useAuth();
  const [realm, setRealm] = React.useState<KeycloakRealmInfo | null>(null);
  const [users, setUsers] = React.useState<KeycloakUser[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState('');
  const [sending, setSending] = React.useState<string | null>(null);
  const [sendResult, setSendResult] = React.useState<{ id: string; ok: boolean } | null>(null);
  const [verifying, setVerifying] = React.useState<string | null>(null);
  const [verifyResult, setVerifyResult] = React.useState<{ id: string; ok: boolean } | null>(null);
  const [deleting, setDeleting] = React.useState<string | null>(null);

  const authHeaders = React.useMemo(() => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    return headers;
  }, [accessToken]);

  const loadData = React.useCallback(async (searchTerm?: string) => {
    setLoading(true);
    setError(null);
    try {
      const [realmResp, usersResp] = await Promise.all([
        fetch(`${GATEWAY_BASE_URL}/api/v1/admin/keycloak/realm`, { headers: authHeaders }),
        fetch(`${GATEWAY_BASE_URL}/api/v1/admin/keycloak/users${searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : ''}`, { headers: authHeaders }),
      ]);
      if (realmResp.ok) setRealm(await realmResp.json());
      if (usersResp.ok) {
        const data = await usersResp.json();
        setUsers(data.users || []);
      } else if (usersResp.status !== 503) {
        const body = await usersResp.json().catch(() => ({}));
        setError(body.detail || `Failed to load users (${usersResp.status})`);
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  React.useEffect(() => { loadData(); }, [loadData]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadData(search.trim() || undefined);
  };

  const handleDeleteUser = async (userId: string, email: string) => {
    if (!window.confirm(`Delete Keycloak user "${email}"? This cannot be undone.`)) return;
    setDeleting(userId);
    const encodedUserId = encodeURIComponent(userId);
    try {
      const resp = await fetch(`${GATEWAY_BASE_URL}/api/v1/admin/keycloak/users/${encodedUserId}`, { method: 'DELETE', headers: authHeaders });
      if (resp.ok) {
        setUsers(prev => prev.filter(u => u.id !== userId));
      } else {
        const body = await resp.json().catch(() => ({}));
        alert(`Delete failed: ${body.detail || resp.status}`);
      }
    } catch (e) {
      alert(`Delete failed: ${String(e)}`);
    } finally {
      setDeleting(null);
    }
  };

  const handleResendVerification = async (userId: string) => {
    setSending(userId);
    setSendResult(null);
    const encodedUserId = encodeURIComponent(userId);
    try {
      const resp = await fetch(`${GATEWAY_BASE_URL}/api/v1/admin/keycloak/users/${encodedUserId}/resend-verification`, { method: 'POST', headers: authHeaders });
      setSendResult({ id: userId, ok: resp.ok });
    } catch {
      setSendResult({ id: userId, ok: false });
    } finally {
      setSending(null);
    }
  };

  const handleForceVerify = async (userId: string, probe: string) => {
    setVerifying(userId);
    setVerifyResult(null);
    try {
      const resp = await fetch(
        `${GATEWAY_BASE_URL}/api/v1/admin/keycloak/users/force-verify?probe=${encodeURIComponent(probe)}`,
        { method: 'POST', headers: authHeaders },
      );
      if (resp.ok) {
        setUsers(prev => prev.map(u => {
          if (u.id !== userId) return u;
          const actions = (u.required_actions || []).filter(a => a.toUpperCase() !== 'VERIFY_EMAIL');
          return { ...u, email_verified: true, required_actions: actions };
        }));
      }
      setVerifyResult({ id: userId, ok: resp.ok });
    } catch {
      setVerifyResult({ id: userId, ok: false });
    } finally {
      setVerifying(null);
    }
  };

  const keycloakConsoleUrl = (() => {
    const realmName = realm?.realm || 'nextcompli';
    if (realm?.admin_url) {
      // Strip everything from /admin/ onward and rebuild the correct console path
      const base = realm.admin_url.replace(/\/admin(?:\/.*)?$/, '');
      return `${base}/admin/${realmName}/console`;
    }
    return `https://auth.nextcompli.com/admin/${realmName}/console`;
  })();

  const activeUsers   = users.filter(u => u.enabled);
  const unverified    = users.filter(u => !u.email_verified);
  const pendingAction = users.filter(u => (u.required_actions?.length ?? 0) > 0);

  const formatJoined = (ts?: number) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const realmName = realm?.display_name || realm?.realm || 'nextcompli';
  const realmIssuer = realm?.issuer || 'https://auth.nextcompli.com/realms/nextcompli';

  return (
    <div className="keycloak-admin-panel">
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', gap: '1rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>Identity Provider</h2>
            {realm?.configured !== false && !realm?.error && (
              <KcBadge variant="success">● Connected</KcBadge>
            )}
            {realm?.error && <KcBadge variant="error">● Disconnected</KcBadge>}
          </div>
          <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
            People sign in with Google or Microsoft. Company sign-in brokers a directory. None of those ask for a new password.
          </p>
        </div>
        <a href={keycloakConsoleUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary" style={{ fontSize: '0.8125rem', whiteSpace: 'nowrap', flexShrink: 0 }}>
          Open broker console
        </a>
      </div>

      <div className="access-management-splash-grid" style={{ marginBottom: '1.25rem' }}>
        {(realm?.providers?.length ? realm.providers : FALLBACK_SIGN_IN_PROVIDERS).map((provider) => (
          <article key={provider.id} className="access-management-splash-link" style={{ cursor: 'default' }}>
            <strong>{provider.name}</strong>
            <span>
              {provider.enabled ? 'On' : 'Off'}
              {provider.id === 'company' ? (provider.connected ? ' · broker answered' : ' · broker did not answer') : ''}
              {' · '}
              {provider.protocol}
            </span>
            <span>{provider.detail}</span>
          </article>
        ))}
      </div>

      {realm?.error && (
        <div style={{ background: 'color-mix(in srgb, var(--color-danger) 14%, var(--color-surface))', border: '1px solid color-mix(in srgb, var(--color-danger) 40%, var(--color-border))', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1.25rem', fontSize: '0.875rem', color: 'var(--color-danger)' }}>
          <strong>Company sign-in is not answering</strong><br />
          {plainBrokerError(realm.error)}
          <span style={{ display: 'block', marginTop: '0.35rem' }}>Realm {realmName}. {realmIssuer}</span>
        </div>
      )}

      {/* ── Stat cards ── */}
      {!loading && !realm?.error && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
          {[
            { label: 'Total Users',      value: users.length,         color: 'var(--color-primary-strong)' },
            { label: 'Active',           value: activeUsers.length,   color: 'var(--color-success)' },
            { label: 'Unverified',       value: unverified.length,    color: 'color-mix(in srgb, #f59e0b 75%, var(--color-text-strong))' },
            { label: 'Pending Actions',  value: pendingAction.length, color: 'var(--color-accent)' },
          ].map(card => (
            <div key={card.label} style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '0.5rem', padding: '0.875rem 1rem' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: card.color, lineHeight: 1 }}>{card.value}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>{card.label}</div>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>Loading…</p>
      ) : error ? (
        <div style={{ background: 'color-mix(in srgb, var(--color-danger) 14%, var(--color-surface))', border: '1px solid color-mix(in srgb, var(--color-danger) 40%, var(--color-border))', borderRadius: '0.5rem', padding: '0.75rem 1rem', fontSize: '0.875rem', color: 'var(--color-danger)' }}>{plainBrokerError(error)}</div>
      ) : (
        <>
          {/* ── Search bar ── */}
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search users by name or email…"
              className="form-input"
              style={{ flex: 1, fontSize: '0.875rem' }}
            />
            <button type="submit" className="btn btn-primary" style={{ whiteSpace: 'nowrap', fontSize: '0.875rem' }}>Search</button>
            {search && (
              <button type="button" className="btn btn-secondary" style={{ fontSize: '0.875rem' }} onClick={() => { setSearch(''); loadData(); }}>Clear</button>
            )}
          </form>

          {/* ── Users table ── */}
          <div style={{ border: '1px solid var(--color-border)', borderRadius: '0.5rem', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead>
                <tr style={{ background: 'var(--color-surface-muted)', borderBottom: '1px solid var(--color-border)' }}>
                  <th style={{ textAlign: 'left', padding: '0.625rem 0.875rem', fontWeight: 600, color: 'var(--color-text-strong)' }}>User</th>
                  <th style={{ textAlign: 'left', padding: '0.625rem 0.875rem', fontWeight: 600, color: 'var(--color-text-strong)' }}>Username</th>
                  <th style={{ textAlign: 'left', padding: '0.625rem 0.875rem', fontWeight: 600, color: 'var(--color-text-strong)' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '0.625rem 0.875rem', fontWeight: 600, color: 'var(--color-text-strong)' }}>Email Verified</th>
                  <th style={{ textAlign: 'left', padding: '0.625rem 0.875rem', fontWeight: 600, color: 'var(--color-text-strong)' }}>Joined</th>
                  <th style={{ textAlign: 'left', padding: '0.625rem 0.875rem', fontWeight: 600, color: 'var(--color-text-strong)' }}>Pending Actions</th>
                  <th style={{ padding: '0.625rem 0.875rem' }}></th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
                      No users found{search ? ` matching "${search}"` : ''}.
                    </td>
                  </tr>
                ) : users.map((u, i) => (
                  <tr key={u.id} style={{ borderBottom: i < users.length - 1 ? '1px solid var(--color-border)' : 'none', background: i % 2 === 0 ? 'var(--color-surface)' : 'var(--color-surface-muted)' }}>
                    <td style={{ padding: '0.625rem 0.875rem' }}>
                      <div style={{ fontWeight: 500, color: 'var(--color-text-strong)' }}>{[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{u.email}</div>
                    </td>
                    <td style={{ padding: '0.625rem 0.875rem', color: 'var(--color-text-primary)', fontFamily: 'monospace', fontSize: '0.8rem' }}>{u.username}</td>
                    <td style={{ padding: '0.625rem 0.875rem' }}>
                      <KcBadge variant={u.enabled ? 'success' : 'error'}>{u.enabled ? 'Enabled' : 'Disabled'}</KcBadge>
                    </td>
                    <td style={{ padding: '0.625rem 0.875rem' }}>
                      {u.email_verified
                        ? <KcBadge variant="success">Verified</KcBadge>
                        : <KcBadge variant="warning">Not verified</KcBadge>}
                    </td>
                    <td style={{ padding: '0.625rem 0.875rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>{formatJoined(u.created_timestamp)}</td>
                    <td style={{ padding: '0.625rem 0.875rem' }}>
                      {(u.required_actions?.length ?? 0) > 0
                        ? u.required_actions!.map(a => <KcBadge key={a} variant="neutral">{a.replace(/_/g, ' ').toLowerCase()}</KcBadge>)
                        : <span style={{ color: 'var(--color-text-muted)' }}>—</span>}
                    </td>
                    <td style={{ padding: '0.625rem 0.875rem', whiteSpace: 'nowrap', display: 'flex', gap: '0.375rem', alignItems: 'center' }}>
                      {sendResult?.id === u.id && (
                        <span style={{ marginRight: '0.5rem', fontSize: '0.75rem', color: sendResult.ok ? 'var(--color-success)' : 'var(--color-danger)' }}>
                          {sendResult.ok ? 'Sent ✓' : 'Failed ✗'}
                        </span>
                      )}
                      {verifyResult?.id === u.id && (
                        <span style={{ marginRight: '0.5rem', fontSize: '0.75rem', color: verifyResult.ok ? 'var(--color-success)' : 'var(--color-danger)' }}>
                          {verifyResult.ok ? 'Verified ✓' : 'Verify failed ✗'}
                        </span>
                      )}
                      {!u.email_verified ? (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ fontSize: '0.75rem', padding: '0.25rem 0.625rem' }}
                          disabled={verifying === u.id || deleting === u.id || sending === u.id}
                          onClick={() => handleForceVerify(u.id, u.email || u.username || u.id)}
                          title="Mark email as verified and clear VERIFY_EMAIL"
                        >
                          {verifying === u.id ? 'Verifying…' : 'Force verify'}
                        </button>
                      ) : null}
                      {!u.email_verified || (u.required_actions?.length ?? 0) > 0 ? (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ fontSize: '0.75rem', padding: '0.25rem 0.625rem' }}
                          disabled={sending === u.id || deleting === u.id || verifying === u.id}
                          onClick={() => handleResendVerification(u.id)}
                          title="Resend verification / set-password email"
                        >
                          {sending === u.id ? 'Sending…' : 'Resend invite'}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="btn btn-danger"
                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.625rem', background: 'color-mix(in srgb, var(--color-danger) 16%, var(--color-surface))', color: 'var(--color-danger)', border: '1px solid color-mix(in srgb, var(--color-danger) 40%, var(--color-border))' }}
                        disabled={deleting === u.id || sending === u.id || verifying === u.id}
                        onClick={() => handleDeleteUser(u.id, u.email)}
                        title="Delete this user from Keycloak"
                      >
                        {deleting === u.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {users.length > 0 && (
            <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--color-text-muted)', textAlign: 'right' }}>
              {users.length} user{users.length !== 1 ? 's' : ''} · Keycloak realm <strong>{realmName}</strong>
            </p>
          )}
        </>
      )}
    </div>
  );
};

const AdminConsolePage: React.FC = () => {
  const chrome = ADMIN_CONSOLE_COPY[storedUiLanguage()];
  const location = useLocation();
  const currentSection = sectionFromPath(location.pathname);
  const { accessToken, user } = useAuth();
  const hasSuperUserAccess = React.useMemo(() => isPlatformAdmin(user), [user]);

  const [groups, setGroups] = React.useState<AdminGroup[]>(() => loadLocalJson<AdminGroup[]>(LOCAL_GROUPS_KEY, []));
  const [users, setUsers] = React.useState<AdminUser[]>(() => loadLocalJson<AdminUser[]>(LOCAL_USERS_KEY, []));
  const [roles, setRoles] = React.useState<IamRole[]>(() => loadIamRoles());
  const [hasLoadedRolesFromApi, setHasLoadedRolesFromApi] = React.useState(false);

  const [userGroupBindings, setUserGroupBindings] = React.useState<UserGroupBinding[]>(() => loadUserGroupBindings());
  const [groupRoleBindings, setGroupRoleBindings] = React.useState<GroupRoleBinding[]>(() => loadGroupRoleBindings());
  const [groupPermissionBindings, setGroupPermissionBindings] = React.useState<GroupPermissionBinding[]>(
    () => loadGroupPermissionBindings(),
  );

  const [isLoading, setIsLoading] = React.useState(true);
  const [statusMessage, setStatusMessage] = React.useState<string | null>(null);
  const isSavingUserRef = React.useRef(false);
  const [isResendingEmail, setIsResendingEmail] = React.useState(false);
  const [resendEmailMessage, setResendEmailMessage] = React.useState<string | null>(null);
  const [resendEmailInviteUrl, setResendEmailInviteUrl] = React.useState<string | null>(null);
  const [resendEmailAttempts, setResendEmailAttempts] = React.useState<DeliveryAttempt[]>([]);
  const [emailRuntimeStatus, setEmailRuntimeStatus] = React.useState<EmailRuntimeStatus | null>(null);
  const [mailboxSignals, setMailboxSignals] = React.useState<Record<string, MailboxSignal>>({});
  const [mailboxSignalError, setMailboxSignalError] = React.useState<string | null>(null);
  const [clearingMailboxSignal, setClearingMailboxSignal] = React.useState<string | null>(null);
  const [costOptimization, setCostOptimization] = React.useState<CostOptimizationSummary | null>(null);
  const [costOptimizationError, setCostOptimizationError] = React.useState<string | null>(null);
  const [costAlertReferenceUsd] = React.useState<number>(1200);
  const [showInboxList, setShowInboxList] = React.useState(false);
  const [tier1MailboxPasswordDrafts, setTier1MailboxPasswordDrafts] = React.useState<Record<string, string>>({});
  const [tier1MailboxPasswordSubmitting, setTier1MailboxPasswordSubmitting] = React.useState<string | null>(null);
  const [isKeycloakAdmin, setIsKeycloakAdmin] = React.useState<boolean>(hasSuperUserAccess);
  const [crmLeads, setCrmLeads] = React.useState<CrmLead[]>([]);
  const [crmLeadsError, setCrmLeadsError] = React.useState<string | null>(null);
  const [isCrmLoading, setIsCrmLoading] = React.useState(false);
  const [crmSearchTerm, setCrmSearchTerm] = React.useState('');
  const [crmSourceFilter, setCrmSourceFilter] = React.useState('all');
  const [crmRecencyFilter, setCrmRecencyFilter] = React.useState<CrmLeadRecencyFilter>('all');
  const consumedLinkedUserIdRef = React.useRef<string | null>(null);
  const consumedLinkedRoleIdRef = React.useRef<string | null>(null);
  const consumedLinkedCompanyIdRef = React.useRef<string | null>(null);

  const emailDirectoryEntries = React.useMemo<EmailDirectoryEntry[]>(() => {
    const defaults: EmailDirectoryEntry[] = [
      { address: 'ceo@nextcompli.com', purpose: 'Tier 1 shared role mailbox for executive leadership', source: 'aws' },
      { address: 'no-reply@nextcompli.com', purpose: 'Primary transactional sender + WorkMail inbox', source: 'aws' },
      { address: 'support@nextcompli.com', purpose: 'Customer support / reply-to contact', source: 'config' },
      { address: 'admin@admin.com', purpose: 'Legacy admin login/testing identity', source: 'legacy' },
      { address: 'operations@nextcompli.com', purpose: 'Operations/policy contact in dashboard copy', source: 'config' },
      { address: 'compliance@nextcompli.ai', purpose: 'Compliance data service contact user-agent', source: 'config' },
    ];

    const fromEnv: EmailDirectoryEntry[] = ADMIN_EMAIL_DIRECTORY
      ? ADMIN_EMAIL_DIRECTORY.split(',')
          .map((entry) => entry.trim())
          .filter(Boolean)
          .map((address) => ({ address, purpose: 'Configured via REACT_APP_ADMIN_EMAIL_DIRECTORY', source: 'config' as const }))
      : [];

    const fromRuntimeSender: EmailDirectoryEntry[] = emailRuntimeStatus?.sender
      ? [{ address: emailRuntimeStatus.sender, purpose: 'Active runtime sender from gateway', source: 'aws' }]
      : [];

    const fromRuntimeIdentities: EmailDirectoryEntry[] = (emailRuntimeStatus?.ses?.known_identities || [])
      .map((address) => String(address || '').trim())
      .filter(Boolean)
      .map((address) => ({ address, purpose: 'Discovered SES identity in runtime region', source: 'aws' as const }));

    const fromAdminUsers: EmailDirectoryEntry[] = users
      .map((entry) => String(entry.email || '').trim())
      .filter((address) => Boolean(address) && /@(nextcompli\.com|nextcompli\.ai|admin\.com)$/i.test(address))
      .map((address) => ({ address, purpose: 'Admin user address discovered from access directory', source: 'config' as const }));

    const merged = [...fromRuntimeSender, ...fromRuntimeIdentities, ...fromAdminUsers, ...fromEnv, ...defaults];
    const deduped = new Map<string, EmailDirectoryEntry>();
    for (const item of merged) {
      if (!item.address) continue;
      const key = item.address.toLowerCase();
      if (!deduped.has(key)) {
        deduped.set(key, item);
      }
    }

    return [...deduped.values()];
  }, [emailRuntimeStatus?.sender, emailRuntimeStatus?.ses?.known_identities, users]);

  const tier1MailboxEntries = React.useMemo(
    () => emailDirectoryEntries.filter((entry) => TIER1_MAILBOX_ALLOWLIST.has(entry.address.toLowerCase())),
    [emailDirectoryEntries],
  );

  const [selectedUserIds, setSelectedUserIds] = React.useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = React.useState<number[]>([]);
  const [selectedRoleIds, setSelectedRoleIds] = React.useState<string[]>([]);
  const [expandedUserId, setExpandedUserId] = React.useState<string | null>(null);
  const [expandedGroupId, setExpandedGroupId] = React.useState<number | null>(null);
  const [expandedRoleId, setExpandedRoleId] = React.useState<string | null>(null);

  const [editingUserId, setEditingUserId] = React.useState<string | null>(null);
  const [editingGroupId, setEditingGroupId] = React.useState<number | null>(null);
  const [editingRoleId, setEditingRoleId] = React.useState<string | null>(null);

  const [isCreateUserOpen, setIsCreateUserOpen] = React.useState(false);
  const [isCreateGroupOpen, setIsCreateGroupOpen] = React.useState(false);
  const [isSavingUser, setIsSavingUser] = React.useState(false);
  const [isDeletingUsers, setIsDeletingUsers] = React.useState(false);
  const [createUserInviteUrl, setCreateUserInviteUrl] = React.useState<string | null>(null);
  const [createUserInviteCopied, setCreateUserInviteCopied] = React.useState(false);
  const [isCreateRoleOpen, setIsCreateRoleOpen] = React.useState(false);

  const [newUserEmail, setNewUserEmail] = React.useState('');
  const [newUserName, setNewUserName] = React.useState('');
  const [newUserCreateManagedMailbox, setNewUserCreateManagedMailbox] = React.useState(false);
  const [, setNewUserManagedMailboxLocalPart] = React.useState('');
  const [newUserInviteEmail, setNewUserInviteEmail] = React.useState('');
  const [editUserAssignManagedEmail, setEditUserAssignManagedEmail] = React.useState(false);
  const [editUserManagedEmailLocalPart, setEditUserManagedEmailLocalPart] = React.useState('');
  const [editUserEmailAliasesCsv, setEditUserEmailAliasesCsv] = React.useState('');
  const [newUserPasswordMode, setNewUserPasswordMode] = React.useState<CreateUserPasswordMode>('generate');
  const [newUserGroupNames, setNewUserGroupNames] = React.useState<string[]>([]);
  const [newUserPassword, setNewUserPassword] = React.useState('');
  const [newUserPasswordConfirm, setNewUserPasswordConfirm] = React.useState('');
  const [newUserRoles, setNewUserRoles] = React.useState<string[]>([]);
  const [companyJobByName, setCompanyJobByName] = React.useState<Record<string, string>>({});
  const [platformOperator, setPlatformOperator] = React.useState(false);
  const [newUserPermissionIds, setNewUserPermissionIds] = React.useState<string[]>([]);
  const [newUserPermissionSearch, setNewUserPermissionSearch] = React.useState('');
  const [newUserPermissionTypeFilter, setNewUserPermissionTypeFilter] = React.useState<PermissionTypeFilter>('all');
  const [newUserPermissionGroupFilter, setNewUserPermissionGroupFilter] = React.useState<PermissionGroupFilter>('all');
  const [newUserPermissionSelectedOnly, setNewUserPermissionSelectedOnly] = React.useState(false);
  const [newUserPermissionUnselectedOnly, setNewUserPermissionUnselectedOnly] = React.useState(false);
  const [newUserPermissionSortKey, setNewUserPermissionSortKey] = React.useState<PermissionSortKey>('policyName');
  const [newUserPermissionSortDirection, setNewUserPermissionSortDirection] = React.useState<SortDirection>('asc');
  const [newUserPermissionPage, setNewUserPermissionPage] = React.useState(1);
  const [newUserPermissionPageSize, setNewUserPermissionPageSize] = React.useState<number>(25);
  const [isCreateUserRolesOpen, setIsCreateUserRolesOpen] = React.useState(false);
  const [isCreateUserPasswordOpen, setIsCreateUserPasswordOpen] = React.useState(false);
  const [isAssignUserGroupsOpen, setIsAssignUserGroupsOpen] = React.useState(false);
  const [isCreateUserPermissionsOpen, setIsCreateUserPermissionsOpen] = React.useState(false);

  const [newGroupName, setNewGroupName] = React.useState('');
  const [newGroupPlan, setNewGroupPlan] = React.useState('basic');
  const [groupUserJobById, setGroupUserJobById] = React.useState<Record<string, string>>({});
  const [newGroupParentId, setNewGroupParentId] = React.useState<string>('');
  const [newGroupUserIds, setNewGroupUserIds] = React.useState<string[]>([]);
  const [newGroupRoleIds, setNewGroupRoleIds] = React.useState<string[]>([]);
  const [newGroupPermissionIds, setNewGroupPermissionIds] = React.useState<string[]>([]);
  const [newGroupUserSearch, setNewGroupUserSearch] = React.useState('');
  const [newGroupPermissionSearch, setNewGroupPermissionSearch] = React.useState('');
  const [newGroupPermissionTypeFilter, setNewGroupPermissionTypeFilter] = React.useState<PermissionTypeFilter>('all');
  const [newGroupPermissionGroupFilter, setNewGroupPermissionGroupFilter] = React.useState<PermissionGroupFilter>('all');
  const [newGroupPermissionSelectedOnly, setNewGroupPermissionSelectedOnly] = React.useState(false);
  const [newGroupPermissionUnselectedOnly, setNewGroupPermissionUnselectedOnly] = React.useState(false);
  const [newGroupPermissionSortKey, setNewGroupPermissionSortKey] = React.useState<PermissionSortKey>('policyName');
  const [newGroupPermissionSortDirection, setNewGroupPermissionSortDirection] = React.useState<SortDirection>('asc');
  const [newGroupPermissionPage, setNewGroupPermissionPage] = React.useState(1);
  const [newGroupPermissionPageSize, setNewGroupPermissionPageSize] = React.useState<number>(25);
  const [isCreateGroupNameOpen, setIsCreateGroupNameOpen] = React.useState(false);
  const [isCreateGroupUsersOpen, setIsCreateGroupUsersOpen] = React.useState(false);
  const [isCreateGroupRolesOpen, setIsCreateGroupRolesOpen] = React.useState(false);
  const [isCreateGroupPermissionsOpen, setIsCreateGroupPermissionsOpen] = React.useState(false);

  const [newRoleName, setNewRoleName] = React.useState('');
  const [newRoleScopeGroupId, setNewRoleScopeGroupId] = React.useState<string>('');
  const [newRoleDescription, setNewRoleDescription] = React.useState('');
  const [newRolePermissionIds, setNewRolePermissionIds] = React.useState<string[]>([]);
  const [newRolePermissionSearch, setNewRolePermissionSearch] = React.useState('');
  const [newRolePermissionTypeFilter, setNewRolePermissionTypeFilter] = React.useState<PermissionTypeFilter>('all');
  const [newRolePermissionGroupFilter, setNewRolePermissionGroupFilter] = React.useState<PermissionGroupFilter>('all');
  const [newRolePermissionSelectedOnly, setNewRolePermissionSelectedOnly] = React.useState(false);
  const [newRolePermissionUnselectedOnly, setNewRolePermissionUnselectedOnly] = React.useState(false);
  const [newRolePermissionSortKey, setNewRolePermissionSortKey] = React.useState<PermissionSortKey>('policyName');
  const [newRolePermissionSortDirection, setNewRolePermissionSortDirection] = React.useState<SortDirection>('asc');
  const [newRolePermissionPage, setNewRolePermissionPage] = React.useState(1);
  const [newRolePermissionPageSize, setNewRolePermissionPageSize] = React.useState<number>(25);
  const [isCreateRolePermissionsOpen, setIsCreateRolePermissionsOpen] = React.useState(false);

  const [userSearch, setUserSearch] = React.useState('');
  const [groupSearch, setGroupSearch] = React.useState('');
  const [showSubgroups, setShowSubgroups] = React.useState(false);

  const permissionCatalog = React.useMemo(() => {
    const products = parseCsvTokens(DEFAULT_PRODUCTS.join(','));
    const services = parseCsvTokens(DEFAULT_SERVICES.join(','));
    const features = parseCsvTokens(DEFAULT_FEATURES.join(','));
    return buildPermissionMatrix(products, services, features);
  }, []);

  const permissionCatalogRows = React.useMemo(() => {
    const usedCountByPermissionId = new Map<string, number>();
    groupPermissionBindings.forEach((binding) => {
      binding.permissionIds.forEach((permissionId) => {
        usedCountByPermissionId.set(permissionId, (usedCountByPermissionId.get(permissionId) || 0) + 1);
      });
    });

    const matrixRows = permissionCatalog
      .map((permission) => mapPermissionCatalogRow(permission, usedCountByPermissionId.get(permission.id) || 0));

    const existingIds = new Set(matrixRows.map((row) => row.id));
    const legacyRows: PermissionCatalogRow[] = LEGACY_RBAC_PERMISSION_IDS
      .filter((permissionId) => !existingIds.has(permissionId))
      .map((permissionId) => ({
        id: permissionId,
        policyName: permissionId === '*' ? 'Super User Wildcard (*)' : permissionId,
        typeLabel: 'Administrative',
        groupLabel: 'Identity',
        groupFilter: 'identity',
        description: permissionId === '*'
          ? 'Full super-user access. Use only for trusted platform administrators.'
          : `Legacy RBAC permission: ${permissionId}`,
        usedAs: `Permissions policy (${usedCountByPermissionId.get(permissionId) || 0})`,
        usedCount: usedCountByPermissionId.get(permissionId) || 0,
        typeFilter: 'admin',
      }));

    return [...matrixRows, ...legacyRows];
  }, [groupPermissionBindings, permissionCatalog]);

  React.useEffect(() => {
    if (hasLoadedRolesFromApi) {
      return;
    }

    const fullPermissionIds = permissionCatalogRows
      .map((row) => row.id)
      .filter((permissionId) => permissionId !== '*');

    setRoles((current) => {
      const byName = new Map(current.map((entry) => [entry.name.trim().toLowerCase(), entry]));
      const next = [...current];

      if (!byName.has(DEFAULT_ADMIN_ROLE_NAME.toLowerCase())) {
        next.push({
          id: roleSlug(DEFAULT_ADMIN_ROLE_NAME),
          name: DEFAULT_ADMIN_ROLE_NAME,
          description: 'Full platform administration. Mirrors super-user wildcard access.',
          permissions: fullPermissionIds,
        });
      } else {
        const existingAdminIndex = next.findIndex(
          (entry) => entry.name.trim().toLowerCase() === DEFAULT_ADMIN_ROLE_NAME.toLowerCase(),
        );
        if (existingAdminIndex >= 0) {
          const existingAdmin = next[existingAdminIndex];
          if (existingAdmin.permissions.length === 1 && existingAdmin.permissions[0] === '*') {
            next[existingAdminIndex] = {
              ...existingAdmin,
              permissions: fullPermissionIds,
            };
          }
        }
      }

      if (!byName.has(DEFAULT_CLIENT_ROLE_NAME.toLowerCase())) {
        next.push({
          id: roleSlug(DEFAULT_CLIENT_ROLE_NAME),
          name: DEFAULT_CLIENT_ROLE_NAME,
          description: 'Baseline client organization role without super-user privileges.',
          permissions: [...DEFAULT_CLIENT_ROLE_PERMISSION_IDS],
        });
      }

      return next.sort((left, right) => left.name.localeCompare(right.name));
    });
  }, [hasLoadedRolesFromApi, permissionCatalogRows]);

  const filteredUsers = React.useMemo(() => {
    const term = userSearch.trim().toLowerCase();
    if (!term) {
      return users;
    }
    return users.filter((entry) => {
      const username = getUsernameLabel(entry);
      const aliases = (entry.email_aliases || []).join(' ').toLowerCase();
      return entry.email.toLowerCase().includes(term) || username.toLowerCase().includes(term) || aliases.includes(term);
    });
  }, [userSearch, users]);

  const filteredGroups = React.useMemo(() => {
    const term = groupSearch.trim().toLowerCase().replace(/\s+/g, '');
    return groups.filter((entry) => {
      const isSubgroup = Number(entry.parent_group_id || 0) > 0;
      if (!showSubgroups && isSubgroup) {
        return false;
      }
      if (!term) {
        return true;
      }
      return String(entry.group_name || '').toLowerCase().replace(/\s+/g, '').includes(term);
    });
  }, [groupSearch, groups, showSubgroups]);

  const groupsSortedByPath = React.useMemo(() => {
    return [...groups].sort((left, right) => {
      const leftPath = String(left.group_path || left.group_name || '').toLowerCase();
      const rightPath = String(right.group_path || right.group_name || '').toLowerCase();
      const pathCompare = leftPath.localeCompare(rightPath);
      if (pathCompare !== 0) {
        return pathCompare;
      }
      return String(left.group_name || '').localeCompare(String(right.group_name || ''));
    });
  }, [groups]);

  const groupParentCandidates = React.useMemo(() => {
    return groupsSortedByPath.filter((entry) => entry.group_id !== editingGroupId);
  }, [editingGroupId, groupsSortedByPath]);

  const groupsById = React.useMemo(() => {
    return new Map<number, AdminGroup>(groups.map((entry) => [entry.group_id, entry]));
  }, [groups]);

  const topLevelGroupCandidates = React.useMemo(() => {
    return groupsSortedByPath.filter((entry) => Number(entry.parent_group_id || 0) === 0);
  }, [groupsSortedByPath]);

  const resolveTopLevelGroup = React.useCallback((groupId: number | null | undefined): AdminGroup | null => {
    const normalizedGroupId = Number(groupId || 0);
    if (normalizedGroupId <= 0) {
      return null;
    }

    let current = groupsById.get(normalizedGroupId) || null;
    const visited = new Set<number>();
    while (current && current.parent_group_id && !visited.has(current.group_id)) {
      visited.add(current.group_id);
      const parent = groupsById.get(Number(current.parent_group_id));
      if (!parent) {
        break;
      }
      current = parent;
    }
    return current;
  }, [groupsById]);

  const getTopLevelPermissionSet = React.useCallback((topLevelGroupName: string): Set<string> => {
    const normalizedTopLevelName = normalizeGroupName(topLevelGroupName);
    const directPermissionIds = groupPermissionBindings.find(
      (binding) => normalizeGroupName(binding.groupName) === normalizedTopLevelName,
    )?.permissionIds || [];
    const topLevelRoleIds = groupRoleBindings.find(
      (binding) => normalizeGroupName(binding.groupName) === normalizedTopLevelName,
    )?.roleIds || [];
    const rolePermissionIds = topLevelRoleIds.flatMap((roleId) => {
      const matchedRole = roles.find((entry) => entry.id === roleId);
      return matchedRole?.permissions || [];
    });

    return new Set([...directPermissionIds, ...rolePermissionIds]);
  }, [groupPermissionBindings, groupRoleBindings, roles]);

  const selectedRoleScopeGroup = React.useMemo(() => {
    const normalized = Number(newRoleScopeGroupId || 0);
    return normalized > 0 ? resolveTopLevelGroup(normalized) : null;
  }, [newRoleScopeGroupId, resolveTopLevelGroup]);

  const allowedRolePermissionIds = React.useMemo(() => {
    if (!selectedRoleScopeGroup) {
      return null;
    }
    return getTopLevelPermissionSet(selectedRoleScopeGroup.group_name);
  }, [getTopLevelPermissionSet, selectedRoleScopeGroup]);

  const selectedGroupParentTopLevel = React.useMemo(() => {
    const normalizedParentId = Number(newGroupParentId || 0);
    if (normalizedParentId <= 0) {
      return null;
    }
    return resolveTopLevelGroup(normalizedParentId);
  }, [newGroupParentId, resolveTopLevelGroup]);

  const allowedSubgroupPermissionIds = React.useMemo(() => {
    if (!selectedGroupParentTopLevel) {
      return null;
    }
    return getTopLevelPermissionSet(selectedGroupParentTopLevel.group_name);
  }, [getTopLevelPermissionSet, selectedGroupParentTopLevel]);

  const scopedRolesForSelectedParent = React.useMemo(() => {
    if (!selectedGroupParentTopLevel) {
      return roles;
    }
    const prefix = `${selectedGroupParentTopLevel.group_name.toLowerCase()}:`;
    return roles.filter((entry) => entry.name.toLowerCase().startsWith(prefix));
  }, [roles, selectedGroupParentTopLevel]);

  const extraRolesForSelectedParent = React.useMemo(
    () => scopedRolesForSelectedParent.filter((role) => !ACCOUNT_TEMPLATE_ROLE_NAMES.has(role.name)),
    [scopedRolesForSelectedParent],
  );

  const newGroupNameAlreadyExists = React.useMemo(() => {
    const name = newGroupName.trim();
    if (!name || editingGroupId !== null) {
      return false;
    }
    return groups.some((entry) => normalizeGroupName(entry.group_name) === normalizeGroupName(name));
  }, [editingGroupId, groups, newGroupName]);

  const selectedUser = React.useMemo(
    () => users.find((entry) => selectedUserIds.length === 1 && entry.user_id === selectedUserIds[0]) || null,
    [selectedUserIds, users],
  );

  const selectedGroup = React.useMemo(
    () => groups.find((entry) => selectedGroupIds.length === 1 && entry.group_id === selectedGroupIds[0]) || null,
    [groups, selectedGroupIds],
  );

  const selectedRole = React.useMemo(
    () => roles.find((entry) => selectedRoleIds.length === 1 && entry.id === selectedRoleIds[0]) || null,
    [roles, selectedRoleIds],
  );

  const filteredNewGroupUsers = React.useMemo(() => {
    const term = newGroupUserSearch.trim().toLowerCase();
    if (!term) {
      return users;
    }
    return users.filter((entry) => {
      const username = getUsernameLabel(entry);
      return entry.email.toLowerCase().includes(term) || username.toLowerCase().includes(term);
    });
  }, [newGroupUserSearch, users]);

  const filteredPermissionCatalog = React.useMemo(() => {
    const term = newGroupPermissionSearch.trim().toLowerCase();

    return permissionCatalogRows
      .filter((permission) => {
        if (newGroupPermissionTypeFilter !== 'all' && permission.typeFilter !== newGroupPermissionTypeFilter) {
          return false;
        }
        if (newGroupPermissionGroupFilter !== 'all' && permission.groupFilter !== newGroupPermissionGroupFilter) {
          return false;
        }
        if (newGroupPermissionSelectedOnly && !newGroupPermissionIds.includes(permission.id)) {
          return false;
        }
        if (newGroupPermissionUnselectedOnly && newGroupPermissionIds.includes(permission.id)) {
          return false;
        }
        if (allowedSubgroupPermissionIds && !allowedSubgroupPermissionIds.has(permission.id)) {
          return false;
        }
        if (!term) {
          return true;
        }
        return permission.policyName.toLowerCase().includes(term)
          || permission.id.toLowerCase().includes(term)
          || permission.typeLabel.toLowerCase().includes(term)
          || permission.groupLabel.toLowerCase().includes(term)
          || permission.description.toLowerCase().includes(term);
      });
  }, [
    newGroupPermissionGroupFilter,
    newGroupPermissionIds,
    newGroupPermissionSearch,
    newGroupPermissionSelectedOnly,
    newGroupPermissionUnselectedOnly,
    newGroupPermissionTypeFilter,
    allowedSubgroupPermissionIds,
    permissionCatalogRows,
  ]);

  const filteredUserPermissionCatalog = React.useMemo(() => {
    const term = newUserPermissionSearch.trim().toLowerCase();
    return permissionCatalogRows.filter((permission) => {
      if (newUserPermissionTypeFilter !== 'all' && permission.typeFilter !== newUserPermissionTypeFilter) {
        return false;
      }
      if (newUserPermissionGroupFilter !== 'all' && permission.groupFilter !== newUserPermissionGroupFilter) {
        return false;
      }
      if (newUserPermissionSelectedOnly && !newUserPermissionIds.includes(permission.id)) {
        return false;
      }
      if (newUserPermissionUnselectedOnly && newUserPermissionIds.includes(permission.id)) {
        return false;
      }
      if (!term) {
        return true;
      }
      return permission.policyName.toLowerCase().includes(term)
        || permission.id.toLowerCase().includes(term)
        || permission.typeLabel.toLowerCase().includes(term)
        || permission.groupLabel.toLowerCase().includes(term)
        || permission.description.toLowerCase().includes(term);
    });
  }, [
    newUserPermissionGroupFilter,
    newUserPermissionIds,
    newUserPermissionSearch,
    newUserPermissionSelectedOnly,
    newUserPermissionUnselectedOnly,
    newUserPermissionTypeFilter,
    permissionCatalogRows,
  ]);

  const sortedUserPermissionCatalog = React.useMemo(() => {
    const sorted = [...filteredUserPermissionCatalog];
    sorted.sort((left, right) => {
      if (newUserPermissionSortKey === 'usedCount') {
        const delta = left.usedCount - right.usedCount;
        return newUserPermissionSortDirection === 'asc' ? delta : -delta;
      }

      const leftValue = left[newUserPermissionSortKey];
      const rightValue = right[newUserPermissionSortKey];
      const comparison = String(leftValue).localeCompare(String(rightValue));
      return newUserPermissionSortDirection === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [filteredUserPermissionCatalog, newUserPermissionSortDirection, newUserPermissionSortKey]);

  const pagedUserPermissionCatalog = React.useMemo(() => {
    const start = (newUserPermissionPage - 1) * newUserPermissionPageSize;
    return sortedUserPermissionCatalog.slice(start, start + newUserPermissionPageSize);
  }, [newUserPermissionPage, newUserPermissionPageSize, sortedUserPermissionCatalog]);

  const userPermissionPageCount = React.useMemo(() => {
    return Math.max(1, Math.ceil(sortedUserPermissionCatalog.length / newUserPermissionPageSize));
  }, [newUserPermissionPageSize, sortedUserPermissionCatalog.length]);

  const areAllPagedUserPermissionsSelected = React.useMemo(
    () => pagedUserPermissionCatalog.length > 0
      && pagedUserPermissionCatalog.every((permission) => newUserPermissionIds.includes(permission.id)),
    [newUserPermissionIds, pagedUserPermissionCatalog],
  );

  const areAllFilteredUserPermissionsSelected = React.useMemo(
    () => sortedUserPermissionCatalog.length > 0
      && sortedUserPermissionCatalog.every((permission) => newUserPermissionIds.includes(permission.id)),
    [newUserPermissionIds, sortedUserPermissionCatalog],
  );

  const filteredRolePermissionCatalog = React.useMemo(() => {
    const term = newRolePermissionSearch.trim().toLowerCase();
    return permissionCatalogRows.filter((permission) => {
      if (newRolePermissionTypeFilter !== 'all' && permission.typeFilter !== newRolePermissionTypeFilter) {
        return false;
      }
      if (newRolePermissionGroupFilter !== 'all' && permission.groupFilter !== newRolePermissionGroupFilter) {
        return false;
      }
      if (newRolePermissionSelectedOnly && !newRolePermissionIds.includes(permission.id)) {
        return false;
      }
      if (newRolePermissionUnselectedOnly && newRolePermissionIds.includes(permission.id)) {
        return false;
      }
      if (allowedRolePermissionIds && !allowedRolePermissionIds.has(permission.id)) {
        return false;
      }
      if (!term) {
        return true;
      }
      return permission.policyName.toLowerCase().includes(term)
        || permission.id.toLowerCase().includes(term)
        || permission.typeLabel.toLowerCase().includes(term)
        || permission.groupLabel.toLowerCase().includes(term)
        || permission.description.toLowerCase().includes(term);
    });
  }, [
    newRolePermissionGroupFilter,
    newRolePermissionIds,
    newRolePermissionSearch,
    newRolePermissionSelectedOnly,
    newRolePermissionUnselectedOnly,
    newRolePermissionTypeFilter,
    allowedRolePermissionIds,
    permissionCatalogRows,
  ]);

  const sortedRolePermissionCatalog = React.useMemo(() => {
    const sorted = [...filteredRolePermissionCatalog];
    sorted.sort((left, right) => {
      if (newRolePermissionSortKey === 'usedCount') {
        const delta = left.usedCount - right.usedCount;
        return newRolePermissionSortDirection === 'asc' ? delta : -delta;
      }

      const leftValue = left[newRolePermissionSortKey];
      const rightValue = right[newRolePermissionSortKey];
      const comparison = String(leftValue).localeCompare(String(rightValue));
      return newRolePermissionSortDirection === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [filteredRolePermissionCatalog, newRolePermissionSortDirection, newRolePermissionSortKey]);

  const pagedRolePermissionCatalog = React.useMemo(() => {
    const start = (newRolePermissionPage - 1) * newRolePermissionPageSize;
    return sortedRolePermissionCatalog.slice(start, start + newRolePermissionPageSize);
  }, [newRolePermissionPage, newRolePermissionPageSize, sortedRolePermissionCatalog]);

  const rolePermissionPageCount = React.useMemo(() => {
    return Math.max(1, Math.ceil(sortedRolePermissionCatalog.length / newRolePermissionPageSize));
  }, [newRolePermissionPageSize, sortedRolePermissionCatalog.length]);

  const areAllPagedRolePermissionsSelected = React.useMemo(
    () => pagedRolePermissionCatalog.length > 0
      && pagedRolePermissionCatalog.every((permission) => newRolePermissionIds.includes(permission.id)),
    [newRolePermissionIds, pagedRolePermissionCatalog],
  );

  const areAllFilteredRolePermissionsSelected = React.useMemo(
    () => sortedRolePermissionCatalog.length > 0
      && sortedRolePermissionCatalog.every((permission) => newRolePermissionIds.includes(permission.id)),
    [newRolePermissionIds, sortedRolePermissionCatalog],
  );

  const sortedPermissionCatalog = React.useMemo(() => {
    const sorted = [...filteredPermissionCatalog];
    sorted.sort((left, right) => {
      if (newGroupPermissionSortKey === 'usedCount') {
        const delta = left.usedCount - right.usedCount;
        return newGroupPermissionSortDirection === 'asc' ? delta : -delta;
      }

      const leftValue = left[newGroupPermissionSortKey];
      const rightValue = right[newGroupPermissionSortKey];
      const comparison = String(leftValue).localeCompare(String(rightValue));
      return newGroupPermissionSortDirection === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [filteredPermissionCatalog, newGroupPermissionSortDirection, newGroupPermissionSortKey]);

  const pagedPermissionCatalog = React.useMemo(() => {
    const start = (newGroupPermissionPage - 1) * newGroupPermissionPageSize;
    return sortedPermissionCatalog.slice(start, start + newGroupPermissionPageSize);
  }, [newGroupPermissionPage, newGroupPermissionPageSize, sortedPermissionCatalog]);

  const permissionPageCount = React.useMemo(() => {
    return Math.max(1, Math.ceil(sortedPermissionCatalog.length / newGroupPermissionPageSize));
  }, [newGroupPermissionPageSize, sortedPermissionCatalog.length]);

  const areAllPagedGroupPermissionsSelected = React.useMemo(
    () => pagedPermissionCatalog.length > 0
      && pagedPermissionCatalog.every((permission) => newGroupPermissionIds.includes(permission.id)),
    [newGroupPermissionIds, pagedPermissionCatalog],
  );

  const areAllFilteredGroupPermissionsSelected = React.useMemo(
    () => sortedPermissionCatalog.length > 0
      && sortedPermissionCatalog.every((permission) => newGroupPermissionIds.includes(permission.id)),
    [newGroupPermissionIds, sortedPermissionCatalog],
  );

  const toggleAllFilteredUserPermissions = (checked: boolean) => {
    const visibleIds = sortedUserPermissionCatalog.map((permission) => permission.id);
    setNewUserPermissionIds((current) => {
      const next = new Set(current);
      if (checked) {
        visibleIds.forEach((id) => next.add(id));
      } else {
        visibleIds.forEach((id) => next.delete(id));
      }
      return Array.from(next).sort((left, right) => left.localeCompare(right));
    });
  };

  const toggleAllFilteredGroupPermissions = (checked: boolean) => {
    const visibleIds = sortedPermissionCatalog.map((permission) => permission.id);
    setNewGroupPermissionIds((current) => {
      const next = new Set(current);
      if (checked) {
        visibleIds.forEach((id) => next.add(id));
      } else {
        visibleIds.forEach((id) => next.delete(id));
      }
      return Array.from(next).sort((left, right) => left.localeCompare(right));
    });
  };

  const toggleAllFilteredRolePermissions = (checked: boolean) => {
    const visibleIds = sortedRolePermissionCatalog.map((permission) => permission.id);
    setNewRolePermissionIds((current) => {
      const next = new Set(current);
      if (checked) {
        visibleIds.forEach((id) => next.add(id));
      } else {
        visibleIds.forEach((id) => next.delete(id));
      }
      return Array.from(next).sort((left, right) => left.localeCompare(right));
    });
  };

  const getRoleLabel = (roleName: string): string => {
    if (roleName.toLowerCase() === 'admin') {
      return 'Admin (full platform administration)';
    }
    return roleName;
  };

  React.useEffect(() => {
    saveIamRoles(roles);
  }, [roles]);

  React.useEffect(() => {
    saveUserGroupBindings(userGroupBindings);
  }, [userGroupBindings]);

  React.useEffect(() => {
    saveGroupRoleBindings(groupRoleBindings);
  }, [groupRoleBindings]);

  React.useEffect(() => {
    saveGroupPermissionBindings(groupPermissionBindings);
  }, [groupPermissionBindings]);

  React.useEffect(() => {
    saveLocalJson(LOCAL_USERS_KEY, users);
  }, [users]);

  React.useEffect(() => {
    saveLocalJson(LOCAL_GROUPS_KEY, groups);
  }, [groups]);

  React.useEffect(() => {
    setNewGroupPermissionPage(1);
  }, [
    newGroupPermissionSearch,
    newGroupPermissionTypeFilter,
    newGroupPermissionGroupFilter,
    newGroupPermissionSelectedOnly,
    newGroupPermissionUnselectedOnly,
  ]);

  React.useEffect(() => {
    setNewUserPermissionPage(1);
  }, [
    newUserPermissionSearch,
    newUserPermissionTypeFilter,
    newUserPermissionGroupFilter,
    newUserPermissionSelectedOnly,
    newUserPermissionUnselectedOnly,
  ]);

  React.useEffect(() => {
    setNewRolePermissionPage(1);
  }, [
    newRolePermissionSearch,
    newRolePermissionTypeFilter,
    newRolePermissionGroupFilter,
    newRolePermissionSelectedOnly,
    newRolePermissionUnselectedOnly,
  ]);

  React.useEffect(() => {
    if (newGroupPermissionPage > permissionPageCount) {
      setNewGroupPermissionPage(permissionPageCount);
    }
  }, [newGroupPermissionPage, permissionPageCount]);

  React.useEffect(() => {
    if (newUserPermissionPage > userPermissionPageCount) {
      setNewUserPermissionPage(userPermissionPageCount);
    }
  }, [newUserPermissionPage, userPermissionPageCount]);

  React.useEffect(() => {
    if (newRolePermissionPage > rolePermissionPageCount) {
      setNewRolePermissionPage(rolePermissionPageCount);
    }
  }, [newRolePermissionPage, rolePermissionPageCount]);

  React.useEffect(() => {
    if (!allowedSubgroupPermissionIds) {
      return;
    }
    setNewGroupPermissionIds((current) => current.filter((id) => allowedSubgroupPermissionIds.has(id)));
  }, [allowedSubgroupPermissionIds]);

  React.useEffect(() => {
    if (!selectedGroupParentTopLevel) {
      return;
    }
    const allowedRoleIds = new Set(scopedRolesForSelectedParent.map((entry) => entry.id));
    setNewGroupRoleIds((current) => current.filter((id) => allowedRoleIds.has(id)));
  }, [scopedRolesForSelectedParent, selectedGroupParentTopLevel]);

  React.useEffect(() => {
    if (!allowedRolePermissionIds) {
      return;
    }
    setNewRolePermissionIds((current) => current.filter((id) => allowedRolePermissionIds.has(id)));
  }, [allowedRolePermissionIds]);

  React.useEffect(() => {
    setStatusMessage(null);
  }, [currentSection]);

  React.useEffect(() => {
    setEditingUserId(null);
    setEditingGroupId(null);
    setEditingRoleId(null);
    setSelectedUserIds([]);
    setSelectedGroupIds([]);
    setSelectedRoleIds([]);
    setExpandedUserId(null);
    setExpandedGroupId(null);
    setExpandedRoleId(null);
  }, [currentSection]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const linkedUserId = String(params.get('userId') || '').trim();
    const linkedRoleId = String(params.get('roleId') || '').trim();
    const linkedCompanyId = String(params.get('company') || '').trim();
    let consumed = false;

    if (currentSection === 'users' && linkedUserId && consumedLinkedUserIdRef.current !== linkedUserId) {
      const targetUser = users.find((entry) => entry.user_id === linkedUserId);
      if (targetUser) {
        setSelectedUserIds([linkedUserId]);
        setExpandedUserId(linkedUserId);
        setEditingUserId(linkedUserId);
        setIsCreateUserOpen(true);
        consumedLinkedUserIdRef.current = linkedUserId;
        consumed = true;
      }
    }

    if (currentSection === 'roles' && linkedRoleId && consumedLinkedRoleIdRef.current !== linkedRoleId) {
      const targetRole = roles.find((entry) => entry.id === linkedRoleId || roleSlug(entry.name) === linkedRoleId);
      if (targetRole) {
        setSelectedRoleIds([targetRole.id]);
        setExpandedRoleId(targetRole.id);
        setEditingRoleId(targetRole.id);
        setIsCreateRoleOpen(true);
        consumedLinkedRoleIdRef.current = linkedRoleId;
        consumed = true;
      }
    }

    if (currentSection === 'groups' && linkedCompanyId && consumedLinkedCompanyIdRef.current !== linkedCompanyId) {
      const companyNum = Number(linkedCompanyId);
      const targetGroup = groups.find((entry) => Number(entry.group_id) === companyNum);
      if (targetGroup && Number.isFinite(companyNum)) {
        setSelectedGroupIds([targetGroup.group_id]);
        setExpandedGroupId(targetGroup.group_id);
        const usersForGroup = users
          .filter((entry) =>
            entry.memberships.some((membership) => Number(membership.company_id) === Number(targetGroup.group_id)),
          )
          .map((entry) => entry.user_id);
        const jobs: Record<string, string> = {};
        users.forEach((entry) => {
          const membership = entry.memberships.find((row) => Number(row.company_id) === Number(targetGroup.group_id));
          if (membership) {
            jobs[entry.user_id] = companyJobFromRoles(membership.roles);
          }
        });
        setEditingGroupId(targetGroup.group_id);
        setIsCreateGroupOpen(true);
        setNewGroupName(targetGroup.group_name);
        setNewGroupParentId(targetGroup.parent_group_id ? String(targetGroup.parent_group_id) : '');
        setNewGroupPlan(planIdFromFeatures(targetGroup.features));
        setGroupUserJobById(jobs);
        setNewGroupUserIds([...usersForGroup].sort((left, right) => left.localeCompare(right)));
        setIsCreateGroupNameOpen(true);
        setIsCreateGroupUsersOpen(true);
        consumedLinkedCompanyIdRef.current = linkedCompanyId;
        consumed = true;
      }
    }

    if (consumed) {
      params.delete('userId');
      params.delete('roleId');
      params.delete('company');
      const nextQuery = params.toString();
      const nextUrl = `${location.pathname}${nextQuery ? `?${nextQuery}` : ''}${location.hash || ''}`;
      window.history.replaceState(window.history.state, '', nextUrl);
    }
  }, [currentSection, groups, location.hash, location.pathname, location.search, roles, users]);

  React.useEffect(() => {
    if (!statusMessage) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setStatusMessage(null);
    }, 5000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [statusMessage]);

  React.useEffect(() => {
    setNewGroupPermissionPage(1);
  }, [newGroupPermissionPageSize]);

  React.useEffect(() => {
    setNewUserPermissionPage(1);
  }, [newUserPermissionPageSize]);

  React.useEffect(() => {
    setNewRolePermissionPage(1);
  }, [newRolePermissionPageSize]);

  const toggleId = (id: string, setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    setter((current) => (current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id]));
  };

  const toggleNumberId = (id: number, setter: React.Dispatch<React.SetStateAction<number[]>>) => {
    setter((current) => (current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id]));
  };

  const togglePermissionSort = (key: PermissionSortKey) => {
    if (newGroupPermissionSortKey === key) {
      setNewGroupPermissionSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setNewGroupPermissionSortKey(key);
    setNewGroupPermissionSortDirection('asc');
  };

  const callAdminApi = React.useCallback(
    async (url: string, init?: RequestInit) => {
      if (!accessToken) {
        throw new Error('Missing access token.');
      }
      const extraHeaders = (init?.headers as Record<string, string> | undefined) || {};
      const response = await fetch(url, {
        ...init,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          ...extraHeaders,
        },
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as ApiErrorPayload;
        const detail = formatApiErrorValue(payload.detail);
        const message = formatApiErrorValue(payload.message);
        const fallback = formatApiErrorValue(payload.error);
        throw new Error(detail || message || fallback || `Request failed (${response.status}).`);
      }
      return response.json();
    },
    [accessToken],
  );

  const loadAdminData = React.useCallback(async () => {
    setIsLoading(true);
    try {
      if (!accessToken) {
        setStatusMessage(null);
        setIsLoading(false);
        return;
      }

      const [groupResult, userResult, roleResult, costResult, emailStatusResult, keycloakStatusResult] = await Promise.allSettled([
        callAdminApi(ADMIN_GROUPS_URL),
        callAdminApi(ADMIN_USERS_URL),
        callAdminApi(`${GATEWAY_BASE_URL}/api/v1/admin/roles`),
        callAdminApi(`${GATEWAY_BASE_URL}/api/v1/admin/cost-optimization?alert_usd=${encodeURIComponent(String(costAlertReferenceUsd || 0))}`).catch((error) => ({
          __fetchError: error instanceof Error ? error.message : 'Unable to load cost optimization data.',
        })),
        callAdminApi(`${GATEWAY_BASE_URL}/api/v1/admin/email-runtime-status`).catch(() => null),
        callAdminApi(`${GATEWAY_BASE_URL}/api/v1/auth/keycloak-admin-status`).catch(() => ({ is_keycloak_admin: false })),
      ]);

      const groupPayload = groupResult.status === 'fulfilled' ? groupResult.value : { groups: [] };
      const userPayload = userResult.status === 'fulfilled' ? userResult.value : { users: [] };
      const rolePayload = roleResult.status === 'fulfilled' ? roleResult.value : { roles: [] };
      const costPayload = costResult.status === 'fulfilled' ? costResult.value : { __fetchError: 'Unable to load cost optimization data.' };
      const emailStatusPayload = emailStatusResult.status === 'fulfilled' ? emailStatusResult.value : null;
      const keycloakStatusPayload = keycloakStatusResult.status === 'fulfilled' ? keycloakStatusResult.value : { is_keycloak_admin: false };

      const nextGroups = Array.isArray((groupPayload as { groups?: unknown[] }).groups)
        ? ((groupPayload as { groups?: unknown[] }).groups || [])
            .map((entry, index) => normalizeAdminGroup(entry, index))
            .filter((entry): entry is AdminGroup => Boolean(entry))
            .filter((entry) => entry.group_name.trim().toLowerCase() !== 'keycloak')
        : [];
      const nextUsers = Array.isArray((userPayload as { users?: unknown[] }).users)
        ? ((userPayload as { users?: AdminUser[] }).users || [])
        : [];
      const nextRoles = Array.isArray((rolePayload as { roles?: unknown[] }).roles)
        ? ((rolePayload as { roles?: IamRole[] }).roles || [])
            .map((entry) => ({
              id: String(entry.id || roleSlug(String(entry.name || 'role'))),
              name: String(entry.name || '').trim(),
              description: String(entry.description || ''),
              permissions: Array.isArray(entry.permissions)
                ? entry.permissions.map((permission) => String(permission || '')).filter(Boolean)
                : [],
            }))
            .map((entry) => {
              if (entry.permissions.length === 1 && entry.permissions[0] === '*') {
                return {
                  ...entry,
                  permissions: permissionCatalogRows
                    .map((row) => row.id)
                    .filter((permissionId) => permissionId !== '*'),
                };
              }
              return entry;
            })
            .filter((entry) => entry.name.length > 0)
            .sort((left, right) => left.name.localeCompare(right.name))
        : [];

      setGroups(nextGroups);
      setUsers(nextUsers);
      if (nextRoles.length > 0) {
        setRoles(nextRoles);
        setHasLoadedRolesFromApi(true);
      }
      if (costPayload && typeof costPayload === 'object' && !('__fetchError' in costPayload)) {
        setCostOptimization(costPayload as CostOptimizationSummary);
        setCostOptimizationError(null);
      } else {
        setCostOptimization(null);
        const detail = costPayload && typeof costPayload === 'object' && '__fetchError' in costPayload
          ? String((costPayload as { __fetchError?: string }).__fetchError || '')
          : '';
        setCostOptimizationError(detail || 'Cost optimization data is unavailable right now.');
      }
      setEmailRuntimeStatus(emailStatusPayload && typeof emailStatusPayload === 'object' ? (emailStatusPayload as EmailRuntimeStatus) : null);
      const keycloakApiAdmin = keycloakStatusPayload && typeof keycloakStatusPayload === 'object'
        && (keycloakStatusPayload as { is_keycloak_admin?: boolean }).is_keycloak_admin === true;
      setIsKeycloakAdmin(Boolean(keycloakApiAdmin || hasSuperUserAccess));
      setStatusMessage(null);
    } catch {
      setCostOptimization(null);
      setCostOptimizationError('Cost optimization data is unavailable right now.');
      setEmailRuntimeStatus(null);
      setIsKeycloakAdmin(hasSuperUserAccess);
      setStatusMessage(null);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, callAdminApi, costAlertReferenceUsd, hasSuperUserAccess]);

  React.useEffect(() => {
    void loadAdminData();
  }, [loadAdminData]);

  const loadMailboxSignals = React.useCallback(async () => {
    if (!accessToken || emailDirectoryEntries.length === 0) {
      setMailboxSignals({});
      setMailboxSignalError(null);
      return;
    }

    const params = new URLSearchParams();
    emailDirectoryEntries.forEach((entry) => params.append('email', entry.address));
    try {
      const payload = await callAdminApi(`${GATEWAY_BASE_URL}/api/v1/email-operations/mailbox-signals?${params.toString()}`) as { signals?: MailboxSignal[] };
      const nextSignals = new Map<string, MailboxSignal>();
      (payload.signals || []).forEach((signal) => {
        const normalizedEmail = String(signal.email || '').trim().toLowerCase();
        if (normalizedEmail) {
          nextSignals.set(normalizedEmail, { ...signal, email: normalizedEmail });
        }
      });
      setMailboxSignals(Object.fromEntries(nextSignals.entries()));
      setMailboxSignalError(null);
    } catch (error) {
      setMailboxSignalError(error instanceof Error ? error.message : 'Unable to load mailbox signals right now.');
    }
  }, [accessToken, callAdminApi, emailDirectoryEntries]);

  React.useEffect(() => {
    void loadMailboxSignals();
  }, [loadMailboxSignals]);

  const clearMailboxSignal = React.useCallback(
    async (mailboxAddress: string) => {
      const normalizedAddress = String(mailboxAddress || '').trim().toLowerCase();
      if (!normalizedAddress) {
        return;
      }
      setClearingMailboxSignal(normalizedAddress);
      try {
        const payload = await callAdminApi(
          `${GATEWAY_BASE_URL}/api/v1/email-operations/mailbox-signals/${encodeURIComponent(normalizedAddress)}/clear`,
          { method: 'POST' },
        ) as { signal?: MailboxSignal };
        setMailboxSignals((current) => ({
          ...current,
          [normalizedAddress]: payload.signal
            ? { ...payload.signal, email: normalizedAddress }
            : {
                email: normalizedAddress,
                has_mail: false,
                last_event_type: 'cleared',
                last_event_at: new Date().toISOString(),
                provider: 'nextcompli',
              },
        }));
        setMailboxSignalError(null);
      } catch (error) {
        setMailboxSignalError(error instanceof Error ? error.message : 'Unable to clear mailbox signal right now.');
      } finally {
        setClearingMailboxSignal(null);
      }
    },
    [callAdminApi],
  );

  const loadCrmLeads = React.useCallback(async () => {
    if (currentSection !== 'crm') {
      return;
    }
    setIsCrmLoading(true);
    try {
      const payload = await callAdminApi(`${GATEWAY_BASE_URL}/api/crm/leads?limit=100`);
      setCrmLeads(Array.isArray(payload?.leads) ? (payload.leads as CrmLead[]) : []);
      setCrmLeadsError(null);
    } catch (error) {
      setCrmLeads([]);
      setCrmLeadsError(error instanceof Error ? error.message : 'Unable to load CRM leads right now.');
    } finally {
      setIsCrmLoading(false);
    }
  }, [callAdminApi, currentSection]);

  React.useEffect(() => {
    void loadCrmLeads();
  }, [loadCrmLeads]);

  const crmSourceOptions = React.useMemo(() => {
    const unique = new Set<string>();
    crmLeads.forEach((lead) => {
      const normalized = String(lead.source || '').trim();
      if (normalized) {
        unique.add(normalized);
      }
    });
    return ['all', ...Array.from(unique).sort((left, right) => left.localeCompare(right))];
  }, [crmLeads]);

  const filteredCrmLeads = React.useMemo(() => {
    const nowMs = Date.now();
    const recencyCutoffMs =
      crmRecencyFilter === '7d'
        ? nowMs - (7 * 24 * 60 * 60 * 1000)
        : crmRecencyFilter === '30d'
          ? nowMs - (30 * 24 * 60 * 60 * 1000)
          : 0;
    const query = crmSearchTerm.trim().toLowerCase();

    return crmLeads.filter((lead) => {
      if (crmSourceFilter !== 'all' && String(lead.source || '') !== crmSourceFilter) {
        return false;
      }

      if (recencyCutoffMs > 0) {
        const submittedMs = new Date(lead.submitted_at || lead.created_at).getTime();
        if (!Number.isFinite(submittedMs) || submittedMs < recencyCutoffMs) {
          return false;
        }
      }

      if (!query) {
        return true;
      }

      const haystack = [
        lead.fullName,
        lead.workEmail,
        lead.phone,
        lead.company,
        lead.useCase,
        lead.source,
        lead.locale,
        lead.page_path,
        lead.employees,
      ]
        .map((value) => String(value || '').toLowerCase())
        .join(' ');

      return haystack.includes(query);
    });
  }, [crmLeads, crmRecencyFilter, crmSearchTerm, crmSourceFilter]);

  const exportCrmLeadsCsv = React.useCallback(() => {
    if (filteredCrmLeads.length === 0) {
      setStatusMessage('No CRM leads to export with the current filters.');
      return;
    }

    const headers = ['lead_id', 'full_name', 'work_email', 'phone', 'company', 'employees', 'use_case', 'source', 'locale', 'page_path', 'submitted_at', 'created_at'];
    const escapeCell = (value: unknown): string => {
      const text = String(value ?? '');
      if (/[",\n\r]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    };

    const rows = filteredCrmLeads.map((lead) => [
      lead.lead_id,
      lead.fullName,
      lead.workEmail,
      lead.phone || '',
      lead.company || '',
      lead.employees || '',
      lead.useCase || '',
      lead.source || '',
      lead.locale || '',
      lead.page_path || '',
      lead.submitted_at,
      lead.created_at,
    ]);

    const csv = [headers.join(','), ...rows.map((row) => row.map(escapeCell).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
    anchor.href = url;
    anchor.download = `nextcompli-crm-leads-${stamp}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    setStatusMessage(`Exported ${filteredCrmLeads.length} CRM leads to CSV.`);
  }, [filteredCrmLeads]);

  const copyCrmEmails = React.useCallback(async () => {
    const uniqueEmails = Array.from(
      new Set(
        filteredCrmLeads
          .map((lead) => String(lead.workEmail || '').trim().toLowerCase())
          .filter((email) => Boolean(email)),
      ),
    );
    if (uniqueEmails.length === 0) {
      setStatusMessage('No lead emails available to copy.');
      return;
    }

    const payload = uniqueEmails.join(', ');
    try {
      await navigator.clipboard.writeText(payload);
      setStatusMessage(`Copied ${uniqueEmails.length} CRM lead emails.`);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = payload;
      textarea.setAttribute('readonly', 'true');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      textarea.remove();
      setStatusMessage(`Copied ${uniqueEmails.length} CRM lead emails.`);
    }
  }, [filteredCrmLeads]);

  const resetTier1MailboxPassword = React.useCallback(
    async (mailboxAddress: string) => {
      const normalizedAddress = String(mailboxAddress || '').trim().toLowerCase();
      const nextPassword = (tier1MailboxPasswordDrafts[normalizedAddress] || '').trim();
      if (!nextPassword) {
        setStatusMessage('Enter a new mailbox password before submitting.');
        return;
      }
      if (nextPassword.length < 12) {
        setStatusMessage('Tier 1 mailbox passwords must be at least 12 characters.');
        return;
      }

      setTier1MailboxPasswordSubmitting(normalizedAddress);
      try {
        await callAdminApi(`${ADMIN_TIER1_MAILBOX_PASSWORD_URL}/${encodeURIComponent(normalizedAddress)}/password`, {
          method: 'POST',
          body: JSON.stringify({ password: nextPassword }),
        });
        setTier1MailboxPasswordDrafts((current) => ({ ...current, [normalizedAddress]: '' }));
        setStatusMessage(`Password updated for ${normalizedAddress}.`);
      } catch (error) {
        setStatusMessage(error instanceof Error ? error.message : 'Unable to update mailbox password right now.');
      } finally {
        setTier1MailboxPasswordSubmitting((current) => (current === normalizedAddress ? null : current));
      }
    },
    [callAdminApi, tier1MailboxPasswordDrafts],
  );

  const startEditSelectedUser = () => {
    if (!selectedUser) {
      return;
    }

    const visibleAccounts = accountMembershipsForDisplay(selectedUser.memberships);
    const mappedGroups = visibleAccounts.map((account) => account.name);
    const mappedRoles = selectedUser.memberships.flatMap((membership) => membershipRoleNames(membership));

    setEditingUserId(selectedUser.user_id);
    setIsCreateUserOpen(true);
    setNewUserName(selectedUser.email);
    setNewUserEmail(selectedUser.email);
    const jobs: Record<string, string> = {};
    visibleAccounts.forEach((account) => {
      jobs[account.name] = companyJobFromRoles(account.roles);
    });
    setCompanyJobByName(jobs);
    setPlatformOperator(mappedRoles.map((role) => role.toLowerCase()).includes('platform_admin'));
    setNewUserCreateManagedMailbox(false);
    setNewUserManagedMailboxLocalPart('');
    setNewUserInviteEmail('');
    setEditUserAssignManagedEmail(false);
    setEditUserManagedEmailLocalPart('');
    setEditUserEmailAliasesCsv((selectedUser.email_aliases || []).join(', '));
    setNewUserGroupNames([...mappedGroups].sort((left, right) => left.localeCompare(right)));
    setNewUserRoles(Array.from(new Set(mappedRoles)).sort((left, right) => left.localeCompare(right)));
    setNewUserPassword('');
    setNewUserPasswordConfirm('');
    setIsCreateUserPasswordOpen(true);
    setIsCreateUserPermissionsOpen(false);
    setStatusMessage(null);
  };

  const showUsersList = () => {
    setIsCreateUserOpen(false);
    setEditingUserId(null);
    setSelectedUserIds([]);
    setExpandedUserId(null);
    setNewUserEmail('');
    setNewUserName('');
    setNewUserCreateManagedMailbox(false);
    setNewUserManagedMailboxLocalPart('');
    setNewUserInviteEmail('');
    setNewUserPasswordMode('generate');
    setNewUserPassword('');
    setNewUserPasswordConfirm('');
    setNewUserGroupNames([]);
    setNewUserRoles([]);
    setEditUserEmailAliasesCsv('');
    setStatusMessage(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const startEditSelectedGroup = () => {
    if (!selectedGroup) {
      return;
    }

    const usersForGroup = users
      .filter((entry) =>
        entry.memberships.some(
          (membership) =>
            Number(membership.company_id) === Number(selectedGroup.group_id)
            || String(membership.company_name || '').trim().toLowerCase() === selectedGroup.group_name.trim().toLowerCase(),
        ),
      )
      .map((entry) => entry.user_id);
    const rolesForGroup = groupRoleBindings.find((binding) => binding.groupName === selectedGroup.group_name)?.roleIds || [];
    const permissionsForGroup = groupPermissionBindings.find((binding) => binding.groupName === selectedGroup.group_name)?.permissionIds || [];

    setEditingGroupId(selectedGroup.group_id);
    setIsCreateGroupOpen(true);
    const selectedTopLevel = selectedGroup.parent_group_id
      ? resolveTopLevelGroup(Number(selectedGroup.parent_group_id))
      : null;
    setNewGroupName(
      selectedTopLevel
        ? stripScopePrefix(selectedTopLevel.group_name, selectedGroup.group_name)
        : selectedGroup.group_name,
    );
    setNewGroupParentId(selectedGroup.parent_group_id ? String(selectedGroup.parent_group_id) : '');
    setNewGroupPlan(planIdFromFeatures(selectedGroup.features));
    const jobs: Record<string, string> = {};
    users.forEach((entry) => {
      const membership = entry.memberships.find((row) => Number(row.company_id) === Number(selectedGroup.group_id));
      if (membership) {
        jobs[entry.user_id] = companyJobFromRoles(membership.roles);
      }
    });
    setGroupUserJobById(jobs);
    setNewGroupUserIds([...usersForGroup].sort((left, right) => left.localeCompare(right)));
    setNewGroupRoleIds([...rolesForGroup].sort((left, right) => left.localeCompare(right)));
    setNewGroupPermissionIds([...permissionsForGroup].sort((left, right) => left.localeCompare(right)));
    setIsCreateGroupNameOpen(true);
    setIsCreateGroupUsersOpen(true);
    setIsCreateGroupRolesOpen(true);
    setIsCreateGroupPermissionsOpen(false);
    setStatusMessage(null);
  };

  const startEditSelectedRole = () => {
    if (!selectedRole) {
      return;
    }

    setEditingRoleId(selectedRole.id);
    setIsCreateRoleOpen(true);
    const inferredRoleScope = topLevelGroupCandidates.find(
      (entry) => selectedRole.name.toLowerCase().startsWith(`${entry.group_name.toLowerCase()}:`),
    );
    setNewRoleScopeGroupId(inferredRoleScope ? String(inferredRoleScope.group_id) : '');
    setNewRoleName(
      inferredRoleScope
        ? stripScopePrefix(inferredRoleScope.group_name, selectedRole.name)
        : selectedRole.name,
    );
    setNewRoleDescription(selectedRole.description || '');
    setNewRolePermissionIds([...selectedRole.permissions].sort((left, right) => left.localeCompare(right)));
    setNewRolePermissionSelectedOnly(false);
    setNewRolePermissionUnselectedOnly(false);
    setIsCreateRolePermissionsOpen(true);
    setStatusMessage(null);
  };

  const createUser = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSavingUserRef.current) {
      return;
    }
    isSavingUserRef.current = true;
    setIsSavingUser(true);
    try {
      let normalizedEmail = newUserEmail.trim().toLowerCase();
      const normalizedUserName = newUserName.trim();
      const normalizedInviteEmail = newUserInviteEmail.trim().toLowerCase();
      const managedMailboxLocalPart = newUserName
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '.')
        .replace(/[^a-z0-9._-]/g, '')
        .replace(/\.{2,}/g, '.')
        .replace(/^[._-]+|[._-]+$/g, '');
      const nextPassword = newUserPassword.trim();
      const nextPasswordConfirm = newUserPasswordConfirm.trim();
      const rolesForGroupMembership = (groupId: number): string[] => {
        const group = groups.find((entry) => entry.group_id === groupId);
        const groupName = String(group?.group_name || '');
        const job = companyJobFromRoles([companyJobByName[groupName] || 'viewer']);
        const existingRoles = (users.find((entry) => entry.user_id === editingUserId)?.memberships || [])
          .filter((membership) => Number(membership.company_id) === groupId)
          .flatMap((membership) => membership.roles || []);
        const kept = existingRoles.filter((role) => ![
          'company_admin',
          'contributor',
          'viewer',
          'platform_admin',
          'admin',
        ].includes(role.trim().toLowerCase()));
        const roles = [job, ...kept];
        if (platformOperator && ['admin', 'nextcompli'].includes(groupName.trim().toLowerCase())) {
          roles.push('platform_admin');
        }
        return Array.from(new Set(roles));
      };
      if (!normalizedEmail || !normalizedUserName) {
        return;
      }

      if (!editingUserId && newUserCreateManagedMailbox) {
        if (!managedMailboxLocalPart || !/^[a-z0-9][a-z0-9._-]{0,63}$/.test(managedMailboxLocalPart)) {
          setStatusMessage('Managed mailbox local-part must start with a letter/number and use only letters, numbers, dot, underscore, or hyphen.');
          return;
        }
        normalizedEmail = `${managedMailboxLocalPart}@${MANAGED_EMAIL_DOMAIN}`;
      }

      if (editingUserId && editUserAssignManagedEmail) {
        const managedLocalPart = editUserManagedEmailLocalPart
          .trim()
          .toLowerCase()
          .replace(/\s+/g, '.')
          .replace(/[^a-z0-9._-]/g, '')
          .replace(/\.{2,}/g, '.')
          .replace(/^[._-]+|[._-]+$/g, '');
        if (!managedLocalPart || !/^[a-z0-9][a-z0-9._-]{0,63}$/.test(managedLocalPart)) {
          setStatusMessage('Managed email local-part must start with a letter or number and use only letters, numbers, dot, underscore, or hyphen.');
          return;
        }
        normalizedEmail = `${managedLocalPart}@${MANAGED_EMAIL_DOMAIN}`;
      }

      const inviteTargetEmail =
        normalizedInviteEmail
        || (!editingUserId && newUserCreateManagedMailbox ? newUserEmail.trim().toLowerCase() : normalizedEmail);
        if (!editingUserId && !/^\S+@\S+\.\S+$/.test(inviteTargetEmail)) {
        setStatusMessage('Invite recipient email must be a valid email address.');
        return;
      }

      if (!editingUserId && newUserPasswordMode === 'set') {
        if (!nextPassword || !nextPasswordConfirm) {
          setStatusMessage('Enter and confirm a temporary password.');
          return;
        }
        if (nextPassword !== nextPasswordConfirm) {
          setStatusMessage('Passwords do not match.');
          return;
        }
        if (nextPassword.length < 12) {
          setStatusMessage('Temporary password must be at least 12 characters.');
          return;
        }
      }

      if (editingUserId && (nextPassword || nextPasswordConfirm)) {
        if (nextPassword !== nextPasswordConfirm) {
          setStatusMessage('Passwords do not match.');
          return;
        }
        if (nextPassword.length < 8) {
          setStatusMessage('Password must be at least 8 characters.');
          return;
        }
      }

      if (editingUserId) {
      const existingUser = users.find((entry) => entry.user_id === editingUserId) || null;
      const desiredGroupIds = Array.from(
        new Set(
          newUserGroupNames
            .map((groupName) => groups.find((entry) => entry.group_name === groupName)?.group_id)
            .filter((groupId): groupId is number => Number(groupId || 0) > 0),
        ),
      );
      const existingGroupIds = Array.from(
        new Set(
          accountMembershipsForDisplay(existingUser?.memberships || [])
            .map((account) => {
              const match = (existingUser?.memberships || []).find(
                (membership) => String(membership.company_name || '').trim().toLowerCase() === account.name.toLowerCase(),
              );
              return Number(match?.company_id || 0);
            })
            .filter((groupId) => groupId > 0),
        ),
      );
      const desiredAliases = Array.from(
        new Set(
          editUserEmailAliasesCsv
            .split(',')
            .map((alias) => alias.trim().toLowerCase())
            .filter(Boolean)
            .filter((alias) => alias !== normalizedEmail),
        ),
      );
      for (const alias of desiredAliases) {
        if (!/^\S+@\S+\.\S+$/.test(alias)) {
          setStatusMessage(`Login alias is invalid: ${alias}`);
          return;
        }
      }

      try {
        if (accessToken) {
          for (const groupId of desiredGroupIds) {
            await callAdminApi(`${ADMIN_USERS_URL}/${editingUserId}/companies/${groupId}/roles`, {
              method: 'PUT',
              body: JSON.stringify({ roles: rolesForGroupMembership(groupId) }),
            });
          }

          for (const groupId of existingGroupIds.filter((id) => !desiredGroupIds.includes(id))) {
            await callAdminApi(`${ADMIN_USERS_URL}/${editingUserId}/companies/${groupId}/roles`, {
              method: 'PUT',
              body: JSON.stringify({ roles: [] }),
            });
          }

          await callAdminApi(`${ADMIN_USERS_URL}/${editingUserId}`, {
            method: 'PUT',
            body: JSON.stringify({
              email: normalizedEmail,
              username: normalizedUserName,
            }),
          });

          const existingAliases = (existingUser?.email_aliases || [])
            .map((alias) => alias.trim().toLowerCase())
            .filter(Boolean);
          const aliasesToAdd = desiredAliases.filter((alias) => !existingAliases.includes(alias));
          const aliasesToRemove = existingAliases.filter((alias) => !desiredAliases.includes(alias));

          for (const alias of aliasesToAdd) {
            await callAdminApi(`${ADMIN_USERS_URL}/${editingUserId}/email-aliases`, {
              method: 'POST',
              body: JSON.stringify({ email: alias }),
            });
          }

          for (const alias of aliasesToRemove) {
            await callAdminApi(`${ADMIN_USERS_URL}/${editingUserId}/email-aliases/${encodeURIComponent(alias)}`, {
              method: 'DELETE',
            });
          }
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to update user.';
        setStatusMessage(message);
        return;
      }

      if (!accessToken) {
        setUsers((current) => current.map((entry) => {
          if (entry.user_id !== editingUserId) {
            return entry;
          }
          const memberships = desiredGroupIds.map((groupId) => {
            const group = groups.find((candidate) => candidate.group_id === groupId);
            return {
              company_id: groupId,
              company_name: group?.group_name || `Group ${groupId}`,
              roles: rolesForGroupMembership(groupId),
            };
          });
          return {
            ...entry,
            email: normalizedEmail,
            email_aliases: desiredAliases,
            username: normalizedUserName,
            display_name: normalizedUserName,
            memberships,
          };
        }));

        setUserGroupBindings((current) => {
          const withoutUser = current.filter((entry) => entry.userId !== editingUserId);
          if (newUserGroupNames.length === 0) {
            return withoutUser;
          }
          return [
            ...withoutUser,
            {
              userId: editingUserId,
              groupNames: [...newUserGroupNames].sort((left, right) => left.localeCompare(right)),
            },
          ];
        });
      }

      if (nextPassword) {
        try {
          if (accessToken) {
            await callAdminApi(`${ADMIN_USERS_URL}/${editingUserId}/password`, {
              method: 'POST',
              body: JSON.stringify({ password: nextPassword }),
            });
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unable to update password.';
          setStatusMessage(message);
          return;
        }
      }

      if (accessToken) {
        await loadAdminData();
      }

      setSelectedUserIds([editingUserId]);
      setEditingUserId(null);
      setIsCreateUserOpen(false);
      setNewUserEmail('');
      setNewUserName('');
      setNewUserPasswordMode('generate');
      setNewUserPassword('');
      setNewUserPasswordConfirm('');
      setNewUserGroupNames([]);
      setNewUserRoles([]);
      setNewUserPermissionIds([]);
      setNewUserPermissionSearch('');
      setNewUserPermissionTypeFilter('all');
      setNewUserPermissionGroupFilter('all');
      setNewUserPermissionSelectedOnly(false);
      setNewUserPermissionUnselectedOnly(false);
      setNewUserPermissionSortKey('policyName');
      setNewUserPermissionSortDirection('asc');
      setNewUserPermissionPage(1);
      setNewUserPermissionPageSize(25);
      setIsCreateUserRolesOpen(false);
      setIsAssignUserGroupsOpen(false);
      setIsCreateUserPasswordOpen(false);
      setIsCreateUserPermissionsOpen(false);
      setEditUserEmailAliasesCsv('');
      setStatusMessage(nextPassword ? 'User updated. Password changed.' : 'User updated.');
      return;
      }

      let createdUserId: string | null = null;

      if (accessToken) {
        const primaryGroupId = newUserGroupNames.length > 0
          ? groups.find((g) => g.group_name === newUserGroupNames[0])?.group_id ?? null
          : null;
        // For new user creation, always ensure we send invite_email (defaults to personal email for managed mailbox, else normalizedEmail)
        const finalInviteEmail = newUserPasswordMode === 'generate' 
          ? (newUserInviteEmail.trim() || (!editingUserId && newUserCreateManagedMailbox ? newUserEmail : normalizedEmail))
          : undefined;
        const payload = await callAdminApi(ADMIN_USERS_URL, {
          method: 'POST',
          body: JSON.stringify({
            email: normalizedEmail,
            username: normalizedUserName,
            send_invite: newUserPasswordMode === 'generate',
            roles: newUserRoles,
            create_managed_mailbox: !editingUserId && newUserCreateManagedMailbox,
            managed_mailbox_local_part: !editingUserId && newUserCreateManagedMailbox ? managedMailboxLocalPart : undefined,
            invite_email: finalInviteEmail,
            feature_overrides: [],
            ...(primaryGroupId !== null ? { company_id: primaryGroupId } : {}),
            ...(newUserPasswordMode === 'set' ? { temporary_password: nextPassword } : {}),
          }),
        });
        createdUserId = String(payload.user?.user_id || payload.user_id || '').trim() || null;
        await loadAdminData();

        const inviteDelivery = String(payload.invite?.delivery || '').trim().toLowerCase();
        const inviteUrl = String(payload.invite?.invite_url || '').trim();
        if (newUserPasswordMode === 'generate') {
          if (inviteDelivery === 'sent') {
            setCreateUserInviteUrl(null);
            setStatusMessage('User created and invite email sent. If not in the inbox within a few minutes, ask the recipient to check their spam or junk folder.');
          } else if (inviteDelivery) {
            const inviteError = sanitizeInviteDeliveryError(String(payload.invite?.error || ''));
            const inviteCopied = inviteUrl ? await copyInviteLinkToClipboard(inviteUrl) : false;
            setCreateUserInviteUrl(inviteCopied ? null : (inviteUrl || null));
            setStatusMessage(
              inviteError
                ? `User created but invite email ${inviteDelivery}: ${inviteError}${inviteCopied ? ' Invite link copied to clipboard.' : ''}`
                : `User created but invite email ${inviteDelivery}.${inviteCopied ? ' Invite link copied to clipboard.' : ''}`,
            );
          } else {
            const inviteCopied = inviteUrl ? await copyInviteLinkToClipboard(inviteUrl) : false;
            setCreateUserInviteUrl(inviteCopied ? null : (inviteUrl || null));
            setStatusMessage(`User created. Invite email status unavailable.${inviteCopied ? ' Invite link copied to clipboard.' : ''}`);
          }
        } else {
          setCreateUserInviteUrl(null);
          setStatusMessage('User created.');
        }
      } else {
        const fallbackUser: AdminUser = {
          user_id: crypto.randomUUID(),
          email: normalizedEmail,
          username: normalizedUserName,
          display_name: normalizedUserName,
          is_active: true,
          memberships: [],
        };
        createdUserId = fallbackUser.user_id;
        setUsers((current) => [fallbackUser, ...current]);
        setStatusMessage('User created in local browser mode. This profile is stored only on this device until admin APIs are available.');
      }

      if (createdUserId && newUserGroupNames.length > 0) {
        const selectedGroups = [...newUserGroupNames].sort((a, b) => a.localeCompare(b));
        const selectedGroupIds = Array.from(
          new Set(
            selectedGroups
              .map((groupName) => groups.find((entry) => entry.group_name === groupName)?.group_id)
              .filter((groupId): groupId is number => Number(groupId || 0) > 0),
          ),
        );

        if (accessToken && selectedGroupIds.length > 0) {
          for (const groupId of selectedGroupIds) {
            await callAdminApi(`${ADMIN_USERS_URL}/${createdUserId}/companies/${groupId}/roles`, {
              method: 'PUT',
              body: JSON.stringify({ roles: rolesForGroupMembership(groupId) }),
            });
          }
          await loadAdminData();
        } else {
          setUserGroupBindings((current) => {
            const withoutUser = current.filter((entry) => entry.userId !== createdUserId);
            return [...withoutUser, { userId: createdUserId as string, groupNames: selectedGroups }];
          });
        }
      }

      setIsCreateUserOpen(false);
      if (createdUserId) {
        setSelectedUserIds([createdUserId]);
      }
      setNewUserEmail('');
      setNewUserName('');
      setNewUserCreateManagedMailbox(false);
      setNewUserManagedMailboxLocalPart('');
      setNewUserInviteEmail('');
      setNewUserPasswordMode('generate');
      setNewUserPassword('');
      setNewUserPasswordConfirm('');
      setNewUserGroupNames([]);
      setNewUserRoles([]);
      setNewUserPermissionIds([]);
      setNewUserPermissionSearch('');
      setNewUserPermissionTypeFilter('all');
      setNewUserPermissionGroupFilter('all');
      setNewUserPermissionSelectedOnly(false);
      setNewUserPermissionUnselectedOnly(false);
      setNewUserPermissionSortKey('policyName');
      setNewUserPermissionSortDirection('asc');
      setNewUserPermissionPage(1);
      setNewUserPermissionPageSize(25);
      setIsCreateUserRolesOpen(false);
      setIsAssignUserGroupsOpen(false);
      setIsCreateUserPasswordOpen(false);
      setIsCreateUserPermissionsOpen(false);
    } catch (error) {
      if (accessToken) {
        const message = error instanceof Error ? error.message : 'Failed to create user via admin API.';
        setStatusMessage(`User was not created: ${message}`);
        setIsSavingUser(false);
        return;
      }

      const fallbackUser: AdminUser = {
        user_id: crypto.randomUUID(),
        email: normalizedEmail,
        username: normalizedUserName,
        display_name: normalizedUserName,
        is_active: true,
        memberships: [],
      };
      if (newUserGroupNames.length > 0) {
        setUserGroupBindings((current) => {
          const withoutUser = current.filter((entry) => entry.userId !== fallbackUser.user_id);
          setIsSavingUser(false);
          return [
            ...withoutUser,
            {
              userId: fallbackUser.user_id,
              groupNames: [...newUserGroupNames].sort((a, b) => a.localeCompare(b)),
            },
          ];
        });
      }
      setUsers((current) => [fallbackUser, ...current]);
      setIsCreateUserOpen(false);
      setSelectedUserIds([fallbackUser.user_id]);
      setNewUserEmail('');
      setNewUserName('');
      setNewUserCreateManagedMailbox(false);
      setNewUserManagedMailboxLocalPart('');
      setNewUserInviteEmail('');
      setNewUserPasswordMode('generate');
      setNewUserPassword('');
      setNewUserPasswordConfirm('');
      setNewUserGroupNames([]);
      setNewUserRoles([]);
      setNewUserPermissionIds([]);
      setNewUserPermissionSearch('');
      setNewUserPermissionTypeFilter('all');
      setNewUserPermissionGroupFilter('all');
      setNewUserPermissionSelectedOnly(false);
      setNewUserPermissionUnselectedOnly(false);
      setNewUserPermissionSortKey('policyName');
      setNewUserPermissionSortDirection('asc');
      setNewUserPermissionPage(1);
      setNewUserPermissionPageSize(25);
      setIsCreateUserRolesOpen(false);
      setIsAssignUserGroupsOpen(false);
      setIsCreateUserPasswordOpen(false);
      setIsCreateUserPermissionsOpen(false);
      setStatusMessage('User created in local browser mode. This profile is stored only on this device until admin APIs are available.');
    } finally {
      isSavingUserRef.current = false;
      setIsSavingUser(false);
    }
  };

  const resendPasswordEmail = async () => {
    if (!editingUserId) {
      setIsSavingUser(false);
      return;
    }
    if (!accessToken) {
      setResendEmailMessage('Resend password email requires an authenticated admin session.');
      setIsSavingUser(false);
      return;
    }

    const targetUser = users.find((entry) => entry.user_id === editingUserId);
    const selectedCompanyId = newUserGroupNames
      .map((groupName) => {
        const normalized = normalizeGroupName(groupName);
        setIsSavingUser(false);
        return groups.find((entry) => normalizeGroupName(entry.group_name) === normalized)?.group_id;
      })
      .find((groupId) => Number(groupId || 0) > 0);
    const companyId = Number(selectedCompanyId || targetUser?.memberships?.[0]?.company_id || 0);
    if (!companyId) {
      setResendEmailMessage('Unable to resend password email because this user has no company access scope yet.');
      setIsSavingUser(false);
      return;
    }

    setIsResendingEmail(true);
    setResendEmailMessage(null);
    setResendEmailInviteUrl(null);
    setResendEmailAttempts([]);
    try {
      const payload = await callAdminApi(
        `${ADMIN_USERS_URL}/${editingUserId}/companies/${companyId}/invite/resend`,
        { method: 'POST' },
      );
      const delivery = String(payload?.invite?.delivery || '').trim().toLowerCase();
      const inviteUrl = String(payload?.invite?.invite_url || '').trim();
      const provider = String(payload?.invite?.provider || '').trim();
      const inviteError = sanitizeInviteDeliveryError(String(payload?.invite?.error || '').trim());
      const remediationSteps = Array.isArray(payload?.invite?.remediation)
        ? payload.invite.remediation
          .map((value: unknown) => sanitizeInviteDeliveryError(String(value || '').trim()))
          .filter((value: string) => Boolean(value))
        : [];
      const attempts = Array.isArray(payload?.invite?.delivery_attempts)
        ? (payload.invite.delivery_attempts as DeliveryAttempt[])
        : [];
      setResendEmailAttempts(attempts);
      const friendlyProvider = provider ? `Provider: ${provider}.` : '';
      const knownCredentialGap = inviteError.toLowerCase().includes('credentials are not available for native mode');

      if (delivery === 'sent') {
        setResendEmailMessage('Password email resent successfully. If not in the inbox within a few minutes, ask the recipient to check their spam or junk folder and mark it as Not Spam.');
        setResendEmailInviteUrl(null);
      } else if (delivery) {
        if (inviteUrl) {
          setResendEmailInviteUrl(inviteUrl);
        }
        if (delivery === 'not_configured' || knownCredentialGap) {
          const remediationNote = remediationSteps.length > 0 ? `Suggested fix: ${remediationSteps[0]}` : '';
          const details = dedupeTokens([remediationNote, friendlyProvider], ' ');
          setResendEmailMessage(
            `Automatic password email delivery is unavailable in this environment. ${details || 'Use the manual invite link below.'}`.trim(),
          );
        } else {
          const remediationNote = remediationSteps.length > 0 ? `Suggested fix: ${remediationSteps[0]}` : '';
          const details = dedupeTokens([inviteError, remediationNote, friendlyProvider]);
          setResendEmailMessage(
            details
              ? `Password email resend ${delivery}: ${details}`
              : `Password email resend ${delivery}.`,
          );
        }
      } else {
        setResendEmailMessage('Password email resend requested. Delivery status unavailable.');
        if (inviteUrl) {
          setResendEmailInviteUrl(inviteUrl);
        }
      }
    } catch (error) {
      const message = error instanceof Error ? sanitizeInviteDeliveryError(error.message) : 'Unable to resend password email.';
      setResendEmailMessage(message);
    } finally {
      setIsResendingEmail(false);
    }
  };

  const deleteSelectedUsers = async () => {
    const ids = [...selectedUserIds];
    if (ids.length === 0) {
      setIsSavingUser(false);
      return;
    }
    setIsDeletingUsers(true);
    const deletedIds: string[] = [];
    const failedDeletes: string[] = [];
    let keycloakDeletedCount = 0;
    let keycloakNotFoundCount = 0;
    let keycloakNotAttemptedCount = 0;
    let keycloakSyncErrorCount = 0;
    for (const userId of ids) {
      try {
        if (accessToken) {
          const payload = await callAdminApi(`${ADMIN_USERS_URL}/${userId}`, { method: 'DELETE' });
          deletedIds.push(userId);
          const keycloakStatus = String(payload?.keycloak_sync_status || '').trim().toLowerCase();
          if (keycloakStatus === 'deleted') keycloakDeletedCount += 1;
          else if (keycloakStatus === 'not_found') keycloakNotFoundCount += 1;
          else if (keycloakStatus === 'not_attempted') keycloakNotAttemptedCount += 1;
          else if (keycloakStatus === 'sync_error') keycloakSyncErrorCount += 1;
          else if (payload?.keycloak_deleted_user_id) keycloakDeletedCount += 1;
        } else {
          deletedIds.push(userId);
        }
      } catch (error) {
        const detail = error instanceof Error ? error.message : 'Delete failed.';
        failedDeletes.push(`${userId}: ${detail}`);
      }
    }
    if (accessToken) {
      await loadAdminData();
    }
    if (!accessToken && deletedIds.length > 0) {
      setUsers((current) => current.filter((entry) => !deletedIds.includes(entry.user_id)));
    }
    setSelectedUserIds((current) => current.filter((id) => !deletedIds.includes(id)));

    const parts: string[] = [];
    if (deletedIds.length > 0) {
      parts.push(`Removed ${deletedIds.length} user${deletedIds.length === 1 ? '' : 's'}.`);
      if (keycloakDeletedCount > 0) {
        parts.push(`Keycloak deleted ${keycloakDeletedCount}.`);
      }
      if (keycloakNotFoundCount > 0) {
        parts.push(`Keycloak already absent for ${keycloakNotFoundCount}.`);
      }
      if (keycloakNotAttemptedCount > 0) {
        parts.push(`Keycloak sync not attempted for ${keycloakNotAttemptedCount}.`);
      }
      if (keycloakSyncErrorCount > 0) {
        parts.push(`Keycloak sync failed for ${keycloakSyncErrorCount} but user deletion completed.`);
      }
    }
    if (failedDeletes.length > 0) {
      parts.push(`Failed ${failedDeletes.length}: ${failedDeletes.slice(0, 2).join(' | ')}`);
    }
    setStatusMessage(parts.length > 0 ? parts.join(' ') : 'No users were removed.');
    setIsDeletingUsers(false);
  };

  const createGroup = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedName = newGroupName.trim();
    const normalizedParentGroupId = Number(newGroupParentId || 0) > 0 ? Number(newGroupParentId) : null;
    const topLevelScopeGroup = normalizedParentGroupId ? resolveTopLevelGroup(normalizedParentGroupId) : null;
    const finalGroupName = topLevelScopeGroup
      ? buildScopedName(topLevelScopeGroup.group_name, normalizedName)
      : normalizedName;
    const allowedPermissionIds = topLevelScopeGroup
      ? getTopLevelPermissionSet(topLevelScopeGroup.group_name)
      : null;
    const allowedRoleIds = topLevelScopeGroup
      ? new Set(
        roles
          .filter((entry) => entry.name.toLowerCase().startsWith(`${topLevelScopeGroup.group_name.toLowerCase()}:`))
          .map((entry) => entry.id),
      )
      : null;
    const scopedPermissionIds = allowedPermissionIds
      ? newGroupPermissionIds.filter((permissionId) => allowedPermissionIds.has(permissionId))
      : [...newGroupPermissionIds];
    const scopedRoleIds = allowedRoleIds
      ? newGroupRoleIds.filter((roleId) => allowedRoleIds.has(roleId))
      : [...newGroupRoleIds];
    if (!normalizedName) {
      setIsSavingUser(false);
      return;
    }

    const duplicateExists = groups.some(
      (entry) => entry.group_id !== editingGroupId
        && normalizeGroupName(entry.group_name) === normalizeGroupName(finalGroupName),
    );
    if (duplicateExists) {
      setStatusMessage(`${finalGroupName} already exists. Edit that account and add the person as Admin.`);
      return;
    }

    const planFeatures = newGroupPlan === 'custom'
      ? (groups.find((entry) => entry.group_id === editingGroupId)?.features || [])
      : (PLAN_FEATURES[newGroupPlan] || []);

    const assignAccountMembers = async (accountId: number) => {
      if (!accessToken || !accountId) {
        return;
      }
      const previousIds = users
        .filter((entry) => entry.memberships.some((membership) => Number(membership.company_id) === accountId))
        .map((entry) => entry.user_id);
      for (const userId of previousIds.filter((userId) => !newGroupUserIds.includes(userId))) {
        await callAdminApi(`${ADMIN_USERS_URL}/${userId}/companies/${accountId}/roles`, {
          method: 'PUT',
          body: JSON.stringify({ roles: [] }),
        });
      }
      for (const userId of newGroupUserIds) {
        const picked = groupUserJobById[userId];
        const job = picked === 'contributor' || picked === 'viewer' ? picked : 'company_admin';
        const membership = users
          .find((entry) => entry.user_id === userId)
          ?.memberships.find((row) => Number(row.company_id) === accountId);
        const kept = (membership?.roles || []).filter((role) => !ACCOUNT_TEMPLATE_ROLE_NAMES.has(role.trim().toLowerCase()));
        await callAdminApi(`${ADMIN_USERS_URL}/${userId}/companies/${accountId}/roles`, {
          method: 'PUT',
          body: JSON.stringify({ roles: [job, ...kept] }),
        });
      }
    };

    if (editingGroupId !== null) {
      if (normalizedParentGroupId === editingGroupId) {
        setStatusMessage('A group cannot be its own parent.');
        setIsSavingUser(false);
        return;
      }
      const originalGroup = groups.find((entry) => entry.group_id === editingGroupId);
      const originalName = originalGroup?.group_name || finalGroupName;

      if (accessToken) {
        await callAdminApi(`${ADMIN_GROUPS_URL}/${editingGroupId}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: finalGroupName,
            parent_group_id: normalizedParentGroupId,
            group_type: normalizedParentGroupId ? 'team' : 'organization',
            features: planFeatures,
          }),
        });
        try {
          await assignAccountMembers(editingGroupId);
        } catch {
          setStatusMessage('The account was saved. One person could not be added. Edit the account and set their job again.');
        }
        await loadAdminData();
      }

      setGroups((current) => current.map((entry) => (
        entry.group_id === editingGroupId
          ? {
            ...entry,
            group_name: finalGroupName,
            parent_group_id: normalizedParentGroupId,
            group_type: normalizedParentGroupId ? 'team' : 'organization',
          }
          : entry
      )));

      setUserGroupBindings((current) => {
        const selectedUserIdSet = new Set(newGroupUserIds);
        setIsSavingUser(false);
        return current
          .map((entry) => {
            const baseNames = entry.groupNames.filter((groupName) => groupName !== originalName && groupName !== finalGroupName);
            if (selectedUserIdSet.has(entry.userId)) {
              setIsSavingUser(false);
              return {
                ...entry,
                groupNames: [...baseNames, finalGroupName].sort((left, right) => left.localeCompare(right)),
              };
            }
            setIsSavingUser(false);
            return {
              ...entry,
              groupNames: baseNames,
            };
          })
          .filter((entry) => entry.groupNames.length > 0)
          .concat(
            Array.from(selectedUserIdSet)
              .filter((userId) => !current.some((entry) => entry.userId === userId))
              .map((userId) => ({ userId, groupNames: [finalGroupName] })),
          );
      });

      setGroupPermissionBindings((current) => {
        const withoutGroup = current.filter((entry) => entry.groupName !== originalName && entry.groupName !== finalGroupName);
        if (scopedPermissionIds.length === 0) {
          setIsSavingUser(false);
          return withoutGroup;
        }
        setIsSavingUser(false);
        return [
          ...withoutGroup,
          {
            groupName: finalGroupName,
            permissionIds: [...scopedPermissionIds].sort((left, right) => left.localeCompare(right)),
          },
        ];
      });

      setGroupRoleBindings((current) => {
        const withoutGroup = current.filter((entry) => entry.groupName !== originalName && entry.groupName !== finalGroupName);
        if (scopedRoleIds.length === 0) {
          setIsSavingUser(false);
          return withoutGroup;
        }
        setIsSavingUser(false);
        return [
          ...withoutGroup,
          {
            groupName: finalGroupName,
            roleIds: [...scopedRoleIds].sort((left, right) => left.localeCompare(right)),
          },
        ];
      });

      setSelectedGroupIds([editingGroupId]);
      setEditingGroupId(null);
      setStatusMessage('Account updated.');
      setIsCreateGroupOpen(false);
      setNewGroupName('');
      setNewGroupParentId('');
      setNewGroupUserIds([]);
      setNewGroupRoleIds([]);
      setNewGroupPermissionIds([]);
      setNewGroupUserSearch('');
      setNewGroupPermissionSearch('');
      setNewGroupPermissionTypeFilter('all');
      setNewGroupPermissionGroupFilter('all');
      setNewGroupPermissionSelectedOnly(false);
      setNewGroupPermissionUnselectedOnly(false);
      setNewGroupPermissionSortKey('policyName');
      setNewGroupPermissionSortDirection('asc');
      setNewGroupPermissionPage(1);
      setNewGroupPermissionPageSize(25);
      setIsCreateGroupNameOpen(false);
      setIsCreateGroupUsersOpen(false);
      setIsCreateGroupRolesOpen(false);
      setIsCreateGroupPermissionsOpen(false);
      setIsSavingUser(false);
      return;
    }

    let groupId = Date.now();
    let savedRemotely = false;
    try {
      if (accessToken) {
        const payload = await callAdminApi(ADMIN_GROUPS_URL, {
          method: 'POST',
          body: JSON.stringify({
            name: finalGroupName,
            parent_group_id: normalizedParentGroupId,
            group_type: normalizedParentGroupId ? 'team' : 'organization',
            features: planFeatures,
          }),
        });
        groupId = Number(payload.group_id || payload.company_id || groupId);
        savedRemotely = true;
        await loadAdminData();
      } else {
        setGroups((current) => [
          {
            group_id: groupId,
            group_name: finalGroupName,
            parent_group_id: normalizedParentGroupId,
            group_type: normalizedParentGroupId ? 'team' : 'organization',
            features: [],
          },
          ...current,
        ]);
      }
    } catch {
      setGroups((current) => [
        {
          group_id: groupId,
          group_name: finalGroupName,
          parent_group_id: normalizedParentGroupId,
          group_type: normalizedParentGroupId ? 'team' : 'organization',
          features: [],
        },
        ...current,
      ]);
    }

    if (savedRemotely) {
      try {
        await assignAccountMembers(groupId);
      } catch {
        setStatusMessage('The account was saved. One person could not be added. Edit the account and set their job again.');
      }
    }

    if (newGroupUserIds.length > 0) {
      setUserGroupBindings((current) => {
        const map = new Map<string, string[]>();
        current.forEach((entry) => {
          map.set(entry.userId, [...entry.groupNames]);
        });
        newGroupUserIds.forEach((userId) => {
          const existing = map.get(userId) || [];
          if (!existing.includes(finalGroupName)) {
            map.set(userId, [...existing, finalGroupName].sort((a, b) => a.localeCompare(b)));
          }
        });
        setIsSavingUser(false);
        return Array.from(map.entries()).map(([userId, groupNames]) => ({ userId, groupNames }));
      });
    }

    setGroupPermissionBindings((current) => {
      const withoutGroup = current.filter((entry) => entry.groupName !== finalGroupName);
      if (scopedPermissionIds.length === 0) {
        setIsSavingUser(false);
        return withoutGroup;
      }
      setIsSavingUser(false);
      return [
        ...withoutGroup,
        {
          groupName: finalGroupName,
          permissionIds: [...scopedPermissionIds].sort((a, b) => a.localeCompare(b)),
        },
      ];
    });

    setGroupRoleBindings((current) => {
      const withoutGroup = current.filter((entry) => entry.groupName !== finalGroupName);
      if (scopedRoleIds.length === 0) {
        setIsSavingUser(false);
        return withoutGroup;
      }
      setIsSavingUser(false);
      return [
        ...withoutGroup,
        {
          groupName: finalGroupName,
          roleIds: [...scopedRoleIds].sort((a, b) => a.localeCompare(b)),
        },
      ];
    });

    setStatusMessage(
      (allowedPermissionIds && scopedPermissionIds.length !== newGroupPermissionIds.length)
      || (allowedRoleIds && scopedRoleIds.length !== newGroupRoleIds.length)
        ? 'Account created. Disallowed subgroup role/permission assignments were removed to match the top-level scope.'
        : 'Account created. Invite the Admin if they are not signed in yet, then open their first compliance job.',
    );
    setIsCreateGroupOpen(false);
    setNewGroupName('');
    setNewGroupParentId('');
    setNewGroupUserIds([]);
    setNewGroupRoleIds([]);
    setNewGroupPermissionIds([]);
    setNewGroupUserSearch('');
    setNewGroupPermissionSearch('');
    setNewGroupPermissionTypeFilter('all');
    setNewGroupPermissionGroupFilter('all');
    setNewGroupPermissionSelectedOnly(false);
    setNewGroupPermissionUnselectedOnly(false);
    setNewGroupPermissionSortKey('policyName');
    setNewGroupPermissionSortDirection('asc');
    setNewGroupPermissionPage(1);
    setNewGroupPermissionPageSize(25);
    setIsCreateGroupNameOpen(false);
    setIsCreateGroupUsersOpen(false);
    setIsCreateGroupRolesOpen(false);
    setIsCreateGroupPermissionsOpen(false);
  };

  const deleteSelectedGroups = async () => {
    const ids = [...selectedGroupIds];
    if (ids.length === 0) {
      setIsSavingUser(false);
      return;
    }

    const deletedIds: number[] = [];
    const failedDeletes: string[] = [];

    for (const groupId of ids) {
      const name = groups.find((group) => group.group_id === groupId)?.group_name || String(groupId);
      try {
        if (accessToken) {
          await callAdminApi(`${ADMIN_GROUPS_URL}/${groupId}`, { method: 'DELETE' });
        }
        deletedIds.push(groupId);
      } catch (error) {
        const detail = error instanceof Error ? error.message : 'Delete failed.';
        failedDeletes.push(`${name}: ${detail}`);
      }
    }

    if (accessToken) {
      await loadAdminData();
    }

    if (failedDeletes.length) {
      setStatusMessage(failedDeletes.join(' '));
    }

    const namesToDelete = groups
      .filter((group) => deletedIds.includes(group.group_id))
      .map((group) => group.group_name);

    setGroups((current) => current.filter((entry) => !deletedIds.includes(entry.group_id)));
    setSelectedGroupIds([]);

    setUserGroupBindings((current) =>
      current
        .map((entry) => ({
          ...entry,
          groupNames: entry.groupNames.filter((groupName) => !namesToDelete.includes(groupName)),
        }))
        .filter((entry) => entry.groupNames.length > 0),
    );

    setGroupPermissionBindings((current) =>
      current.filter((entry) => !namesToDelete.includes(entry.groupName)),
    );

    setGroupRoleBindings((current) =>
      current.filter((entry) => !namesToDelete.includes(entry.groupName)),
    );

    setStatusMessage('Selected accounts removed.');
  };

  React.useEffect(() => {
    if (!createUserInviteUrl) {
      setCreateUserInviteCopied(false);
    }
  }, [createUserInviteUrl]);

  const copyInviteLinkToClipboard = async (inviteLink: string): Promise<boolean> => {
    try {
      if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
        return false;
      }
      await navigator.clipboard.writeText(inviteLink);
      return true;
    } catch {
      return false;
    }
  };

  const copyCreateUserInviteLink = async () => {
    if (!createUserInviteUrl) {
      return;
    }

    try {
      const copied = await copyInviteLinkToClipboard(createUserInviteUrl);
      if (!copied) {
        throw new Error('Clipboard API unavailable');
      }
      setCreateUserInviteCopied(true);
      setStatusMessage('Invite link copied. Share it directly with the new user.');
    } catch {
      setStatusMessage('Unable to copy the invite link automatically. Open it in a new tab and copy it from the browser address bar.');
    }
  };

  const createRole = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const requestedRoleName = newRoleName.trim();
    const scopedGroupId = Number(newRoleScopeGroupId || 0);
    const scopedTopLevelGroup = scopedGroupId > 0 ? resolveTopLevelGroup(scopedGroupId) : null;
    const roleName = scopedTopLevelGroup
      ? buildScopedName(scopedTopLevelGroup.group_name, requestedRoleName)
      : requestedRoleName;
    const rolePermissionScope = scopedTopLevelGroup
      ? getTopLevelPermissionSet(scopedTopLevelGroup.group_name)
      : null;
    const scopedRolePermissionIds = rolePermissionScope
      ? newRolePermissionIds.filter((permissionId) => rolePermissionScope.has(permissionId))
      : [...newRolePermissionIds];

    if (!requestedRoleName) {
      setIsSavingUser(false);
      return;
    }

    const purpose = newRoleDescription.trim();
    const normalizedRoleName = requestedRoleName.toLowerCase().replace(/[\s_-]+/g, '');
    const planRoleNames = new Set(['complicore', 'compliadvantage', 'complicomplete', 'basic', 'advanced', 'enterprise', 'keycloak', 'keycloakadmin']);
    if (purpose.length < 20) {
      setStatusMessage('Write the job this role is for. A new role needs a purpose before it can be saved.');
      setIsSavingUser(false);
      return;
    }
    if (planRoleNames.has(normalizedRoleName)) {
      setStatusMessage('A subscription plan is not a role. Put the plan on the account, and give the person Admin, Contributor, or Viewer.');
      setIsSavingUser(false);
      return;
    }
    if (requestedRoleName.includes('@')) {
      setStatusMessage('A role is a job on an account, not a person or an email address.');
      setIsSavingUser(false);
      return;
    }

    if (editingRoleId) {
      const duplicate = roles.some((entry) => entry.id !== editingRoleId && entry.name.toLowerCase() === roleName.toLowerCase());
      if (duplicate) {
        setStatusMessage('Role already exists.');
        setIsSavingUser(false);
        return;
      }

      setRoles((current) => current
        .map((entry) => (entry.id === editingRoleId
          ? {
            ...entry,
            name: roleName,
            description: newRoleDescription.trim(),
            permissions: [...scopedRolePermissionIds],
          }
          : entry))
        .sort((left, right) => left.name.localeCompare(right.name)));
      setSelectedRoleIds([editingRoleId]);
      setEditingRoleId(null);
      setStatusMessage(
        rolePermissionScope && scopedRolePermissionIds.length !== newRolePermissionIds.length
          ? 'Role updated. Permissions outside the top-level group scope were removed.'
          : 'Role updated.',
      );
      setIsCreateRoleOpen(false);
      setNewRoleName('');
      setNewRoleScopeGroupId('');
      setNewRoleDescription('');
      setNewRolePermissionIds([]);
      setNewRolePermissionSearch('');
      setNewRolePermissionTypeFilter('all');
      setNewRolePermissionGroupFilter('all');
      setNewRolePermissionSelectedOnly(false);
      setNewRolePermissionUnselectedOnly(false);
      setNewRolePermissionSortKey('policyName');
      setNewRolePermissionSortDirection('asc');
      setNewRolePermissionPage(1);
      setNewRolePermissionPageSize(25);
      setIsCreateRolePermissionsOpen(false);
      setIsSavingUser(false);
      return;
    }

    if (roles.some((entry) => entry.name.toLowerCase() === roleName.toLowerCase())) {
      setStatusMessage('Role already exists.');
      setIsSavingUser(false);
      return;
    }

    const permissions = [...scopedRolePermissionIds];
    const nextRole: IamRole = {
      id: roleSlug(roleName),
      name: roleName,
      description: newRoleDescription.trim(),
      permissions,
    };

    setRoles((current) => [...current, nextRole].sort((a, b) => a.name.localeCompare(b.name)));
    setStatusMessage(
      rolePermissionScope && scopedRolePermissionIds.length !== newRolePermissionIds.length
        ? 'Role created. Permissions outside the top-level group scope were removed.'
        : 'Role created.',
    );
    setIsCreateRoleOpen(false);
    setNewRoleName('');
    setNewRoleScopeGroupId('');
    setNewRoleDescription('');
    setNewRolePermissionIds([]);
    setNewRolePermissionSearch('');
    setNewRolePermissionTypeFilter('all');
    setNewRolePermissionGroupFilter('all');
    setNewRolePermissionSelectedOnly(false);
    setNewRolePermissionUnselectedOnly(false);
    setNewRolePermissionSortKey('policyName');
    setNewRolePermissionSortDirection('asc');
    setNewRolePermissionPage(1);
    setNewRolePermissionPageSize(25);
    setIsCreateRolePermissionsOpen(false);
  };

  const deleteSelectedRoles = () => {
    const ids = [...selectedRoleIds];
    if (ids.length === 0) {
      setIsSavingUser(false);
      return;
    }
    setRoles((current) => current.filter((entry) => !ids.includes(entry.id)));
    setGroupRoleBindings((current) =>
      current
        .map((entry) => ({
          ...entry,
          roleIds: entry.roleIds.filter((roleId) => !ids.includes(roleId)),
        }))
        .filter((entry) => entry.roleIds.length > 0),
    );
    setSelectedRoleIds([]);
    setStatusMessage('Selected roles removed.');
  };

  // Check if user trying to access keycloakAdmin section without permission
  if (currentSection === 'keycloakAdmin' && !isKeycloakAdmin) {
    return (
      <section className="page-section">
        <div className="theme-admin-card">
          <h1>{chrome.identityProvider}</h1>
          <p>{chrome.identityRestricted}</p>
        </div>
      </section>
    );
  }

  if (!isPlatformAdmin(user)) {
    return (
      <section className="page-section">
        <div className="theme-admin-card">
          <h1>{chrome.accessManagement}</h1>
          <p>{chrome.accessRestricted}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="page-section access-management-page">
      {(isSavingUser || isDeletingUsers) ? (
        <div className="admin-fullscreen-overlay" role="status" aria-label={isDeletingUsers ? chrome.deleting : chrome.saving}>
          <div className="admin-fullscreen-overlay-content">
            <span className="admin-fullscreen-spinner" aria-hidden="true" />
            <span className="admin-fullscreen-overlay-label">{isDeletingUsers ? chrome.deleting : chrome.saving}</span>
          </div>
        </div>
      ) : null}
      <nav className="admin-console-breadcrumbs" aria-label={chrome.breadcrumb}>
        <Link to="/console">{chrome.console}</Link>
        <span>/</span>
        {currentSection === 'costOptimization' ? (
          <span className="admin-console-breadcrumb-current">{chrome.costOptimization}</span>
        ) : (
          <>
            <Link to="/console/admin/groups">{chrome.accessManagement}</Link>
            <span>/</span>
            {currentSection === 'users' && isCreateUserOpen ? (
              <>
                <button type="button" onClick={showUsersList}>
                  {adminSectionLabel(currentSection, chrome)}
                </button>
                <span>/</span>
                <span className="admin-console-breadcrumb-current">
                  {editingUserId ? (newUserEmail.trim() || adminSectionLabel(currentSection, chrome)) : chrome.newUser}
                </span>
              </>
            ) : (
              <span className="admin-console-breadcrumb-current">{adminSectionLabel(currentSection, chrome)}</span>
            )}
          </>
        )}
      </nav>

      {isLoading ? (
        <div className="admin-page-loading-state" role="status" aria-live="polite">
          <div className="admin-page-loading-card">
            <span className="admin-page-loading-spinner" aria-hidden="true" />
            <div className="admin-page-loading-copy">
              <strong>
                {currentSection === 'costOptimization'
                  ? 'Loading Cost Optimization'
                  : currentSection === 'observability'
                    ? 'Loading Observability'
                    : currentSection === 'experience'
                      ? 'Loading User Experience'
                  : currentSection === 'crm'
                    ? 'Loading CRM'
                    : 'Loading User Management'}
              </strong>
              <span>
                {currentSection === 'costOptimization'
                  ? 'Refreshing optimization signals and cost controls.'
                  : currentSection === 'crm'
                    ? 'Loading customer records and workflow context.'
                    : 'Syncing identities, groups, roles, and admin controls.'}
              </span>
            </div>
          </div>
        </div>
      ) : null}
      {statusMessage ? <p className="theme-admin-message access-management-status-flash">{statusMessage}</p> : null}
      {createUserInviteUrl ? (
        <div className="theme-admin-message access-management-status-flash admin-invite-url-banner" role="status" aria-live="polite">
          <div className="admin-invite-url-banner-header">
            <div className="admin-invite-url-banner-copy">
              <strong>Local invite ready</strong>
              <span>Email delivery is not configured in local dev. Share this onboarding link directly with the new user.</span>
            </div>
            <div className="admin-invite-url-banner-actions">
              <button type="button" className="btn btn-secondary" onClick={copyCreateUserInviteLink}>
                {createUserInviteCopied ? 'Copied' : 'Copy link'}
              </button>
              <a className="btn btn-secondary" href={createUserInviteUrl} target="_blank" rel="noreferrer">Open link</a>
              <button type="button" className="btn btn-secondary" onClick={() => setCreateUserInviteUrl(null)}>
                Dismiss
              </button>
            </div>
          </div>
          <p className="admin-invite-url-banner-url">{createUserInviteUrl}</p>
        </div>
      ) : null}

      {currentSection === 'splash' ? (
        <div className="theme-admin-card access-management-splash-card">
          <div className="access-management-splash-hero">
            <p className="access-management-splash-kicker">Access</p>
            <h2>Who can sign in, and with what</h2>
            <p>
              Start with the people and the sign-in method. Operations sits under that. Sales lives in Pursuit, not on this page.
            </p>
          </div>
          <h3 className="access-management-splash-group">People</h3>
          <div className="access-management-splash-grid">
            <Link to="/console/admin/users" className="access-management-splash-link">
              <strong>Users</strong>
              <span>The named people who can sign in, and the account they belong to.</span>
            </Link>
            <Link to="/console/admin/groups" className="access-management-splash-link">
              <strong>Accounts</strong>
              <span>One subscription, and the people on it.</span>
            </Link>
            <Link to="/console/admin/roles" className="access-management-splash-link">
              <strong>Roles</strong>
              <span>The permission bundle a person gets on that account.</span>
            </Link>
          </div>
          <h3 className="access-management-splash-group">Sign-in</h3>
          <div className="access-management-splash-grid">
            {isKeycloakAdmin ? (
              <Link to="/console/admin/keycloak" className="access-management-splash-link">
                <strong>Identity providers</strong>
                <span>Google, Microsoft, and company sign-in. Those paths do not create a password.</span>
              </Link>
            ) : null}
          </div>
          <h3 className="access-management-splash-group">Operations</h3>
          <div className="access-management-splash-grid">
            <Link to="/console/admin/email-operations" className="access-management-splash-link">
              <strong>Email</strong>
              <span>Whether mail is going out, and which shared addresses have waiting mail.</span>
            </Link>
            <Link to="/console/admin/voice-messages" className="access-management-splash-link">
              <strong>Phone greetings</strong>
              <span>The greeting and the bot prompt on the company line.</span>
            </Link>
            <Link to={ADMIN_CONSOLE_OBSERVABILITY_PATH} className="access-management-splash-link">
              <strong>Observability</strong>
              <span>Logs, metrics, and the alerts that say the platform is down.</span>
            </Link>
            <Link to={ADMIN_CONSOLE_EXPERIENCE_PATH} className="access-management-splash-link">
              <strong>Sign-in trouble</strong>
              <span>Failed logins, abandoned applications, and uploads that did not finish.</span>
            </Link>
          </div>
        </div>
      ) : null}

      {currentSection === 'crm' ? (
        <div className="theme-admin-card">
          <div className="admin-console-toolbar">
            <div>
              <h2>
                CRM <span className="access-management-count">({filteredCrmLeads.length}/{crmLeads.length})</span>
                <span className="access-management-info">Info</span>
              </h2>
              <p>Review demo-request submissions captured from the website CRM flow. The live NextCompli pipeline lives under dashboard <strong>CRM</strong> (Pursuit).</p>
            </div>
            <div className="admin-console-inline-actions">
              <button type="button" className="btn btn-secondary" onClick={() => void loadCrmLeads()}>
                Refresh leads
              </button>
              <button type="button" className="btn btn-secondary" onClick={copyCrmEmails}>
                Copy emails
              </button>
              <button type="button" className="btn btn-primary" onClick={exportCrmLeadsCsv}>
                Export CSV
              </button>
            </div>
          </div>
          {crmLeadsError ? <p className="admin-console-error">{crmLeadsError}</p> : null}
          {isCrmLoading ? <p className="theme-admin-message">Loading CRM leads...</p> : null}
          {!isCrmLoading && crmLeads.length > 0 ? (
            <div className="crm-leads-filters" aria-label="CRM lead filters">
              <input
                type="search"
                value={crmSearchTerm}
                onChange={(event) => setCrmSearchTerm(event.target.value)}
                className="access-management-search"
                placeholder="Search name, company, email, use case, source, or page"
                aria-label="Search CRM leads"
              />
              <label className="crm-leads-filter-control">
                <span>Source</span>
                <select value={crmSourceFilter} onChange={(event) => setCrmSourceFilter(event.target.value)}>
                  {crmSourceOptions.map((option) => (
                    <option key={option} value={option}>
                      {option === 'all' ? 'All sources' : option}
                    </option>
                  ))}
                </select>
              </label>
              <label className="crm-leads-filter-control">
                <span>Recency</span>
                <select
                  value={crmRecencyFilter}
                  onChange={(event) => setCrmRecencyFilter(event.target.value as CrmLeadRecencyFilter)}
                >
                  <option value="all">All time</option>
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                </select>
              </label>
            </div>
          ) : null}
          {!isCrmLoading && crmLeads.length === 0 && !crmLeadsError ? (
            <p className="theme-admin-message">No CRM leads captured yet.</p>
          ) : null}
          {!isCrmLoading && crmLeads.length > 0 && filteredCrmLeads.length === 0 ? (
            <p className="theme-admin-message">No leads match the current filters.</p>
          ) : null}
          {filteredCrmLeads.length > 0 ? (
            <div className="admin-console-table-wrap crm-leads-table-wrap">
              <table className="access-management-permissions-table crm-leads-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Company</th>
                    <th>Work Email</th>
                    <th>Phone</th>
                    <th>Use Case</th>
                    <th>Source</th>
                    <th>Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCrmLeads.map((lead) => (
                    <tr key={lead.lead_id}>
                      <td>
                        <div className="access-management-policy-cell">
                          <strong>{lead.fullName}</strong>
                          <span>{lead.employees || 'Employee count not provided'}</span>
                        </div>
                      </td>
                      <td>{lead.company || 'Not provided'}</td>
                      <td>{lead.workEmail}</td>
                      <td>{lead.phone || 'Not provided'}</td>
                      <td>{lead.useCase || 'Not provided'}</td>
                      <td>
                        <div className="access-management-policy-cell">
                          <strong>{lead.source || 'Website'}</strong>
                          <span>{lead.page_path || 'Unknown page'}</span>
                        </div>
                      </td>
                      <td>{new Date(lead.submitted_at || lead.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}

      {currentSection === 'users' ? (
        <div className="theme-admin-card">
          <div className="admin-console-toolbar">
            <div>
              <h2>
                Users <span className="access-management-count">({users.length})</span>
                <span className="access-management-info">Info</span>
              </h2>
              <p>Manage identities for IAM access.</p>
            </div>
            <div className="admin-console-inline-actions">
              {isCreateUserOpen ? (
                <>
                  {!editingUserId ? (
                    <>
                      <button type="submit" form="create-user-form" className="btn btn-primary" disabled={isSavingUser}>
                        {isSavingUser ? (
                          <span className="btn-loading-content" aria-live="polite">
                            <span className="btn-spinner" aria-hidden="true" />
                            Saving...
                          </span>
                        ) : 'Save user'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          setIsCreateUserOpen(false);
                          setEditingUserId(null);
                          setNewUserEmail('');
                          setNewUserName('');
                          setNewUserCreateManagedMailbox(false);
                          setNewUserManagedMailboxLocalPart('');
                          setNewUserInviteEmail('');
                          setNewUserPasswordMode('generate');
                          setNewUserPassword('');
                          setNewUserPasswordConfirm('');
                          setNewUserGroupNames([]);
                          setNewUserRoles([]);
                          setStatusMessage('');
                        }}
                      >
                        Cancel
                      </button>
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setIsCreateUserOpen((open) => {
                        const nextOpen = !open;
                        if (nextOpen) {
                          setEditingUserId(null);
                          setNewUserCreateManagedMailbox(false);
                          setNewUserManagedMailboxLocalPart('');
                          setNewUserInviteEmail('');
                          setNewUserPasswordMode('generate');
                          setNewUserPassword('');
                          setNewUserPasswordConfirm('');
                          setIsCreateUserPasswordOpen(false);
                          setIsAssignUserGroupsOpen(false);
                        } else {
                          setEditingUserId(null);
                          setNewUserPasswordMode('generate');
                          setNewUserPassword('');
                          setNewUserPasswordConfirm('');
                          setIsCreateUserPasswordOpen(false);
                          setIsAssignUserGroupsOpen(false);
                        }
                        return nextOpen;
                      });
                    }}
                  >
                    Create user
                  </button>
                  <button type="button" className="btn btn-secondary" disabled={selectedUserIds.length !== 1} onClick={startEditSelectedUser}>
                    Edit
                  </button>
                  <button type="button" className="btn btn-secondary" disabled={selectedUserIds.length === 0} onClick={() => void deleteSelectedUsers()}>
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>

          {!isCreateUserOpen ? (
            <div className="access-management-search-row">
              <input
                type="search"
                className="access-management-search"
                placeholder="Search"
                value={userSearch}
                onChange={(event) => setUserSearch(event.target.value)}
                aria-label="Search users"
              />
            </div>
          ) : null}

          {isCreateUserOpen ? (
            <form id="create-user-form" className="admin-console-form" onSubmit={(event) => void createUser(event)}>
              <label className="theme-admin-field">
                <span>Sign-in name</span>
                <input value={newUserName} onChange={(event) => setNewUserName(event.target.value)} required />
                <small>Use the email address so every person is listed the same way.</small>
              </label>
              <label className="theme-admin-field">
                <span>{!editingUserId && newUserCreateManagedMailbox ? 'Personal / recovery email (invite destination)' : 'Email'}</span>
                <input type="email" value={newUserEmail} onChange={(event) => setNewUserEmail(event.target.value)} required />
                {!editingUserId && newUserCreateManagedMailbox ? (
                  <small>The invite and verification email will be sent here. The @{MANAGED_EMAIL_DOMAIN} address below will be the login.</small>
                ) : null}
                {editingUserId ? (
                  <small>
                    The sign-in email is this user. Leave aliases empty unless this same person must also sign in with a second address.
                  </small>
                ) : null}
              </label>
              {editingUserId ? (
                <section className="admin-console-accordion-group">
                  <div className="theme-admin-grid">
                    <label className="theme-admin-field">
                      <span>Login email aliases</span>
                      <input
                        value={editUserEmailAliasesCsv}
                        onChange={(event) => setEditUserEmailAliasesCsv(event.target.value)}
                        placeholder="alias@example.com"
                        autoComplete="off"
                      />
                      <small>
                        {editUserEmailAliasesCsv.trim()
                          ? 'Extra sign-in addresses for this same user. They are not separate people and they do not add roles.'
                          : 'No login aliases. One email, one user. The faded example is placeholder text.'}
                      </small>
                    </label>
                  </div>
                  <div className="theme-admin-grid">
                    <label className="admin-console-checkbox">
                      <input
                        type="checkbox"
                        checked={editUserAssignManagedEmail}
                        onChange={(event) => {
                          setEditUserAssignManagedEmail(event.target.checked);
                          if (event.target.checked && !editUserManagedEmailLocalPart) {
                            const derived = newUserName.trim().toLowerCase().replace(/\s+/g, '.').replace(/[^a-z0-9._-]/g, '').replace(/\.{2,}/g, '.').replace(/^[._-]+|[._-]+$/g, '');
                            setEditUserManagedEmailLocalPart(derived);
                          }
                        }}
                      />
                      <span>Assign @{MANAGED_EMAIL_DOMAIN} login email</span>
                    </label>
                  </div>
                  {editUserAssignManagedEmail ? (
                    <div className="theme-admin-grid">
                      <label className="theme-admin-field">
                        <span>Email local-part</span>
                        <input
                          value={editUserManagedEmailLocalPart}
                          onChange={(event) => setEditUserManagedEmailLocalPart(event.target.value)}
                          placeholder="firstname.lastname"
                          required
                        />
                        <small>Login email will be set to {editUserManagedEmailLocalPart.trim().toLowerCase() || 'local-part'}@{MANAGED_EMAIL_DOMAIN}</small>
                      </label>
                    </div>
                  ) : null}
                </section>
              ) : null}
              {!editingUserId ? (
                <section className="admin-console-accordion-group">
                  <div className="theme-admin-grid">
                    <label className="admin-console-checkbox">
                      <input
                        type="checkbox"
                        checked={newUserCreateManagedMailbox}
                        onChange={(event) => {
                          setNewUserCreateManagedMailbox(event.target.checked);
                          // Auto-populate invite email with personal email when managed mailbox is enabled
                          if (event.target.checked && !newUserInviteEmail && newUserEmail) {
                            setNewUserInviteEmail(newUserEmail);
                          }
                        }}
                      />
                      <span>Provision managed {MANAGED_EMAIL_DOMAIN} login email</span>
                    </label>
                  </div>
                  {newUserCreateManagedMailbox ? (
                    <div className="theme-admin-grid">
                      <label className="theme-admin-field">
                        <span>Login email (read-only)</span>
                        <input
                          value={`${newUserName.trim().toLowerCase().replace(/\s+/g, '.').replace(/[^a-z0-9._-]/g, '').replace(/\.{2,}/g, '.').replace(/^[._-]+|[._-]+$/g, '') || 'username'}@${MANAGED_EMAIL_DOMAIN}`}
                          readOnly
                        />
                        <small>This value is auto-filled from User name, never from the recovery email.</small>
                      </label>
                    </div>
                  ) : null}
                </section>
              ) : null}
              {!editingUserId ? (
                <section className="admin-console-accordion-group">
                  <button
                    type="button"
                    className="admin-console-accordion-toggle"
                    onClick={() => setIsCreateUserRolesOpen((open) => !open)}
                    aria-expanded={isCreateUserRolesOpen}
                  >
                    <span>Assign direct roles - Optional ({newUserRoles.length})</span>
                    <AccordionChevronIcon open={isCreateUserRolesOpen} />
                  </button>
                  {isCreateUserRolesOpen ? (
                    <>
                      <div className="theme-admin-grid">
                        {roles.filter((role) => !['company_admin', 'contributor', 'viewer', 'platform_admin', 'admin'].includes(role.name)).map((role) => (
                          <label key={`new-user-role-${role.id}`} className="admin-console-checkbox">
                            <input
                              type="checkbox"
                              checked={newUserRoles.includes(role.name)}
                              onChange={() => toggleId(role.name, setNewUserRoles)}
                            />
                            <span>{getRoleLabel(role.name)}</span>
                          </label>
                        ))}
                      </div>
                      <p className="access-management-admin-role-note">
                        These are extra duties, such as sales. A person's job in a company is set under Company access.
                      </p>
                    </>
                  ) : null}
                </section>
              ) : null}
              <section className="admin-console-accordion-group">
                <button
                  type="button"
                  className="admin-console-accordion-toggle"
                  onClick={() => setIsAssignUserGroupsOpen((open) => !open)}
                  aria-expanded={isAssignUserGroupsOpen}
                >
                  <span>Company access ({newUserGroupNames.length})</span>
                  <AccordionChevronIcon open={isAssignUserGroupsOpen} />
                </button>
                {isAssignUserGroupsOpen ? (
                  <>
                      <p className="access-management-admin-role-note">
                      Pick one job per subscription. Subscription admin invites people on that account. Contributor does the work. Viewer can read it. None removes access. NextCompli operator is a separate checkbox and is not a subscription job.
                    </p>
                    <label className="admin-console-checkbox">
                      <input
                        type="checkbox"
                        checked={platformOperator}
                        onChange={(event) => setPlatformOperator(event.target.checked)}
                      />
                      <span>NextCompli operator — can assign companies, jobs, and plans</span>
                    </label>
                    <div className="theme-admin-grid">
                      {groups.map((group) => {
                        const included = newUserGroupNames.includes(group.group_name);
                        return (
                          <label key={`new-user-group-${group.group_id}`} className="theme-admin-field">
                            <span>{group.group_name}</span>
                            <select
                              aria-label={`${group.group_name} role`}
                              value={included ? (companyJobByName[group.group_name] || 'viewer') : ''}
                              onChange={(event) => {
                                const next = event.target.value;
                                if (!next) {
                                  setNewUserGroupNames((current) => current.filter((name) => name !== group.group_name));
                                  return;
                                }
                                setNewUserGroupNames((current) => (
                                  current.includes(group.group_name) ? current : [...current, group.group_name]
                                ));
                                setCompanyJobByName((current) => ({ ...current, [group.group_name]: next }));
                              }}
                            >
                              <option value="">No access</option>
                              {COMPANY_JOBS.map((job) => (
                                <option key={job.id} value={job.id}>{job.label}</option>
                              ))}
                            </select>
                          </label>
                        );
                      })}
                    </div>
                  </>
                ) : null}
              </section>
              {!editingUserId ? (
                <section className="admin-console-accordion-group">
                  <button
                    type="button"
                    className="admin-console-accordion-toggle"
                    onClick={() => setIsCreateUserPasswordOpen((open) => !open)}
                    aria-expanded={isCreateUserPasswordOpen}
                  >
                    <span>Set onboarding password mode</span>
                    <AccordionChevronIcon open={isCreateUserPasswordOpen} />
                  </button>
                  {isCreateUserPasswordOpen ? (
                    <>
                      <div className="theme-admin-grid">
                        <label className="admin-console-checkbox">
                          <input
                            type="radio"
                            name="new-user-password-mode"
                            checked={newUserPasswordMode === 'generate'}
                            onChange={() => setNewUserPasswordMode('generate')}
                          />
                          <span>Generate temporary password and send invite</span>
                        </label>
                        <label className="admin-console-checkbox">
                          <input
                            type="radio"
                            name="new-user-password-mode"
                            checked={newUserPasswordMode === 'set'}
                            onChange={() => setNewUserPasswordMode('set')}
                          />
                          <span>Set temporary password manually</span>
                        </label>
                      </div>
                      {newUserPasswordMode === 'set' ? (
                        <div className="theme-admin-grid">
                          <label className="theme-admin-field">
                            <span>Temporary password</span>
                            <input
                              type="password"
                              value={newUserPassword}
                              onChange={(event) => setNewUserPassword(event.target.value)}
                              autoComplete="new-password"
                              minLength={12}
                            />
                            <small className="access-management-password-strength-note">
                              {getPasswordStrengthLabel(newUserPassword)}
                            </small>
                          </label>
                          <label className="theme-admin-field">
                            <span>Confirm temporary password</span>
                            <input
                              type="password"
                              value={newUserPasswordConfirm}
                              onChange={(event) => setNewUserPasswordConfirm(event.target.value)}
                              autoComplete="new-password"
                              minLength={12}
                            />
                          </label>
                        </div>
                      ) : null}
                      <p className="access-management-password-best-practice-note">
                        Best practice: use long temporary passphrases, avoid composition-rule complexity requirements, and require reset on first sign-in.
                      </p>
                    </>
                  ) : null}
                </section>
              ) : null}
              {editingUserId ? (
                <section className="admin-console-accordion-group">
                  <button
                    type="button"
                    className="admin-console-accordion-toggle"
                    onClick={() => setIsCreateUserPasswordOpen((open) => !open)}
                    aria-expanded={isCreateUserPasswordOpen}
                  >
                    <span>Change password - Optional</span>
                    <AccordionChevronIcon open={isCreateUserPasswordOpen} />
                  </button>
                  {isCreateUserPasswordOpen ? (
                    <div className="theme-admin-grid">
                      <label className="theme-admin-field">
                        <span>New password</span>
                        <input
                          type="password"
                          value={newUserPassword}
                          onChange={(event) => setNewUserPassword(event.target.value)}
                          autoComplete="new-password"
                        />
                      </label>
                      <label className="theme-admin-field">
                        <span>Confirm new password</span>
                        <input
                          type="password"
                          value={newUserPasswordConfirm}
                          onChange={(event) => setNewUserPasswordConfirm(event.target.value)}
                          autoComplete="new-password"
                        />
                      </label>
                    </div>
                  ) : null}
                </section>
              ) : null}
              <section className="admin-console-accordion-group">
                <button
                  type="button"
                  className="admin-console-accordion-toggle"
                  onClick={() => setIsCreateUserPermissionsOpen((open) => !open)}
                  aria-expanded={isCreateUserPermissionsOpen}
                >
                  <span>Attach permissions policies - Optional ({newUserPermissionIds.length})</span>
                  <AccordionChevronIcon open={isCreateUserPermissionsOpen} />
                </button>
                {isCreateUserPermissionsOpen ? (
                  <>
                    <div className="access-management-search-row">
                      <input
                        type="search"
                        className="access-management-search"
                        placeholder="Search permissions"
                        value={newUserPermissionSearch}
                        onChange={(event) => setNewUserPermissionSearch(event.target.value)}
                        aria-label="Search permissions to attach to user"
                      />
                    </div>
                    <div className="access-management-permission-toolbar">
                      <label className="theme-admin-field">
                        <span>Filter by type</span>
                        <select
                          value={newUserPermissionTypeFilter}
                          onChange={(event) => setNewUserPermissionTypeFilter(event.target.value as PermissionTypeFilter)}
                        >
                          <option value="all">All types</option>
                          <option value="service">Service operation</option>
                          <option value="admin">Administrative</option>
                        </select>
                      </label>
                      <label className="theme-admin-field">
                        <span>Logical group</span>
                        <select
                          value={newUserPermissionGroupFilter}
                          onChange={(event) => setNewUserPermissionGroupFilter(event.target.value as PermissionGroupFilter)}
                        >
                          <option value="all">All groups</option>
                          <option value="products">Products</option>
                          <option value="services">Services</option>
                          <option value="features">Features</option>
                          <option value="identity">Identity</option>
                          <option value="themes">Themes</option>
                        </select>
                      </label>
                      <label className="admin-console-checkbox access-management-selected-only-toggle">
                        <input
                          type="checkbox"
                          checked={newUserPermissionSelectedOnly}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setNewUserPermissionSelectedOnly(checked);
                            if (checked) {
                              setNewUserPermissionUnselectedOnly(false);
                            }
                          }}
                        />
                        <span>Show selected only</span>
                      </label>
                      <label className="admin-console-checkbox access-management-selected-only-toggle">
                        <input
                          type="checkbox"
                          checked={newUserPermissionUnselectedOnly}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setNewUserPermissionUnselectedOnly(checked);
                            if (checked) {
                              setNewUserPermissionSelectedOnly(false);
                            }
                          }}
                        />
                        <span>Show unselected only</span>
                      </label>
                      <label className="admin-console-checkbox access-management-select-all-toggle">
                        <input
                          type="checkbox"
                          checked={areAllFilteredUserPermissionsSelected}
                          onChange={(event) => toggleAllFilteredUserPermissions(event.target.checked)}
                        />
                        <span>Select all filtered permissions ({sortedUserPermissionCatalog.length})</span>
                      </label>
                    </div>

                    <div className="admin-console-table-wrap access-management-permissions-table-wrap">
                      <table className="admin-console-table access-management-permissions-table">
                        <thead>
                          <tr>
                            <th>
                              <input
                                type="checkbox"
                                aria-label="Select all permissions on this page"
                                checked={areAllPagedUserPermissionsSelected}
                                onChange={(event) => {
                                  const visibleIds = pagedUserPermissionCatalog.map((permission) => permission.id);
                                  setNewUserPermissionIds((current) => {
                                    const next = new Set(current);
                                    if (event.target.checked) {
                                      visibleIds.forEach((id) => next.add(id));
                                    } else {
                                      visibleIds.forEach((id) => next.delete(id));
                                    }
                                    return Array.from(next).sort((left, right) => left.localeCompare(right));
                                  });
                                }}
                              />
                            </th>
                            <th>
                              <button
                                type="button"
                                className="access-management-sort-btn"
                                onClick={() => {
                                  if (newUserPermissionSortKey === 'policyName') {
                                    setNewUserPermissionSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
                                  } else {
                                    setNewUserPermissionSortKey('policyName');
                                    setNewUserPermissionSortDirection('asc');
                                  }
                                }}
                              >
                                Policy name
                              </button>
                            </th>
                            <th>
                              <button
                                type="button"
                                className="access-management-sort-btn"
                                onClick={() => {
                                  if (newUserPermissionSortKey === 'typeLabel') {
                                    setNewUserPermissionSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
                                  } else {
                                    setNewUserPermissionSortKey('typeLabel');
                                    setNewUserPermissionSortDirection('asc');
                                  }
                                }}
                              >
                                Type
                              </button>
                            </th>
                            <th>
                              <button
                                type="button"
                                className="access-management-sort-btn"
                                onClick={() => {
                                  if (newUserPermissionSortKey === 'usedCount') {
                                    setNewUserPermissionSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
                                  } else {
                                    setNewUserPermissionSortKey('usedCount');
                                    setNewUserPermissionSortDirection('asc');
                                  }
                                }}
                              >
                                Attached entities
                              </button>
                            </th>
                            <th>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedUserPermissionCatalog.length > 0 ? (
                            pagedUserPermissionCatalog.map((permission) => (
                              <tr key={`new-user-permission-${permission.id}`}>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={newUserPermissionIds.includes(permission.id)}
                                    onChange={() => toggleId(permission.id, setNewUserPermissionIds)}
                                  />
                                </td>
                                <td>
                                  <div className="access-management-policy-cell">
                                    <strong>{permission.policyName}</strong>
                                    <span>{permission.groupLabel}</span>
                                  </div>
                                </td>
                                <td>{permission.typeLabel}</td>
                                <td>{permission.usedCount}</td>
                                <td>{permission.description}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5}>No permissions match this filter.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="access-management-permission-pagination" aria-label="User permissions pagination">
                      <label className="access-management-page-size-control">
                        <span>Rows per page</span>
                        <select
                          value={newUserPermissionPageSize}
                          onChange={(event) => setNewUserPermissionPageSize(Number(event.target.value))}
                        >
                          {PERMISSION_PAGE_SIZE_OPTIONS.map((size) => (
                            <option key={`user-perm-page-size-${size}`} value={size}>{size}</option>
                          ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={newUserPermissionPage <= 1}
                        onClick={() => setNewUserPermissionPage((page) => Math.max(1, page - 1))}
                      >
                        Previous
                      </button>
                      <span>Page {newUserPermissionPage} of {userPermissionPageCount}</span>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={newUserPermissionPage >= userPermissionPageCount}
                        onClick={() => setNewUserPermissionPage((page) => Math.min(userPermissionPageCount, page + 1))}
                      >
                        Next
                      </button>
                    </div>
                  </>
                ) : null}
              </section>
              {editingUserId ? (
                <div className="admin-console-inline-actions">
                  <button type="submit" className="btn btn-primary" disabled={isSavingUser}>
                    {isSavingUser ? (
                      <span className="btn-loading-content" aria-live="polite">
                        <span className="btn-spinner" aria-hidden="true" />
                        Saving...
                      </span>
                    ) : 'Save user changes'}
                  </button>
                  <button type="button" className="btn btn-secondary" disabled={isResendingEmail} onClick={() => void resendPasswordEmail()}>
                    {isResendingEmail ? 'Sending…' : 'Resend password email'}
                  </button>
                  {resendEmailMessage ? (
                    <div className="access-management-status-flash" style={{ marginTop: '0.5rem' }}>
                      <p style={{ margin: 0 }}>{resendEmailMessage}</p>
                      {resendEmailInviteUrl ? (
                        <p style={{ margin: '0.35rem 0 0' }}>
                          <span className="access-management-resend-note">
                            The resend action completed, but this environment is still using manual invite fallback.
                          </span>
                          Manual invite link:{' '}
                          <a className="access-management-resend-link" href={resendEmailInviteUrl} target="_blank" rel="noreferrer">
                            {resendEmailInviteUrl}
                          </a>
                        </p>
                      ) : null}
                      {resendEmailAttempts.some((attempt) => {
                        const delivery = String(attempt.delivery || '').trim().toLowerCase();
                        return delivery === 'sent' || delivery === 'failed';
                      }) ? (
                        <div className="access-management-resend-note" style={{ marginTop: '0.5rem' }}>
                          <strong>Delivery attempts</strong>
                          <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.1rem' }}>
                            {resendEmailAttempts
                              .filter((attempt) => {
                                const delivery = String(attempt.delivery || '').trim().toLowerCase();
                                return delivery === 'sent' || delivery === 'failed';
                              })
                              .map((attempt, idx) => (
                              <li key={`${attempt.channel || attempt.provider || 'attempt'}-${idx}`}>
                                {`${attempt.channel || attempt.provider || 'provider'}: ${attempt.delivery || 'unknown'}${attempt.error ? ` (${attempt.error})` : ''}`}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : !editingUserId && isCreateUserOpen ? (
                <div className="admin-console-inline-actions">
                  <button type="submit" form="create-user-form" className="btn btn-primary" disabled={isSavingUser}>
                    {isSavingUser ? (
                      <span className="btn-loading-content" aria-live="polite">
                        <span className="btn-spinner" aria-hidden="true" />
                        Saving...
                      </span>
                    ) : 'Save user'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setIsCreateUserOpen(false);
                      setEditingUserId(null);
                      setNewUserEmail('');
                      setNewUserName('');
                      setNewUserCreateManagedMailbox(false);
                      setNewUserManagedMailboxLocalPart('');
                      setNewUserInviteEmail('');
                      setNewUserPasswordMode('generate');
                      setNewUserPassword('');
                      setNewUserPasswordConfirm('');
                      setNewUserGroupNames([]);
                      setNewUserRoles([]);
                      setNewUserPermissionIds([]);
                      setNewUserPermissionSearch('');
                      setNewUserPermissionTypeFilter('all');
                      setNewUserPermissionGroupFilter('all');
                      setNewUserPermissionSelectedOnly(false);
                      setNewUserPermissionUnselectedOnly(false);
                      setNewUserPermissionSortKey('policyName');
                      setNewUserPermissionSortDirection('asc');
                      setNewUserPermissionPage(1);
                      setNewUserPermissionPageSize(25);
                      setIsCreateUserRolesOpen(false);
                      setIsAssignUserGroupsOpen(false);
                      setIsCreateUserPasswordOpen(false);
                      setIsCreateUserPermissionsOpen(false);
                      setCreateUserInviteUrl(null);
                      setStatusMessage('');
                    }}
                  >
                    Delete
                  </button>
                </div>
              ) : (
                <button type="submit" className="btn btn-primary">Save user</button>
              )}
            </form>
          ) : null}

          {!isCreateUserOpen ? (
            <div className="admin-console-table-wrap">
            <table className="admin-console-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Sign-in email</th>
                  <th>Accounts</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((entry) => {
                  const username = getUsernameLabel(entry);
                  const visibleAccounts = accountMembershipsForDisplay(entry.memberships);
                  const mappedGroups = visibleAccounts.map((account) => account.name);
                  const mappedRoles = Array.from(
                    new Set(visibleAccounts.flatMap((account) => account.roles)),
                  ).sort((left, right) => left.localeCompare(right));
                  const mappedGroupRoles = Array.from(
                    new Set(
                      visibleAccounts.flatMap((account) =>
                        account.roles.map((role) => `${account.name}: ${role}`)
                      )
                    ),
                  ).sort((left, right) => left.localeCompare(right));

                  const isPrivileged = mappedRoles.includes('platform_admin') || mappedRoles.includes('company_admin');
                  const isExpanded = expandedUserId === entry.user_id;
                  return (
                    <React.Fragment key={entry.user_id}>
                      <tr aria-expanded={isExpanded}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(entry.user_id)}
                            onChange={() => toggleId(entry.user_id, setSelectedUserIds)}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="access-management-user-disclosure"
                            onClick={() => setExpandedUserId((current) => (current === entry.user_id ? null : entry.user_id))}
                            aria-expanded={isExpanded}
                          >
                            <span className="access-management-user-identity">
                              <span>{username}</span>
                              {(entry.email_aliases || []).length > 0 ? (
                                <span className="access-management-user-aliases">
                                  Also signs in as {(entry.email_aliases || []).join(', ')}
                                </span>
                              ) : null}
                            </span>
                            <AccordionChevronIcon open={isExpanded} />
                          </button>
                        </td>
                        <td>{accountsCellLabel(entry.memberships)}</td>
                        <td>{entry.is_active ? 'Active' : 'Inactive'}</td>
                      </tr>
                      {isExpanded ? (
                        <tr className="access-management-user-detail-row">
                          <td colSpan={4}>
                            <div className="access-management-user-detail-card">
                              <div className="access-management-user-detail-grid">
                                <div>
                                  <strong>Email</strong>
                                  <p>{entry.email}</p>
                                </div>
                                <div>
                                  <strong>Login aliases</strong>
                                  <p>{(entry.email_aliases || []).join(', ') || 'None'}</p>
                                </div>
                                <div>
                                  <strong>Last accessed</strong>
                                  <p>{formatLastAccessed(entry.last_login_at)}</p>
                                </div>
                                <div>
                                  <strong>Created</strong>
                                  <p>{formatTimestamp(entry.created_at)}</p>
                                </div>
                                <div>
                                  <strong>Status</strong>
                                  <p>{entry.is_active ? 'Active' : 'Inactive'}</p>
                                </div>
                              </div>
                              <div className="access-management-user-detail-grid">
                                <div>
                                  <strong>Accounts</strong>
                                  <p>{mappedGroups.join(', ') || 'None'}</p>
                                </div>
                                <div>
                                  <strong>Assigned roles</strong>
                                  <p>{mappedRoles.join(', ') || 'None'}</p>
                                </div>
                                <div>
                                  <strong>Account roles</strong>
                                  <p>{mappedGroupRoles.join(', ') || 'None'}</p>
                                </div>
                                <div>
                                  <strong>Password status</strong>
                                  <p>{entry.password_reset_required && !entry.last_login_at ? 'Reset required' : 'Ready for sign-in'}</p>
                                </div>
                              </div>
                              {isPrivileged ? (
                                <p className="access-management-user-detail-note">
                                  Privileged access is assigned. Keep an elevated role on one account, and let the subscription set what that account can use.
                                </p>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            </div>
          ) : null}
        </div>
      ) : null}

      {currentSection === 'emailOperations' ? (
        <div className="theme-admin-card">
          <div className="admin-console-toolbar">
            <div>
              <h2>Email Operations</h2>
              <p>Compact delivery diagnostics and runtime sender visibility.</p>
            </div>
          </div>

          <div className="access-management-status-flash" style={{ marginBottom: '0.75rem' }}>
            <p style={{ margin: 0, fontWeight: 600 }}>Email Delivery Runtime</p>
            <p style={{ margin: '0.35rem 0 0' }}>
              Status: {emailRuntimeStatus?.status || 'unknown'} | Provider: {emailRuntimeStatus?.normalized_provider || emailRuntimeStatus?.provider || 'unknown'}
              {emailRuntimeStatus?.ses?.mode ? ` (${emailRuntimeStatus.ses.mode})` : ''} | Sender: {emailRuntimeStatus?.sender || 'unknown'}
            </p>
            {Array.isArray(emailRuntimeStatus?.effective_delivery_order) && emailRuntimeStatus.effective_delivery_order.length > 0 ? (
              <p style={{ margin: '0.35rem 0 0' }}>
                Delivery order: {emailRuntimeStatus.effective_delivery_order.join(' \u2192 ')}
              </p>
            ) : null}
            {Array.isArray(emailRuntimeStatus?.warnings) && emailRuntimeStatus.warnings.length > 0 ? (
              <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.1rem' }}>
                {emailRuntimeStatus.warnings.slice(0, 5).map((warning, index) => (
                  <li key={`email-ops-runtime-warning-${index}`}>{warning}</li>
                ))}
              </ul>
            ) : (
              <p style={{ margin: '0.35rem 0 0' }}>No runtime configuration warnings detected.</p>
            )}
          </div>

          <div className="access-management-status-flash" style={{ marginBottom: '0.75rem' }}>
            <p style={{ margin: 0, fontWeight: 600 }}>Email Address Directory</p>
            <p style={{ margin: '0.35rem 0 0' }}>
              Operational inbox: {ADMIN_EMAIL_INBOX_URL ? (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ marginRight: '0.5rem' }}
                    onClick={() => setShowInboxList((prev) => !prev)}
                  >
                    {showInboxList ? 'Hide mailbox list' : 'Open mailbox list'}
                  </button>
                  <a href={ADMIN_EMAIL_INBOX_URL} target="_blank" rel="noreferrer">open WorkMail home</a>
                </>
              ) : 'not configured'}
            </p>
            <p style={{ margin: '0.35rem 0 0' }}>
              Mailbox signal: {Object.values(mailboxSignals).filter((signal) => signal.has_mail || (signal.imap_unseen_count || 0) > 0).length > 0
                ? `${Object.values(mailboxSignals).filter((signal) => signal.has_mail || (signal.imap_unseen_count || 0) > 0).length} mailbox(es) flagged with waiting mail`
                : 'no mailbox currently flagged with waiting mail'}
            </p>
            <p style={{ margin: '0.35rem 0 0', opacity: 0.75 }}>
              The badge combines inbound "received" events with live WorkMail IMAP unread counts and stays visible until review is confirmed (unless unread mail remains).
            </p>
            {mailboxSignalError ? <p style={{ margin: '0.35rem 0 0', color: 'var(--color-danger)', fontWeight: 600 }}>{mailboxSignalError}</p> : null}
            {ADMIN_EMAIL_INBOX_URL && showInboxList ? (
              <div style={{ marginTop: '0.5rem', padding: '0.6rem 0.75rem', background: 'var(--color-surface-muted)', borderRadius: '0.5rem' }}>
                <p style={{ margin: 0, fontWeight: 600 }}>Known inbox identities</p>
                <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.1rem' }}>
                  {emailDirectoryEntries.map((entry) => {
                    const signal = mailboxSignals[String(entry.address || '').trim().toLowerCase()];
                    const unreadCount = Math.max(0, Number(signal?.imap_unseen_count || 0));
                    const hasMail = Boolean(signal?.has_mail) || unreadCount > 0;
                    const isClearing = clearingMailboxSignal === String(entry.address || '').trim().toLowerCase();
                    return (
                      <li key={`email-ops-inbox-${entry.address}`} style={{ marginBottom: '0.45rem' }}>
                        <a href={buildInboxUrlForAddress(entry.address)} target="_blank" rel="noreferrer">{entry.address}</a>
                        {' '}({entry.purpose})
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            marginLeft: '0.45rem',
                            padding: '0.15rem 0.45rem',
                            borderRadius: '999px',
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            background: hasMail
                              ? 'color-mix(in srgb, var(--color-danger) 14%, var(--color-surface))'
                              : 'color-mix(in srgb, var(--color-success) 12%, var(--color-surface))',
                            color: hasMail ? 'var(--color-danger)' : 'var(--color-success)',
                          }}
                        >
                          {hasMail ? (unreadCount > 0 ? `${unreadCount} unread` : 'Mail waiting') : 'Empty'}
                        </span>
                        <span style={{ opacity: 0.72 }}>
                          {' '}[WorkMail sign-in username: {entry.address.split('@')[0]} or email: {entry.address}]
                        </span>
                        {signal?.last_event_at ? (
                          <span style={{ opacity: 0.72 }}>
                            {' '}[Last signal: {new Date(signal.last_event_at).toLocaleString()}]
                          </span>
                        ) : null}
                        {hasMail ? (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ marginLeft: '0.5rem' }}
                            onClick={() => { void clearMailboxSignal(entry.address); }}
                            disabled={isClearing}
                          >
                            {isClearing ? 'Clearing...' : 'Mark empty'}
                          </button>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
            {emailRuntimeStatus?.sender && (
              <p style={{ margin: '0.35rem 0 0', color: 'var(--color-primary)', fontWeight: 500 }}>
                📧 Active sender (emails from system): <strong>{emailRuntimeStatus.sender}</strong>
                {emailRuntimeStatus.normalized_provider && ` (${emailRuntimeStatus.normalized_provider})`}
              </p>
            )}
            {Array.isArray(emailRuntimeStatus?.immediate_checks) && emailRuntimeStatus.immediate_checks.length > 0 ? (
              <div style={{ marginTop: '0.75rem' }}>
                <p style={{ margin: 0, fontWeight: 600 }}>Recommended immediate checks</p>
                <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.1rem' }}>
                  {emailRuntimeStatus.immediate_checks.slice(0, 5).map((item, index) => (
                    <li key={`email-ops-check-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {Array.isArray(emailRuntimeStatus?.natural_next_steps) && emailRuntimeStatus.natural_next_steps.length > 0 ? (
              <div style={{ marginTop: '0.75rem' }}>
                <p style={{ margin: 0, fontWeight: 600 }}>Natural next steps</p>
                <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.1rem' }}>
                  {emailRuntimeStatus.natural_next_steps.slice(0, 5).map((item, index) => (
                    <li key={`email-ops-next-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>

          <div className="access-management-status-flash" style={{ marginBottom: '0.75rem' }}>
            <p style={{ margin: 0, fontWeight: 600 }}>Tier 1 Mailbox Password Management</p>
            <p style={{ margin: '0.35rem 0 0' }}>
              Admin-only reset flow for shared role mailboxes. Password values are not stored in the browser after update.
            </p>
            {tier1MailboxEntries.length > 0 ? (
              <div style={{ marginTop: '0.6rem', display: 'grid', gap: '0.6rem' }}>
                {tier1MailboxEntries.map((entry) => {
                  const normalizedAddress = entry.address.toLowerCase();
                  const isSubmitting = tier1MailboxPasswordSubmitting === normalizedAddress;
                  return (
                    <div
                      key={`tier1-password-reset-${entry.address}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(260px, 1fr) minmax(220px, 1fr) auto',
                        gap: '0.5rem',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <strong>{entry.address}</strong>
                        <p style={{ margin: '0.2rem 0 0', opacity: 0.75 }}>{entry.purpose}</p>
                      </div>
                      <input
                        type="password"
                        autoComplete="new-password"
                        value={tier1MailboxPasswordDrafts[normalizedAddress] || ''}
                        onChange={(event) => {
                          const value = event.target.value;
                          setTier1MailboxPasswordDrafts((current) => ({ ...current, [normalizedAddress]: value }));
                        }}
                        placeholder="Enter new mailbox password"
                        className="access-management-search"
                        aria-label={`New password for ${entry.address}`}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={isSubmitting}
                        onClick={() => void resetTier1MailboxPassword(entry.address)}
                      >
                        {isSubmitting ? (
                          <span className="password-submit-loading" aria-live="polite">
                            <span className="password-submit-spinner" aria-hidden="true" />
                            <span>Updating password...</span>
                          </span>
                        ) : 'Update password'}
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ margin: '0.5rem 0 0' }}>No Tier 1 mailboxes detected in the directory.</p>
            )}
          </div>
        </div>
      ) : null}

      {currentSection === 'observability' ? (
        <ObservabilityAdminSection
          gatewayBaseUrl={GATEWAY_BASE_URL}
          metricsServiceUrl={METRICS_SERVICE_URL}
          accessToken={accessToken}
        />
      ) : null}

      {currentSection === 'experience' ? (
        <ExperienceAdminSection gatewayBaseUrl={GATEWAY_BASE_URL} accessToken={accessToken} />
      ) : null}

      {currentSection === 'costOptimization' ? (
        <div className="theme-admin-card">
          <div className="admin-console-toolbar">
            <div>
              <h2>Cost Optimization</h2>
              <p>Monitor platform health and operational costs.</p>
            </div>
          </div>
          {costOptimization ? (
            <div style={{ marginTop: '1.5rem' }}>
              <div
                style={{
                  marginBottom: '1rem',
                  padding: '0.9rem 1rem',
                  borderRadius: '0.5rem',
                  backgroundColor: 'var(--color-background-secondary)',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '0.75rem',
                }}
              >
                <div>
                  <p style={{ margin: '0 0 0.2rem', fontSize: '0.8rem', opacity: 0.7 }}>Last Updated</p>
                  <p style={{ margin: 0, fontWeight: 600 }}>{formatTimestamp(costOptimization.timestamp)}</p>
                </div>
                <div>
                  <p style={{ margin: '0 0 0.2rem', fontSize: '0.8rem', opacity: 0.7 }}>Billing Source</p>
                  <p style={{ margin: 0, fontWeight: 600 }}>
                    {costOptimization.cost?.actual_source || costOptimization.analysis?.billing_alignment?.source || 'Modeled runtime estimate'}
                  </p>
                  <p
                    style={{
                      margin: '0.35rem 0 0',
                      display: 'inline-block',
                      fontSize: '0.75rem',
                      padding: '0.2rem 0.45rem',
                      borderRadius: '999px',
                      background: 'color-mix(in srgb, var(--color-primary) 12%, var(--color-surface))',
                      color: 'var(--color-primary)',
                      fontWeight: 600,
                    }}
                  >
                    Metric: {costOptimization.analysis?.billing_alignment?.metric || 'unknown'} | Scope: {
                      costOptimization.analysis?.billing_alignment?.linked_account_filter_applied
                        ? `linked account ${costOptimization.analysis?.billing_alignment?.linked_account || 'filtered'}`
                        : 'payer-wide'
                    }
                  </p>
                </div>
                <div>
                  <p style={{ margin: '0 0 0.2rem', fontSize: '0.8rem', opacity: 0.7 }}>Current Billing Window</p>
                  <p style={{ margin: 0, fontWeight: 600 }}>
                    {formatBillingWindow(costOptimization.cost?.actual_time_period || costOptimization.analysis?.billing_alignment?.time_period)}
                  </p>
                </div>
                <div>
                  <p style={{ margin: '0 0 0.2rem', fontSize: '0.8rem', opacity: 0.7 }}>Data Status</p>
                  <p style={{ margin: 0, fontWeight: 600 }}>{costOptimization.cost?.status || costOptimization.status || 'unknown'}</p>
                </div>
              </div>
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ marginBottom: '0.75rem' }}>Cost Analysis</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                  <div style={{ padding: '1rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: '0.5rem' }}>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', opacity: 0.7 }}>Estimated Daily Cost</p>
                    <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>
                      {formatUsd(costOptimization.cost?.estimated_daily_usd)}
                    </p>
                  </div>
                  <div style={{ padding: '1rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: '0.5rem' }}>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', opacity: 0.7 }}>Billing-Aligned Monthly Cost</p>
                    <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>
                      {formatUsd(costOptimization.analysis?.estimated_monthly_usd)}
                    </p>
                  </div>
                  <div style={{ padding: '1rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: '0.5rem' }}>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', opacity: 0.7 }}>Monthly Target Budget</p>
                    <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>
                      {formatUsd(isFiniteNumber(costOptimization.analysis?.alert_reference_usd) ? costOptimization.analysis?.alert_reference_usd : costAlertReferenceUsd)}
                    </p>
                  </div>
                  <div style={{ padding: '1rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: '0.5rem' }}>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', opacity: 0.7 }}>Variance Vs Target</p>
                    <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>
                      {isFiniteNumber(costOptimization.analysis?.estimated_monthly_usd)
                        ? formatUsd(
                          costOptimization.analysis.estimated_monthly_usd
                          - (isFiniteNumber(costOptimization.analysis?.alert_reference_usd)
                            ? costOptimization.analysis.alert_reference_usd
                            : costAlertReferenceUsd),
                        )
                        : 'Unavailable'}
                    </p>
                  </div>
                  <div style={{ padding: '1rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: '0.5rem' }}>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', opacity: 0.7 }}>Actual Hourly Cost</p>
                    <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>
                      {formatUsd(costOptimization.cost?.actual_hourly_usd, 4, 'Modeled estimate')}
                    </p>
                  </div>
                </div>
                <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                  <div style={{ padding: '1rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: '0.5rem' }}>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', opacity: 0.7 }}>AWS Month-to-Date</p>
                    <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 600 }}>
                      {formatUsd(costOptimization.cost?.actual_month_to_date_usd)}
                    </p>
                  </div>
                  <div style={{ padding: '1rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: '0.5rem' }}>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', opacity: 0.7 }}>AWS Previous Month</p>
                    <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 600 }}>
                      {formatUsd(costOptimization.cost?.actual_previous_month_usd)}
                    </p>
                  </div>
                  <div style={{ padding: '1rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: '0.5rem' }}>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', opacity: 0.7 }}>AWS Forecast This Month</p>
                    <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 600 }}>
                      {formatUsd(costOptimization.cost?.actual_forecast_monthly_usd)}
                    </p>
                  </div>
                  <div style={{ padding: '1rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: '0.5rem' }}>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', opacity: 0.7 }}>AWS Projected This Month</p>
                    <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 600 }}>
                      {formatUsd(costOptimization.cost?.actual_projected_monthly_usd)}
                    </p>
                  </div>
                  <div style={{ padding: '1rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: '0.5rem' }}>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', opacity: 0.7 }}>AWS Daily Average</p>
                    <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 600 }}>
                      {formatUsd(costOptimization.cost?.actual_daily_average_usd)}
                    </p>
                  </div>
                </div>
                {costOptimization.analysis?.billing_scope_warning ? (
                  <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: '0.5rem' }}>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--color-warning, #b45309)', fontWeight: 600 }}>
                      {costOptimization.analysis.billing_scope_warning}
                    </p>
                  </div>
                ) : null}
                {costOptimization.analysis?.model_breakdown_hourly_usd ? (
                  <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: '0.5rem' }}>
                    <p style={{ margin: '0 0 0.35rem', fontWeight: 600 }}>Modeled Hourly Breakdown</p>
                    <p style={{ margin: '0 0 0.2rem' }}>Base platform: {formatUsd(costOptimization.analysis.model_breakdown_hourly_usd.base_platform, 4)}</p>
                    <p style={{ margin: '0 0 0.2rem' }}>Compute vCPU: {formatUsd(costOptimization.analysis.model_breakdown_hourly_usd.compute_vcpu, 4)}</p>
                    <p style={{ margin: '0 0 0.2rem' }}>Compute memory: {formatUsd(costOptimization.analysis.model_breakdown_hourly_usd.compute_memory, 4)}</p>
                    <p style={{ margin: 0 }}>Modeled total: {formatUsd(costOptimization.analysis.model_breakdown_hourly_usd.modeled_total, 4)}</p>
                    <p style={{ margin: '0.35rem 0 0 0' }}>
                      Modeled monthly: {formatUsd(costOptimization.analysis.modeled_monthly_usd)}
                    </p>
                  </div>
                ) : null}
                <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                  <div style={{ padding: '1rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: '0.5rem' }}>
                    <p style={{ margin: '0 0 0.35rem', fontSize: '0.875rem', opacity: 0.7 }}>Current Billing Window</p>
                    <p style={{ margin: 0, fontWeight: 600 }}>{formatBillingWindow(costOptimization.cost?.actual_time_period)}</p>
                  </div>
                  <div style={{ padding: '1rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: '0.5rem' }}>
                    <p style={{ margin: '0 0 0.35rem', fontSize: '0.875rem', opacity: 0.7 }}>Previous Billing Window</p>
                    <p style={{ margin: 0, fontWeight: 600 }}>{formatBillingWindow(costOptimization.cost?.actual_previous_time_period)}</p>
                  </div>
                </div>
                <div style={{ marginTop: '1rem' }}>
                  <h3 style={{ marginBottom: '0.5rem' }}>Top AWS Cost Drivers</h3>
                  {(costOptimization.cost?.actual_top_services || []).length > 0 ? (
                    <ul style={{ paddingLeft: '1.25rem', margin: 0, lineHeight: 1.6 }}>
                      {(costOptimization.cost?.actual_top_services || [])
                        .slice(0, 8)
                        .map((item, idx) => (
                          <li key={`${item.service || 'service'}-${idx}`} style={{ marginBottom: '0.35rem', fontSize: '0.9375rem' }}>
                            {String(item.service || 'Other')}: {formatUsd(item.amount_usd)}
                          </li>
                        ))}
                    </ul>
                  ) : (
                    <p style={{ margin: 0, opacity: 0.8 }}>Showing modeled service-level spend breakdown for this environment.</p>
                  )}
                </div>
                <div style={{ marginTop: '1rem' }}>
                  <h3 style={{ marginBottom: '0.5rem' }}>AWS Cost Categories</h3>
                  {costOptimization.analysis?.billing_alignment?.service_categories
                  && Object.keys(costOptimization.analysis.billing_alignment.service_categories).length > 0 ? (
                    <ul style={{ paddingLeft: '1.25rem', margin: 0, lineHeight: 1.6 }}>
                      {Object.entries(costOptimization.analysis.billing_alignment.service_categories)
                        .sort((a, b) => Number(b[1] || 0) - Number(a[1] || 0))
                        .map(([name, value]) => (
                          <li key={`category-${name}`} style={{ marginBottom: '0.35rem', fontSize: '0.9375rem' }}>
                            {name}: {formatUsd(value)}
                          </li>
                        ))}
                    </ul>
                  ) : (
                    <p style={{ margin: 0, opacity: 0.8 }}>Category-level AWS costs are unavailable for this window.</p>
                  )}
                </div>
                <div style={{ marginTop: '1rem' }}>
                  <h3 style={{ marginBottom: '0.5rem' }}>All AWS Services (Actual)</h3>
                  {costOptimization.analysis?.billing_alignment?.all_services
                  && Object.keys(costOptimization.analysis.billing_alignment.all_services).length > 0 ? (
                    <>
                      <ul style={{ paddingLeft: '1.25rem', margin: 0, lineHeight: 1.6 }}>
                        {Object.entries(costOptimization.analysis.billing_alignment.all_services)
                          .sort((a, b) => Number(b[1] || 0) - Number(a[1] || 0))
                          .slice(0, 25)
                          .map(([name, value]) => (
                            <li key={`service-${name}`} style={{ marginBottom: '0.35rem', fontSize: '0.9375rem' }}>
                              {name}: {formatUsd(value)}
                            </li>
                          ))}
                      </ul>
                      <p style={{ marginTop: '0.5rem', opacity: 0.8, fontSize: '0.85rem' }}>
                        Showing top {Math.min(25, Object.keys(costOptimization.analysis.billing_alignment.all_services).length)} of {Object.keys(costOptimization.analysis.billing_alignment.all_services).length} services by spend.
                      </p>
                    </>
                  ) : (
                    <p style={{ margin: 0, opacity: 0.8 }}>Full AWS service totals are unavailable for this window.</p>
                  )}
                </div>
                {costOptimization.analysis?.potential_causes && costOptimization.analysis.potential_causes.length > 0 ? (
                  <div style={{ marginTop: '1rem' }}>
                    <h3 style={{ marginBottom: '0.5rem' }}>Potential Drivers For Higher Bill</h3>
                    <ul style={{ paddingLeft: '1.25rem', margin: 0, lineHeight: 1.6 }}>
                      {costOptimization.analysis.potential_causes.map((cause, idx) => (
                        <li key={idx} style={{ marginBottom: '0.4rem', fontSize: '0.9375rem' }}>{cause}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ marginBottom: '0.75rem' }}>Gemini Captcha Vision</h3>
                <p style={{ margin: '0 0 0.75rem', opacity: 0.8, fontSize: '0.9rem' }}>
                  Per-request usageMetadata from Vantage cloud vision (not included in AWS Cost Explorer).
                  Project {costOptimization.gemini?.project?.project_id || 'NextCompli'}
                  {costOptimization.gemini?.project?.key_suffix ? ` · key …${costOptimization.gemini.project.key_suffix}` : ''}.
                </p>
                {costOptimization.gemini?.status === 'ok' ? (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                      <div style={{ padding: '1rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: '0.5rem' }}>
                        <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', opacity: 0.7 }}>Est. Today</p>
                        <p style={{ margin: 0, fontSize: '1.35rem', fontWeight: 600 }}>
                          {formatUsd(costOptimization.gemini.estimated_today_usd)}
                        </p>
                        <p style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', opacity: 0.7 }}>
                          {formatCount(costOptimization.gemini.requests_today, '0')} req · {formatCount(costOptimization.gemini.tokens_today, '0')} tokens
                        </p>
                      </div>
                      <div style={{ padding: '1rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: '0.5rem' }}>
                        <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', opacity: 0.7 }}>Est. Month-to-Date</p>
                        <p style={{ margin: 0, fontSize: '1.35rem', fontWeight: 600 }}>
                          {formatUsd(costOptimization.gemini.estimated_month_to_date_usd)}
                        </p>
                        <p style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', opacity: 0.7 }}>
                          {formatCount(costOptimization.gemini.requests_month_to_date, '0')} req · {formatCount(costOptimization.gemini.tokens_month_to_date, '0')} tokens
                        </p>
                      </div>
                      <div style={{ padding: '1rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: '0.5rem' }}>
                        <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', opacity: 0.7 }}>Errors / Rate Limits (today)</p>
                        <p style={{ margin: 0, fontSize: '1.35rem', fontWeight: 600 }}>
                          {formatCount(costOptimization.gemini.today?.errors, '0')} / {formatCount(costOptimization.gemini.today?.rate_limited, '0')}
                        </p>
                        {costOptimization.gemini.last_rate_limited_at ? (
                          <p style={{ margin: '0.35rem 0 0', fontSize: '0.8rem', opacity: 0.7 }}>
                            Last 429: {formatTimestamp(costOptimization.gemini.last_rate_limited_at)}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <div style={{ marginTop: '0.85rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                      {costOptimization.gemini.project?.ai_studio_usage_url ? (
                        <a href={costOptimization.gemini.project.ai_studio_usage_url} target="_blank" rel="noreferrer">
                          AI Studio Usage
                        </a>
                      ) : null}
                      {costOptimization.gemini.project?.ai_studio_spend_url ? (
                        <a href={costOptimization.gemini.project.ai_studio_spend_url} target="_blank" rel="noreferrer">
                          AI Studio Spend
                        </a>
                      ) : null}
                      {costOptimization.gemini.project?.ai_studio_rate_limits_url ? (
                        <a href={costOptimization.gemini.project.ai_studio_rate_limits_url} target="_blank" rel="noreferrer">
                          Rate Limits
                        </a>
                      ) : null}
                    </div>
                    {costOptimization.gemini.ops_checklist && costOptimization.gemini.ops_checklist.length > 0 ? (
                      <ul style={{ marginTop: '0.85rem', paddingLeft: '1.25rem', lineHeight: 1.55 }}>
                        {costOptimization.gemini.ops_checklist.map((item, idx) => (
                          <li key={`gemini-ops-${idx}`} style={{ marginBottom: '0.3rem', fontSize: '0.9rem' }}>{item}</li>
                        ))}
                      </ul>
                    ) : null}
                    {costOptimization.gemini.by_model && Object.keys(costOptimization.gemini.by_model).length > 0 ? (
                      <div style={{ marginTop: '0.75rem' }}>
                        <p style={{ margin: '0 0 0.4rem', fontWeight: 600, fontSize: '0.9rem' }}>By model</p>
                        <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                          {Object.entries(costOptimization.gemini.by_model).map(([model, stats]) => (
                            <li key={`gemini-model-${model}`} style={{ marginBottom: '0.25rem', fontSize: '0.875rem' }}>
                              {model}: {formatCount(stats?.requests, '0')} req · {formatCount(stats?.total_tokens, '0')} tokens · {formatUsd(stats?.estimated_cost_usd)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <p style={{ margin: 0, opacity: 0.8 }}>
                    Gemini usage ledger unavailable{costOptimization.gemini?.error ? `: ${costOptimization.gemini.error}` : '.'}
                  </p>
                )}
              </div>
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ marginBottom: '0.75rem' }}>Resource Health</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
                  <div style={{ padding: '1rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: '0.5rem', textAlign: 'center' }}>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', opacity: 0.7 }}>Active Services</p>
                    <p style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>{formatCount(costOptimization.resource_usage?.active_services, '0')}</p>
                  </div>
                  <div style={{ padding: '1rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: '0.5rem', textAlign: 'center' }}>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', opacity: 0.7 }}>Healthy Services</p>
                    <p style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>{formatCount(costOptimization.resource_usage?.healthy_services, '0')}</p>
                  </div>
                  <div style={{ padding: '1rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: '0.5rem', textAlign: 'center' }}>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', opacity: 0.7 }}>Degraded Services</p>
                    <p style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>{formatCount(costOptimization.resource_usage?.degraded_services, '0')}</p>
                  </div>
                  <div style={{ padding: '1rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: '0.5rem', textAlign: 'center' }}>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', opacity: 0.7 }}>Unhealthy Services</p>
                    <p style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>{formatCount(costOptimization.resource_usage?.unhealthy_services, '0')}</p>
                  </div>
                </div>
              </div>
              {costOptimization.recommendations && costOptimization.recommendations.length > 0 ? (
                <div>
                  <h3 style={{ marginBottom: '0.75rem' }}>Recommendations</h3>
                  <ul style={{ paddingLeft: '1.25rem', margin: 0, lineHeight: 1.6 }}>
                    {costOptimization.recommendations.map((rec, idx) => (
                      <li key={idx} style={{ marginBottom: '0.5rem', fontSize: '0.9375rem' }}>
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div>
                  <h3 style={{ marginBottom: '0.75rem' }}>Recommendations</h3>
                  <p style={{ margin: 0, opacity: 0.8 }}>No optimization recommendations were returned by the metrics service for this environment.</p>
                </div>
              )}
            </div>
          ) : (
            <p style={{ marginTop: '1.5rem' }}>{costOptimizationError || 'Cost optimization data is loading...'}</p>
          )}
        </div>
      ) : null}

      {currentSection === 'groups' ? (
        <div className="theme-admin-card">
          <div className="admin-console-toolbar">
            <div>
              <h2>
                Accounts <span className="access-management-count">({filteredGroups.length}/{groups.length})</span>
                <span className="access-management-info">Info</span>
              </h2>
              <p>
                Platform view of subscription companies. Invite and manage people on{' '}
                <strong>Paying subscriptions</strong> (Manage people) or the console{' '}
                <strong>Team</strong> page — not here.
              </p>
            </div>
            <div className="admin-console-inline-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setIsCreateGroupOpen((open) => {
                    const nextOpen = !open;
                    setEditingGroupId(null);
                    setNewGroupName('');
                    setNewGroupParentId('');
                    setNewGroupPlan('basic');
                    setGroupUserJobById({});
                    setNewGroupUserIds([]);
                    setNewGroupRoleIds([]);
                    setNewGroupPermissionIds([]);
                    setNewGroupUserSearch('');
                    setNewGroupPermissionSearch('');
                    setIsCreateGroupNameOpen(nextOpen);
                    setIsCreateGroupUsersOpen(nextOpen);
                    setIsCreateGroupRolesOpen(false);
                    setIsCreateGroupPermissionsOpen(false);
                    return nextOpen;
                  });
                }}
              >
                {isCreateGroupOpen ? 'Close' : 'Create account'}
              </button>
              <button type="button" className="btn btn-secondary" disabled={selectedGroupIds.length !== 1} onClick={startEditSelectedGroup}>
                Edit
              </button>
              <button type="button" className="btn btn-secondary" disabled={selectedGroupIds.length === 0} onClick={() => void deleteSelectedGroups()}>
                Delete
              </button>
            </div>
          </div>

          {!isCreateGroupOpen ? (
            <div className="access-management-search-row">
              <input
                type="search"
                className="access-management-search"
                placeholder="Search"
                value={groupSearch}
                onChange={(event) => setGroupSearch(event.target.value)}
                aria-label="Search accounts"
              />
              <label className="access-management-subgroups-toggle" aria-label="Show teams">
                <input
                  type="checkbox"
                  checked={showSubgroups}
                  onChange={(event) => setShowSubgroups(event.target.checked)}
                />
                <span>Show teams</span>
              </label>
            </div>
          ) : null}

          {isCreateGroupOpen ? (
            <form className="admin-console-form" onSubmit={(event) => void createGroup(event)}>
              <h3>{editingGroupId !== null ? 'Edit account' : 'Create account'}</h3>
              {editingGroupId === null ? (
                <ol className="access-management-onboard-steps" aria-label="New account steps">
                  <li>Name the account and pick the subscription plan</li>
                  <li>Add the Admin (and optional people)</li>
                  <li>Save — then open their first compliance job</li>
                </ol>
              ) : null}

              <section className="admin-console-accordion-group">
                <button
                  type="button"
                  className="admin-console-accordion-toggle"
                  onClick={() => setIsCreateGroupNameOpen((open) => !open)}
                  aria-expanded={isCreateGroupNameOpen}
                >
                  <span>Name the account</span>
                  <AccordionChevronIcon open={isCreateGroupNameOpen} />
                </button>
                {isCreateGroupNameOpen ? (
                  <>
                    <label className="theme-admin-field">
                      <span>Account name</span>
                      <input aria-label="Account name" value={newGroupName} onChange={(event) => setNewGroupName(event.target.value)} required />
                    </label>
                    {selectedGroupParentTopLevel ? (
                      <p className="access-management-scope-note">
                        Team name will be saved as <strong>{selectedGroupParentTopLevel.group_name}:{newGroupName.trim() || 'name'}</strong>.
                      </p>
                    ) : null}
                    <label className="theme-admin-field">
                      <span>Subscription plan</span>
                      <select
                        aria-label="Subscription plan"
                        value={newGroupPlan}
                        onChange={(event) => setNewGroupPlan(event.target.value)}
                      >
                        <option value="none">No plan</option>
                        <option value="basic">CompliCore</option>
                        <option value="advanced">CompliAdvantage</option>
                        <option value="enterprise">CompliComplete</option>
                        {newGroupPlan === 'custom' ? <option value="custom">Custom features already on this company</option> : null}
                      </select>
                    </label>
                    <p className="access-management-scope-note">
                      The plan is the set of features everyone on this account can use. A new account starts on CompliCore.
                    </p>
                    {newGroupNameAlreadyExists ? (
                      <p className="access-management-scope-note">
                        {newGroupName.trim()} already exists. Close this form, select that account, and choose Edit to set the plan and add a person.
                      </p>
                    ) : null}
                  </>
                ) : null}
              </section>

              <p className="access-management-scope-note">
                Admin, Contributor, and Viewer are already on every account. Pick one of those jobs for each person. Extra roles are other duties, such as sales.
              </p>

              <section className="admin-console-accordion-group">
                <button
                  type="button"
                  className="admin-console-accordion-toggle"
                  onClick={() => setIsCreateGroupRolesOpen((open) => !open)}
                  aria-expanded={isCreateGroupRolesOpen}
                >
                  <span>Extra roles - optional ({extraRolesForSelectedParent.filter((role) => newGroupRoleIds.includes(role.id)).length})</span>
                  <AccordionChevronIcon open={isCreateGroupRolesOpen} />
                </button>
                {isCreateGroupRolesOpen ? (
                  <div className="theme-admin-grid">
                    {extraRolesForSelectedParent.length === 0 ? (
                      <p className="access-management-scope-note">No extra roles. Admin, Contributor, and Viewer cover this account.</p>
                    ) : null}
                    {extraRolesForSelectedParent.map((role) => (
                      <label key={`new-group-role-${role.id}`} className="admin-console-checkbox">
                        <input
                          type="checkbox"
                          checked={newGroupRoleIds.includes(role.id)}
                          onChange={() => toggleId(role.id, setNewGroupRoleIds)}
                        />
                        <span>{role.name}</span>
                      </label>
                    ))}
                  </div>
                ) : null}
              </section>

              <section className="admin-console-accordion-group">
                <button
                  type="button"
                  className="admin-console-accordion-toggle"
                  onClick={() => setIsCreateGroupUsersOpen((open) => !open)}
                  aria-expanded={isCreateGroupUsersOpen}
                >
                  <span>People on this account ({newGroupUserIds.length})</span>
                  <AccordionChevronIcon open={isCreateGroupUsersOpen} />
                </button>
                {isCreateGroupUsersOpen ? (
                  <>
                    <div className="access-management-search-row">
                      <input
                        type="search"
                        className="access-management-search"
                        placeholder="Search people"
                        value={newGroupUserSearch}
                        onChange={(event) => setNewGroupUserSearch(event.target.value)}
                        aria-label="Search people to add"
                      />
                    </div>
                    <div className="theme-admin-grid">
                      {filteredNewGroupUsers.map((entry) => {
                        const selected = newGroupUserIds.includes(entry.user_id);
                        const job = groupUserJobById[entry.user_id] === 'contributor' || groupUserJobById[entry.user_id] === 'viewer'
                          ? groupUserJobById[entry.user_id]
                          : 'company_admin';
                        return (
                          <div key={`new-group-user-${entry.user_id}`}>
                            <label className="admin-console-checkbox">
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => {
                                  toggleId(entry.user_id, setNewGroupUserIds);
                                  if (!selected) {
                                    setGroupUserJobById((current) => ({
                                      ...current,
                                      [entry.user_id]: current[entry.user_id] || 'company_admin',
                                    }));
                                  }
                                }}
                              />
                              <span>{getUsernameLabel(entry)}</span>
                            </label>
                            {selected ? (
                              <label className="theme-admin-field">
                                <span>Job</span>
                                <select
                                  aria-label={`Job for ${getUsernameLabel(entry)}`}
                                  value={job}
                                  onChange={(event) => {
                                    const nextJob = event.target.value;
                                    setGroupUserJobById((current) => ({ ...current, [entry.user_id]: nextJob }));
                                  }}
                                >
                                  {COMPANY_JOBS.map((companyJob) => (
                                    <option key={`${entry.user_id}-${companyJob.id}`} value={companyJob.id}>{companyJob.label}</option>
                                  ))}
                                </select>
                              </label>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : null}
              </section>

              <section className="admin-console-accordion-group">
                <button
                  type="button"
                  className="admin-console-accordion-toggle"
                  onClick={() => setIsCreateGroupPermissionsOpen((open) => !open)}
                  aria-expanded={isCreateGroupPermissionsOpen}
                >
                  <span>Optional permission additions ({newGroupPermissionIds.length})</span>
                  <AccordionChevronIcon open={isCreateGroupPermissionsOpen} />
                </button>
                {isCreateGroupPermissionsOpen ? (
                  <>
                    {selectedGroupParentTopLevel ? (
                      <p className="access-management-scope-note">
                        This subgroup can only use permissions already granted to top-level group <strong>{selectedGroupParentTopLevel.group_name}</strong>.
                      </p>
                    ) : null}
                    <div className="access-management-search-row">
                      <input
                        type="search"
                        className="access-management-search"
                        placeholder="Search permissions"
                        value={newGroupPermissionSearch}
                        onChange={(event) => setNewGroupPermissionSearch(event.target.value)}
                        aria-label="Search permissions to add"
                      />
                    </div>
                    <div className="access-management-permission-toolbar">
                      <label className="theme-admin-field">
                        <span>Filter by type</span>
                        <select
                          value={newGroupPermissionTypeFilter}
                          onChange={(event) => setNewGroupPermissionTypeFilter(event.target.value as PermissionTypeFilter)}
                        >
                          <option value="all">All types</option>
                          <option value="service">Service operation</option>
                          <option value="admin">Administrative</option>
                        </select>
                      </label>
                      <label className="theme-admin-field">
                        <span>Logical group</span>
                        <select
                          value={newGroupPermissionGroupFilter}
                          onChange={(event) => setNewGroupPermissionGroupFilter(event.target.value as PermissionGroupFilter)}
                        >
                          <option value="all">All groups</option>
                          <option value="products">Products</option>
                          <option value="services">Services</option>
                          <option value="features">Features</option>
                          <option value="identity">Identity</option>
                          <option value="themes">Themes</option>
                        </select>
                      </label>
                      <label className="admin-console-checkbox access-management-selected-only-toggle">
                        <input
                          type="checkbox"
                          checked={newGroupPermissionSelectedOnly}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setNewGroupPermissionSelectedOnly(checked);
                            if (checked) {
                              setNewGroupPermissionUnselectedOnly(false);
                            }
                          }}
                        />
                        <span>Show selected only</span>
                      </label>
                      <label className="admin-console-checkbox access-management-selected-only-toggle">
                        <input
                          type="checkbox"
                          checked={newGroupPermissionUnselectedOnly}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setNewGroupPermissionUnselectedOnly(checked);
                            if (checked) {
                              setNewGroupPermissionSelectedOnly(false);
                            }
                          }}
                        />
                        <span>Show unselected only</span>
                      </label>
                      <label className="admin-console-checkbox access-management-select-all-toggle">
                        <input
                          type="checkbox"
                          checked={areAllFilteredGroupPermissionsSelected}
                          onChange={(event) => toggleAllFilteredGroupPermissions(event.target.checked)}
                        />
                        <span>Select all filtered permissions ({sortedPermissionCatalog.length})</span>
                      </label>
                    </div>

                    <div className="admin-console-table-wrap access-management-permissions-table-wrap">
                      <table className="admin-console-table access-management-permissions-table">
                        <thead>
                          <tr>
                            <th>
                              <input
                                type="checkbox"
                                aria-label="Select all permissions on this page"
                                checked={areAllPagedGroupPermissionsSelected}
                                onChange={(event) => {
                                  const visibleIds = pagedPermissionCatalog.map((permission) => permission.id);
                                  setNewGroupPermissionIds((current) => {
                                    const next = new Set(current);
                                    if (event.target.checked) {
                                      visibleIds.forEach((id) => next.add(id));
                                    } else {
                                      visibleIds.forEach((id) => next.delete(id));
                                    }
                                    return Array.from(next).sort((left, right) => left.localeCompare(right));
                                  });
                                }}
                              />
                            </th>
                            <th>
                              <button type="button" className="access-management-sort-btn" onClick={() => togglePermissionSort('policyName')}>
                                Policy name
                              </button>
                            </th>
                            <th>
                              <button type="button" className="access-management-sort-btn" onClick={() => togglePermissionSort('typeLabel')}>
                                Type
                              </button>
                            </th>
                            <th>
                              <button type="button" className="access-management-sort-btn" onClick={() => togglePermissionSort('usedCount')}>
                                Used as
                              </button>
                            </th>
                            <th>
                              <button type="button" className="access-management-sort-btn" onClick={() => togglePermissionSort('description')}>
                                Description
                              </button>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedPermissionCatalog.length > 0 ? (
                            pagedPermissionCatalog.map((permission) => (
                              <tr key={`new-group-permission-${permission.id}`}>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={newGroupPermissionIds.includes(permission.id)}
                                    onChange={() => toggleId(permission.id, setNewGroupPermissionIds)}
                                  />
                                </td>
                                <td>
                                  <div className="access-management-policy-cell">
                                    <strong>{permission.policyName}</strong>
                                    <span>{permission.groupLabel}</span>
                                  </div>
                                </td>
                                <td>{permission.typeLabel}</td>
                                <td>{permission.usedAs}</td>
                                <td>{permission.description}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5}>No permissions match this filter.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="access-management-permission-pagination" aria-label="Permissions pagination">
                      <label className="access-management-page-size-control">
                        <span>Rows per page</span>
                        <select
                          value={newGroupPermissionPageSize}
                          onChange={(event) => setNewGroupPermissionPageSize(Number(event.target.value))}
                        >
                          {PERMISSION_PAGE_SIZE_OPTIONS.map((size) => (
                            <option key={`group-perm-page-size-${size}`} value={size}>{size}</option>
                          ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={newGroupPermissionPage <= 1}
                        onClick={() => setNewGroupPermissionPage((page) => Math.max(1, page - 1))}
                      >
                        Previous
                      </button>
                      <span>Page {newGroupPermissionPage} of {permissionPageCount}</span>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={newGroupPermissionPage >= permissionPageCount}
                        onClick={() => setNewGroupPermissionPage((page) => Math.min(permissionPageCount, page + 1))}
                      >
                        Next
                      </button>
                    </div>
                  </>
                ) : null}
              </section>

              <button type="submit" className="btn btn-primary" disabled={newGroupNameAlreadyExists}>{editingGroupId !== null ? 'Save account changes' : 'Save account'}</button>
            </form>
          ) : null}

          {!isCreateGroupOpen ? (
            <div className="admin-console-table-wrap">
            <table className="admin-console-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Account name</th>
                  <th>Users</th>
                  <th>Roles</th>
                  <th>Permissions</th>
                </tr>
              </thead>
              <tbody>
                {filteredGroups.map((entry) => {
                  const normalizedGroupName = normalizeGroupName(entry.group_name);
                  const usersFromMemberships = users.filter((userEntry) =>
                    userEntry.memberships.some(
                      (membership) =>
                        Number(membership.company_id) === Number(entry.group_id)
                        || normalizeGroupName(membership.company_name || '') === normalizedGroupName,
                    ),
                  );
                  const usersFromBindings = userGroupBindings
                    .filter((binding) =>
                      binding.groupNames.some((groupName) => normalizeGroupName(groupName) === normalizedGroupName),
                    )
                    .map((binding) => users.find((userEntry) => userEntry.user_id === binding.userId))
                    .filter((userEntry): userEntry is AdminUser => Boolean(userEntry))
                    ;
                  const usersForGroup = Array.from(new Set([...usersFromMemberships, ...usersFromBindings].map((userEntry) => userEntry.user_id)));

                  const membershipRoleNamesForGroup = Array.from(
                    new Set(
                      usersFromMemberships.flatMap((userEntry) =>
                        userEntry.memberships
                          .filter(
                            (membership) =>
                              Number(membership.company_id) === Number(entry.group_id)
                              || normalizeGroupName(membership.company_name || '') === normalizedGroupName,
                          )
                          .flatMap((membership) => membership.roles || []),
                      ),
                    ),
                  );
                  const configuredRolesForGroup = groupRoleBindings.find(
                    (binding) => normalizeGroupName(binding.groupName) === normalizedGroupName,
                  )?.roleIds || [];
                  const configuredRoleNamesForGroup = configuredRolesForGroup
                    .map((roleId) => roles.find((role) => role.id === roleId)?.name || roleId)
                    .sort((left, right) => left.localeCompare(right));
                  const roleNamesForGroup = Array.from(new Set([...membershipRoleNamesForGroup, ...configuredRoleNamesForGroup]))
                    .sort((left, right) => left.localeCompare(right));
                  const usersForGroupDetailed = Array.from(
                    new Map(
                      [...usersFromMemberships, ...usersFromBindings]
                        .map((userEntry) => [userEntry.user_id, userEntry]),
                    ).values(),
                  ).sort((left, right) => getUsernameLabel(left).localeCompare(getUsernameLabel(right)));
                  const rolesForGroupDetailed = roleNamesForGroup.map((roleName) => {
                    const matched = roles.find((roleEntry) => roleEntry.name === roleName || roleEntry.id === roleName);
                    return {
                      id: matched?.id || roleSlug(roleName),
                      label: matched?.name || roleName,
                    };
                  });
                  const userRoleAssignments = usersForGroupDetailed.map((userEntry) => {
                    const membershipRoles = userEntry.memberships
                      .filter(
                        (membership) =>
                          Number(membership.company_id) === Number(entry.group_id)
                          || normalizeGroupName(membership.company_name || '') === normalizedGroupName,
                      )
                      .flatMap((membership) => membership.roles || []);
                    const assignedRoleLabels = Array.from(new Set([...membershipRoles, ...configuredRoleNamesForGroup]))
                      .sort((left, right) => left.localeCompare(right));

                    return {
                      user: userEntry,
                      roles: assignedRoleLabels,
                    };
                  });
                  const permissionsForGroup = groupPermissionBindings.find(
                    (binding) => normalizeGroupName(binding.groupName) === normalizedGroupName,
                  )?.permissionIds || [];
                  const isExpanded = expandedGroupId === entry.group_id;

                  return (
                    <React.Fragment key={entry.group_id}>
                      <tr aria-expanded={isExpanded}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedGroupIds.includes(entry.group_id)}
                            onChange={() => toggleNumberId(entry.group_id, setSelectedGroupIds)}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="access-management-entity-disclosure"
                            onClick={() => setExpandedGroupId((current) => (current === entry.group_id ? null : entry.group_id))}
                            aria-expanded={isExpanded}
                          >
                            <span>{entry.group_name}</span>
                            <AccordionChevronIcon open={isExpanded} />
                          </button>
                        </td>
                        <td>{usersForGroup.length}</td>
                        <td>{roleNamesForGroup.length > 0 ? roleNamesForGroup.join(', ') : 'None'}</td>
                        <td>{permissionsForGroup.length}</td>
                      </tr>
                      {isExpanded ? (
                        <tr className="access-management-entity-detail-row">
                          <td colSpan={5}>
                            <div className="access-management-entity-detail-card">
                              <div className="access-management-entity-detail-grid">
                                <div>
                                  <strong>Account name</strong>
                                  <p>{entry.group_name}</p>
                                </div>
                                <div>
                                  <strong>Users</strong>
                                  <p>{usersForGroup.length}</p>
                                </div>
                                <div>
                                  <strong>Roles</strong>
                                  <p>{roleNamesForGroup.length}</p>
                                </div>
                                <div>
                                  <strong>Direct permissions</strong>
                                  <p>{permissionsForGroup.length}</p>
                                </div>
                              </div>
                              <div className="access-management-entity-detail-grid">
                                <div>
                                  <strong>Assigned users and roles</strong>
                                  {userRoleAssignments.length > 0 ? (
                                    <ul className="access-management-link-list">
                                      {userRoleAssignments.map(({ user: userEntry, roles: assignedRoles }) => (
                                        <li key={`group-user-link-${entry.group_id}-${userEntry.user_id}`}>
                                          <Link
                                            to={`/console/admin/users?userId=${encodeURIComponent(userEntry.user_id)}`}
                                            className="access-management-entity-link"
                                          >
                                            {getUsernameLabel(userEntry)}
                                          </Link>
                                          <span className="access-management-link-hint">
                                            {assignedRoles.length > 0 ? `Roles: ${assignedRoles.join(', ')}` : 'No explicit role mapping'}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  ) : <p>None</p>}
                                </div>
                                <div>
                                  <strong>Group-level roles</strong>
                                  {rolesForGroupDetailed.length > 0 ? (
                                    <ul className="access-management-link-list">
                                      {rolesForGroupDetailed.map((roleEntry) => (
                                        <li key={`group-role-link-${entry.group_id}-${roleEntry.id}`}>
                                          <Link
                                            to={`/console/admin/roles?roleId=${encodeURIComponent(roleEntry.id)}`}
                                            className="access-management-entity-link"
                                          >
                                            {roleEntry.label}
                                          </Link>
                                          <span className="access-management-link-hint">Open role</span>
                                        </li>
                                      ))}
                                    </ul>
                                  ) : <p>None</p>}
                                </div>
                                <div>
                                  <strong>Permission IDs</strong>
                                  <p>{permissionsForGroup.length > 0 ? permissionsForGroup.join(', ') : 'None'}</p>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            </div>
          ) : null}
        </div>
      ) : null}

      {currentSection === 'roles' ? (
        <div className="theme-admin-card">
          <div className="admin-console-toolbar">
            <div>
              <h2>
                Roles <span className="access-management-count">({roles.length})</span>
                <span className="access-management-info">Info</span>
              </h2>
              <p>Keep three jobs on an account: Admin, Contributor, and Viewer. Add a role only when a real job needs a different permission set, and write that job in the description. Do not create a role for a subscription plan or for one person.</p>
            </div>
            <div className="admin-console-inline-actions">
              {(isCreateRoleOpen || Boolean(editingRoleId)) ? (
                <button
                  type="submit"
                  form="create-role-form"
                  className="btn btn-primary"
                  disabled={isSavingUser}
                >
                  {editingRoleId ? 'Save role changes' : 'Save role'}
                </button>
              ) : null}
              <button
                type="button"
                className={isCreateRoleOpen ? 'btn btn-secondary' : 'btn btn-primary'}
                onClick={() => {
                  setIsCreateRoleOpen((open) => {
                    const nextOpen = !open;
                    if (nextOpen) {
                      setEditingRoleId(null);
                      setNewRoleScopeGroupId('');
                    } else {
                      setEditingRoleId(null);
                      setNewRoleScopeGroupId('');
                    }
                    return nextOpen;
                  });
                }}
              >
                {isCreateRoleOpen ? 'Close' : 'Create role'}
              </button>
              <button type="button" className="btn btn-secondary" disabled={selectedRoleIds.length !== 1} onClick={startEditSelectedRole}>
                Edit
              </button>
              <button type="button" className="btn btn-secondary" disabled={selectedRoleIds.length === 0} onClick={deleteSelectedRoles}>
                Delete
              </button>
            </div>
          </div>

          {isCreateRoleOpen ? (
            <form id="create-role-form" className="admin-console-form" onSubmit={createRole}>
              <label className="theme-admin-field">
                <span>Role name</span>
                <input value={newRoleName} onChange={(event) => setNewRoleName(event.target.value)} required />
              </label>
              <label className="theme-admin-field">
                <span>Role scope (optional)</span>
                <select value={newRoleScopeGroupId} onChange={(event) => setNewRoleScopeGroupId(event.target.value)}>
                  <option value="">Global role (no group prefix)</option>
                  {topLevelGroupCandidates.map((group) => (
                    <option key={`role-scope-group-${group.group_id}`} value={String(group.group_id)}>
                      {group.group_name}
                    </option>
                  ))}
                </select>
              </label>
              {selectedRoleScopeGroup ? (
                <p className="access-management-scope-note">
                  Scoped role will be saved as <strong>{selectedRoleScopeGroup.group_name}:{newRoleName.trim() || 'name'}</strong> and permissions are limited to that top-level group.
                </p>
              ) : null}
              <label className="theme-admin-field">
                <span>Description</span>
                <input value={newRoleDescription} onChange={(event) => setNewRoleDescription(event.target.value)} />
              </label>
              <section className="admin-console-accordion-group">
                <button
                  type="button"
                  className="admin-console-accordion-toggle"
                  onClick={() => setIsCreateRolePermissionsOpen((open) => !open)}
                  aria-expanded={isCreateRolePermissionsOpen}
                >
                  <span>Attach permissions policies - Optional ({newRolePermissionIds.length})</span>
                  <AccordionChevronIcon open={isCreateRolePermissionsOpen} />
                </button>
                {isCreateRolePermissionsOpen ? (
                  <>
                    <div className="access-management-search-row">
                      <input
                        type="search"
                        className="access-management-search"
                        placeholder="Search permissions"
                        value={newRolePermissionSearch}
                        onChange={(event) => setNewRolePermissionSearch(event.target.value)}
                        aria-label="Search permissions to attach to role"
                      />
                    </div>
                    <div className="access-management-permission-toolbar">
                      <label className="theme-admin-field">
                        <span>Filter by type</span>
                        <select
                          value={newRolePermissionTypeFilter}
                          onChange={(event) => setNewRolePermissionTypeFilter(event.target.value as PermissionTypeFilter)}
                        >
                          <option value="all">All types</option>
                          <option value="service">Service operation</option>
                          <option value="admin">Administrative</option>
                        </select>
                      </label>
                      <label className="theme-admin-field">
                        <span>Logical group</span>
                        <select
                          value={newRolePermissionGroupFilter}
                          onChange={(event) => setNewRolePermissionGroupFilter(event.target.value as PermissionGroupFilter)}
                        >
                          <option value="all">All groups</option>
                          <option value="products">Products</option>
                          <option value="services">Services</option>
                          <option value="features">Features</option>
                          <option value="identity">Identity</option>
                          <option value="themes">Themes</option>
                        </select>
                      </label>
                      <div className="access-management-selection-visibility-controls">
                        <label className="admin-console-checkbox access-management-selected-only-toggle">
                          <input
                            type="checkbox"
                            checked={newRolePermissionSelectedOnly}
                            onChange={(event) => {
                              const checked = event.target.checked;
                              setNewRolePermissionSelectedOnly(checked);
                              if (checked) {
                                setNewRolePermissionUnselectedOnly(false);
                              }
                            }}
                          />
                          <span>Show selected only</span>
                        </label>
                        <label className="admin-console-checkbox access-management-selected-only-toggle">
                          <input
                            type="checkbox"
                            checked={newRolePermissionUnselectedOnly}
                            onChange={(event) => {
                              const checked = event.target.checked;
                              setNewRolePermissionUnselectedOnly(checked);
                              if (checked) {
                                setNewRolePermissionSelectedOnly(false);
                              }
                            }}
                          />
                          <span>Show unselected only</span>
                        </label>
                      </div>
                      <label className="admin-console-checkbox access-management-select-all-toggle">
                        <input
                          type="checkbox"
                          checked={areAllFilteredRolePermissionsSelected}
                          onChange={(event) => toggleAllFilteredRolePermissions(event.target.checked)}
                        />
                        <span>Select all filtered permissions ({sortedRolePermissionCatalog.length})</span>
                      </label>
                    </div>

                    <div className="admin-console-table-wrap access-management-permissions-table-wrap">
                      <table className="admin-console-table access-management-permissions-table">
                        <thead>
                          <tr>
                            <th>
                              <input
                                type="checkbox"
                                aria-label="Select all permissions on this page"
                                checked={areAllPagedRolePermissionsSelected}
                                onChange={(event) => {
                                  const visibleIds = pagedRolePermissionCatalog.map((permission) => permission.id);
                                  setNewRolePermissionIds((current) => {
                                    const next = new Set(current);
                                    if (event.target.checked) {
                                      visibleIds.forEach((id) => next.add(id));
                                    } else {
                                      visibleIds.forEach((id) => next.delete(id));
                                    }
                                    return Array.from(next).sort((left, right) => left.localeCompare(right));
                                  });
                                }}
                              />
                            </th>
                            <th>
                              <button
                                type="button"
                                className="access-management-sort-btn"
                                onClick={() => {
                                  if (newRolePermissionSortKey === 'policyName') {
                                    setNewRolePermissionSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
                                  } else {
                                    setNewRolePermissionSortKey('policyName');
                                    setNewRolePermissionSortDirection('asc');
                                  }
                                }}
                              >
                                Policy name
                              </button>
                            </th>
                            <th>
                              <button
                                type="button"
                                className="access-management-sort-btn"
                                onClick={() => {
                                  if (newRolePermissionSortKey === 'typeLabel') {
                                    setNewRolePermissionSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
                                  } else {
                                    setNewRolePermissionSortKey('typeLabel');
                                    setNewRolePermissionSortDirection('asc');
                                  }
                                }}
                              >
                                Type
                              </button>
                            </th>
                            <th>
                              <button
                                type="button"
                                className="access-management-sort-btn"
                                onClick={() => {
                                  if (newRolePermissionSortKey === 'usedCount') {
                                    setNewRolePermissionSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
                                  } else {
                                    setNewRolePermissionSortKey('usedCount');
                                    setNewRolePermissionSortDirection('asc');
                                  }
                                }}
                              >
                                Attached entities
                              </button>
                            </th>
                            <th>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedRolePermissionCatalog.length > 0 ? (
                            pagedRolePermissionCatalog.map((permission) => (
                              <tr key={`new-role-permission-${permission.id}`}>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={newRolePermissionIds.includes(permission.id)}
                                    onChange={() => toggleId(permission.id, setNewRolePermissionIds)}
                                  />
                                </td>
                                <td>
                                  <div className="access-management-policy-cell">
                                    <strong>{permission.policyName}</strong>
                                    <span>{permission.groupLabel}</span>
                                  </div>
                                </td>
                                <td>{permission.typeLabel}</td>
                                <td>{permission.usedCount}</td>
                                <td>{permission.description}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5}>No permissions match this filter.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="access-management-permission-pagination" aria-label="Role permissions pagination">
                      <label className="access-management-page-size-control">
                        <span>Rows per page</span>
                        <select
                          value={newRolePermissionPageSize}
                          onChange={(event) => setNewRolePermissionPageSize(Number(event.target.value))}
                        >
                          {PERMISSION_PAGE_SIZE_OPTIONS.map((size) => (
                            <option key={`role-perm-page-size-${size}`} value={size}>{size}</option>
                          ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={newRolePermissionPage <= 1}
                        onClick={() => setNewRolePermissionPage((page) => Math.max(1, page - 1))}
                      >
                        Previous
                      </button>
                      <span>Page {newRolePermissionPage} of {rolePermissionPageCount}</span>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={newRolePermissionPage >= rolePermissionPageCount}
                        onClick={() => setNewRolePermissionPage((page) => Math.min(rolePermissionPageCount, page + 1))}
                      >
                        Next
                      </button>
                    </div>
                  </>
                ) : null}
              </section>
              <button type="submit" className="btn btn-primary access-management-role-bottom-save">{editingRoleId ? 'Save role changes' : 'Save role'}</button>
            </form>
          ) : null}

          {!isCreateRoleOpen ? (
            <div className="admin-console-table-wrap">
            <table className="admin-console-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Role name</th>
                  <th>Description</th>
                  <th>Groups</th>
                  <th>Permissions</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((entry) => {
                  const groupsForRole = groupRoleBindings
                    .filter((binding) => binding.roleIds.includes(entry.id))
                    .map((binding) => binding.groupName)
                    .sort((left, right) => left.localeCompare(right));

                  const isExpanded = expandedRoleId === entry.id;

                  return (
                    <React.Fragment key={entry.id}>
                      <tr aria-expanded={isExpanded}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedRoleIds.includes(entry.id)}
                            onChange={() => toggleId(entry.id, setSelectedRoleIds)}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="access-management-entity-disclosure"
                            onClick={() => setExpandedRoleId((current) => (current === entry.id ? null : entry.id))}
                            aria-expanded={isExpanded}
                          >
                            <span>{entry.name}</span>
                            <AccordionChevronIcon open={isExpanded} />
                          </button>
                        </td>
                        <td>{entry.description || '-'}</td>
                        <td>{groupsForRole.length > 0 ? groupsForRole.join(', ') : 'None'}</td>
                        <td>{entry.permissions.length}</td>
                      </tr>
                      {isExpanded ? (
                        <tr className="access-management-entity-detail-row">
                          <td colSpan={5}>
                            <div className="access-management-entity-detail-card">
                              <div className="access-management-entity-detail-grid">
                                <div>
                                  <strong>Role name</strong>
                                  <p>{entry.name}</p>
                                </div>
                                <div>
                                  <strong>Description</strong>
                                  <p>{entry.description || '-'}</p>
                                </div>
                                <div>
                                  <strong>Assigned groups</strong>
                                  <p>{groupsForRole.length > 0 ? groupsForRole.join(', ') : 'None'}</p>
                                </div>
                                <div>
                                  <strong>Permissions</strong>
                                  <p>{entry.permissions.length}</p>
                                </div>
                              </div>
                              <div className="access-management-entity-detail-grid">
                                <div>
                                  <strong>Permission IDs</strong>
                                  <p>{entry.permissions.length > 0 ? entry.permissions.join(', ') : 'None'}</p>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            </div>
          ) : null}
        </div>
      ) : null}

      {currentSection === 'voiceMessages' ? (
        <VoiceMessagesPage />
      ) : null}

      {currentSection === 'keycloakAdmin' ? (
        <KeycloakAdminPanel />
      ) : null}
    </section>
  );
};

export default AdminConsolePage;
